/**
 * OrderDetailModal - Full-screen order detail modal with two tabs:
 * 1. Order tab: line items with pricing comparison (recommended vs order price)
 * 2. Special Pricing tab: customer agreements with product-level detail
 *
 * Reusable from both PortalOrdersPage and email inbox.
 */

import { Fragment, Suspense, lazy, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { portalOrderService } from '../../services/portalOrders';
import { trackEvent } from '../../services/portalTracking';
import LinePriceReasonPrompt from './LinePriceReasonPrompt';
import { shouldPromptForPriceReason } from './linePriceReason';
import priceReasonStyles from './LinePriceReasonPrompt.module.css';
import DetailNavButtons from './DetailNavButtons';
import type { DetailNavigation } from './detailNavigation';
import { useDetailNavKeys } from '../../hooks/useDetailNavKeys';
import type { BackorderPrompt, DuplicatePoOrderSummary, FreightWaiverPushInfo, InstructionMutationResponse, LineItemUpdateRequest, OrderInstruction, OrderLineComments, SiblingOrderSummary, SubmissionFieldError, SurchargeSetResponse, TruckloadFreightSummary } from '../../services/portalOrders';
import { portalProductService } from '../../services/portalProducts';
import type { ProductSearchResult, ResolvedPriceInfo } from '../../services/portalProducts';
import {
  buildPriceChoices,
  formatAddSuccess,
  recommendedChoiceIndex,
  type PriceChoice,
} from './addLinePricing';
import KitComponentBadge from './KitComponentBadge';
import BrandAliasBadge from './BrandAliasBadge';
import DocumentsPanel from './DocumentsPanel';
import MerlinEditProposalCard from './MerlinEditProposalCard';
import ExtractionConcernsBanner from './ExtractionConcernsBanner';
import { renderInlineBold } from '../../utils/inlineBold';
import ShipToSearch from './ShipToSearch';
import { formatHeaderSaveError, describeSaveFailure } from './headerSaveError';
import HeaderIdentityWarningModal from './HeaderIdentityWarningModal';
import { runHeaderIdentityCheck } from './headerIdentityGate';
import type { HeaderIdentityRuling, PendingHeaderIdentityWarning } from './headerIdentityGate';
import type { ShipToResult } from './ShipToSearch';
import NewSageShipToForm from './NewSageShipToForm';
import type { CreatedShipTo } from './NewSageShipToForm';
import AddCustomerModal from './AddCustomerModal';
import SearchableDropdown from './SearchableDropdown';
import type { DropdownItem } from './SearchableDropdown';
import FreightQuoteModal from './FreightQuoteModal';
import TruckloadFreightControl from './TruckloadFreightControl';
import BolScanDeskAskBanner from './BolScanDeskAskBanner';
import OrderSurchargeControl from './OrderSurchargeControl';
import MerlinRecommendationChip from './MerlinRecommendationChip';
import UnpricedLinesMarker from './UnpricedLinesMarker';
import { countUnpricedLines, joinComponents, orderTotalFromDetail } from '../../utils/orderTotals';
import SageEditModal from './SageEditModal';
import { BILL_OF_LADING_OPTIONS, billOfLadingLabel } from './billOfLading';
import { formatPendingSageWrite } from './sagePendingWrites';
import { shipDateBounds, todayIsoDate } from '../../utils/shipDateBounds';
import DeletedInSageBadge from './DeletedInSageBadge';
import HoldReasonField from './HoldReasonField';
import ParkedBadge, { formatIsoDate, formatParkedBy } from './ParkedBadge';
import DueBadge from './DueBadge';
import AwaitingSageAccountBadge from './AwaitingSageAccountBadge';
import ItemsNowInSageBadge from './ItemsNowInSageBadge';
import ParkDraftDialog from './ParkDraftDialog';
import { ConfirmSageDialog, UndoConfirmSageDialog } from './ConfirmSageDialog';
import ConfidenceBadge from './ConfidenceBadge';
import { buildWarehouseDropdownItems } from '../../data/warehouseDropdownItems';
import { warehouseProvenance } from '../../utils/warehouseProvenance';
import { isHighlightedShipVia } from '../../data/highlightedShipViaCodes';
import { mapEmailToComponent } from '../../utils/mapEmailToComponent';
import { formatAddLineItemError, formatProductSearchError } from './orderAddLineItemError';
import type {
  PortalOrderDetailResponse,
  OrderPricingContextResponse,
  LineItemPricingContext,
  CustomerPricingAgreement,
  AgreementProduct,
  CustomerOpenQuote,
  QuoteDetailResponse,
  ReferencedOrderQuote,
  QuoteProductDetail,
  WarehouseOption,
  ShippingCarrierOption,
  CancelReasonCodeOption,
  LineItemMutationResponse,
  LineItemDeleteResponse,
  WeightSummaryResponse,
  PortalOrderUpdateRequest,
} from '../../services/portalOrders';
import type { OrderLineItem, OrderRecord, OrderStatus, LineItemCreateRequest } from '../../types/orders';
import type { SalespersonOption } from '../../types/portal';
import { resolveRegionalResponsible, resolveSalespersonDisplay } from '../../utils/salespersonDisplay';
import { ORDER_STATUS_LABELS, PARK_REASON_LABELS, hasResolvedItems, isAwaitingSageAccount } from '../../types/orders';
import type { DraftParkReason } from '../../types/orders';
import { formatDate } from '../../utils/dateFormatters';
import {
  formatWeight,
  calculateLineWeight,
  parseWeight,
  quantityForTargetWeight,
  resolveShipWeight,
} from '../../utils/formatting';
import { useIsolatedPrint } from '../../hooks/useIsolatedPrint';
import { buildOrderPrintJob, type OrderPrintJob } from '../../utils/orderPrintModel';
import OrderPrintDocument from './OrderPrintDocument';
import PricingSourcePicker from './PricingSourcePicker';

// ── Confirm-price step (kanban e271f10d) ──
interface PriceConfirmState {
  product: ProductSearchResult;
  quantity: string;
  /** "Set item code" keeps the target line's quantity — not editable here. */
  quantityLocked: boolean;
  targetLineId: string | null;
  resolved: ResolvedPriceInfo;
  choices: PriceChoice[];
}

const priceChoiceBadgeText = (type: PriceChoice['type']): string => {
  switch (type) {
    case 'special':
    case 'tiered':
    case 'base_discount':
      return 'SP';
    case 'quote':
      return 'Qt';
    case 'standard':
      return 'Std';
    default:
      return 'Custom';
  }
};

const priceChoiceBadgeClass = (type: PriceChoice['type']): string => {
  switch (type) {
    case 'special':
    case 'tiered':
    case 'base_discount':
      return styles.priceConfirmBadgeSP;
    case 'quote':
      return styles.priceConfirmBadgeQuote;
    case 'standard':
      return styles.priceConfirmBadgeStd;
    default:
      return styles.priceConfirmBadgeCustom;
  }
};
import PriceMismatchConfirmModal from './PriceMismatchConfirmModal';
import {
  findAllPriceMismatches,
  sourceLabel,
  type PriceMismatchItem,
} from '../../utils/priceMismatchCheck';
import { formatTierLadder, resolveTierPrice } from '../../utils/tierPricing';
import {
  lineNeedsAnswer,
  isInfoTier,
  isResolvedReviewLine,
  attentionSummary,
  describeEvidence,
  questionPartsFor,
  issueChipLabel,
} from '../../utils/reviewTier';
import { useOrderDocuments } from './hooks/useOrderDocuments';
import OrderTimelineTab from './OrderTimelineTab';
import OrderNotesPanel from './OrderNotesPanel';
import { useGuruWidgetViewContext } from '../../hooks/useGuruWidgetViewContext';
import LineItemSageComments from './LineItemSageComments';
import { matchLineCommentBundles } from './lineCommentBundles';
import DuplicatePoWarning from './DuplicatePoWarning';
import { OrderStoryStrip } from './OrderStoryStrip';
import { OrderInvoiceSection } from './OrderInvoiceSection';
import OrderContactsField from './OrderContactsField';
import OrderNotificationsPanel from './OrderNotificationsPanel';
import styles from './OrderDetailModal.module.css';
import {
  hasOrderRecord,
  MISSING_ORDER_RECORD_MESSAGE,
  findEmptyQuoteSlot,
  findQuoteSlot,
  getAttachedQuoteRefs,
  isQuoteAttached,
  normalizeQuoteRef,
  type QuoteSlot,
} from './OrderDetailModal.utils';

// Tooltip strings for the weight-summary fallback path. The backend
// returns ``fallback_reason`` as a discriminated union; mapping each to a
// short operator-facing explanation keeps the rendering site clean.
const FALLBACK_REASON_MESSAGES: Record<
  | 'no_ship_to_address'
  | 'no_zip_code'
  | 'no_line_items'
  | 'zip_not_found'
  | 'freight_api_error',
  string
> = {
  no_ship_to_address:
    "Ship-to address isn't set yet — can't ask Sage's freight engine for the authoritative weight.",
  no_zip_code:
    "Ship-to zip code is missing or invalid — can't ask Sage's freight engine for the authoritative weight.",
  no_line_items:
    'Order has no line items yet.',
  zip_not_found:
    "Sage's freight system doesn't recognise this zip code; showing the local product-weight sum instead.",
  freight_api_error:
    "Sage's freight engine is temporarily unavailable; showing the local product-weight sum instead.",
};

// ─── Props ───────────────────────────────────────────────────────────────

// Lazy: EmailViewModal -> SplitScreenModal -> OrderDetailModal is a static
// import cycle (same reason SplitScreenModal lazy-loads both panes).
import { portalService } from '../../services/portal';
import { useRecorderObject } from '../../hooks/useRecorderObject';

const EmailViewModal = lazy(() => import('./EmailViewModal'));

/** Shape of the email shown in the modal's embedded email pane (mapEmailToComponent's output). */
type MappedPaneEmail = ReturnType<typeof mapEmailToComponent>;
// Reply from the order's own email pane (2026-09-24 order portal review).
const EmailComposeModal = lazy(() => import('./EmailComposeModal'));

export interface OrderDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Provide orderId directly (from Orders page) */
  orderId?: string | null;
  /** Provide emailId to resolve orderId (from email inbox) */
  emailId?: string | null;
  /** Called when an action is taken (submit, cancel, etc.) */
  onOrderAction?: (action: string, orderId: string) => void;
  /** Render mode: 'modal' (default) renders with overlay; 'embedded' renders inline for split-screen */
  renderMode?: 'modal' | 'embedded';
  /**
   * Next / previous through the orders list the order was opened from
   * (kanban 97abd058). Header buttons plus j/k and the arrow keys; the keys
   * stay quiet while typing in any field or while one of this modal's own
   * dialogs is open.
   */
  navigation?: DetailNavigation;
  /**
   * Called once the order was looked up FROM an emailId (no orderId given):
   * the resolved order id, or null when the email has no order yet / the
   * lookup failed. The split view opened from an email uses it to record
   * email_open_order with both ids (kanban 1136a376). Not called on the
   * orderId path, where the caller already knows the order.
   */
  onOrderResolved?: (orderId: string | null) => void;
}

type TabId = 'order' | 'pricing' | 'quotes' | 'documents' | 'timeline';

/** Outcome banner shown on the Open Quotes tab after an Attach press. */
export interface QuoteAttachNotice {
  kind: 'success' | 'info' | 'error';
  text: string;
}

/** Success/info attach notices auto-dismiss after this long; errors stay. */
export const QUOTE_ATTACH_NOTICE_MS = 6000;

// ─── Component ───────────────────────────────────────────────────────────

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({
  isOpen,
  onClose,
  orderId: propOrderId,
  emailId,
  onOrderAction,
  renderMode = 'modal',
  navigation,
  onOrderResolved,
}) => {
  const navigate = useNavigate();
  const { user, hasScope } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const isEmbedded = renderMode === 'embedded';
  // orders:read viewers (e.g. Customers-tab personas) see the order read-only;
  // every edit affordance below requires orders:manage.
  const canManageOrders = hasScope('orders:manage');

  // ── State ──
  const [activeTab, setActiveTab] = useState<TabId>('order');
  // Always start null — the resolution effect below is the single source of truth
  // for setting this from either `propOrderId` or an `emailId` lookup. Seeding
  // from `propOrderId` here caused a stale-state bug where reopening the modal
  // with the same order id would not re-trigger the fetchData effect.
  const [resolvedOrderId, setResolvedOrderId] = useState<string | null>(null);
  // The recorder's leave/return markers name the order open at that moment.
  useRecorderObject(isOpen ? { order_id: resolvedOrderId } : null);
  const [orderDetail, setOrderDetail] = useState<PortalOrderDetailResponse | null>(null);
  // Latest callback, read inside the resolution effect without re-running it
  // (a new function identity must never refetch the order).
  const onOrderResolvedRef = useRef(onOrderResolved);
  onOrderResolvedRef.current = onOrderResolved;

  // Tab-bar clicks are the discoverability signal the engagement monitor
  // reads from portal_events (which tabs CSRs find, e.g. Open Quotes). Only
  // user-initiated switches are tracked — never the programmatic resets to
  // 'order' on close / sibling change, and not re-clicks on the active tab.
  const openTab = useCallback((tab: TabId) => {
    if (tab !== activeTab) {
      trackEvent('click', 'order_detail_tab', {
        tab,
        order_id: resolvedOrderId,
        order_status: orderDetail?.order?.status ?? null,
      });
    }
    setActiveTab(tab);
  }, [activeTab, resolvedOrderId, orderDetail]);

  // Tell the Guru feedback widget which order is in view (state-held, not in
  // the URL) so widget submissions carry the order identifiers.
  useGuruWidgetViewContext(
    'order_detail',
    isOpen && (resolvedOrderId || emailId)
      ? {
          order_id: resolvedOrderId ?? undefined,
          email_id: emailId ?? undefined,
          order_status: orderDetail?.order?.status ?? undefined,
          order_customer_name: orderDetail?.order?.customer_name ?? undefined,
          order_customer_no: orderDetail?.order?.customer_no ?? undefined,
          order_po_number: orderDetail?.order?.po_number ?? undefined,
          order_salesperson: orderDetail?.order?.salesperson_name ?? undefined,
        }
      : null,
  );
  const [pricingContext, setPricingContext] = useState<OrderPricingContextResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [sageError, setSageError] = useState<string | null>(null);
  const [submissionFieldErrors, setSubmissionFieldErrors] = useState<Array<{field: string; message: string; current_value?: string | null; line_item_position?: number | null}>>([]);

  // Dismiss draft confirmation modal
  const [showDismissConfirm, setShowDismissConfirm] = useState(false);
  // Why a Dismiss was refused (draft submitted or dismissed by someone else).
  const [dismissError, setDismissError] = useState<string | null>(null);
  // Parked-draft marker (2026-09-24 order portal review, kanban c7acc1a6).
  const [showParkDialog, setShowParkDialog] = useState(false);
  // Manual Confirm-in-Sage confirm step and its undo (kanban c79be365).
  const [showConfirmSage, setShowConfirmSage] = useState(false);
  const [showUndoConfirmSage, setShowUndoConfirmSage] = useState(false);
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [parkSubmitting, setParkSubmitting] = useState(false);
  const [parkError, setParkError] = useState<string | null>(null);
  const [showSageEdit, setShowSageEdit] = useState(false);

  // Price-mismatch confirmation modal (triggered by Submit to Sage when
  // one or more line items have prices that don't match any known source).
  const [priceMismatches, setPriceMismatches] = useState<PriceMismatchItem[] | null>(null);
  // Soft, overridable submission warnings returned by the submit
  // (requires_acknowledgment): ship-to "address doesn't match" AND line items
  // still flagged needs_review. Non-null → show the "Submit anyway" confirm.
  const [submissionWarnings, setSubmissionWarnings] = useState<
    Array<{ field: string; message: string; current_value?: string | null; line_item_position?: number | null }> | null
  >(null);

  // Credit-hold confirmation modal (triggered by Submit to Sage when
  // the customer is flagged credit_hold='Y' in Sage). Gated separately
  // from the price-mismatch check so each warning is acknowledged on
  // its own.
  const [showCreditHoldConfirm, setShowCreditHoldConfirm] = useState(false);

  // Freight-carrier soft gate (triggered by Submit to Sage when no ship_via
  // carrier is selected). Like the credit-hold / price-mismatch gates this is
  // OVERRIDABLE: we highlight the Ship Via field red and open an "is it blank —
  // submit anyway?" confirm so the rep decides. We deliberately do NOT
  // special-case truckload-pending (FRT) orders — the rep judges every
  // blank-carrier submit, so a new "carrier comes later" case never needs a
  // code change.
  const [freightCarrierError, setFreightCarrierError] = useState(false);
  // Open state for the "Ship Via is blank — submit anyway?" confirm dialog.
  const [showFreightConfirm, setShowFreightConfirm] = useState(false);

  // Merlin-suggested instructions at Submit (kanban f1c674a6 / 88963483).
  // A suggestion never rides the Sage payload until a CSR adds it
  // (2026-08-24 ruling, review npjrae). The submit popup that listed them
  // (PR #2812) was removed on the owner's 2026-10-21 ruling (review 4w6tmm):
  // the footer shows a one-line note by Submit instead, and Submit stamps
  // every leftover suggestion "left off at Submit, no prompt" on its way
  // out, so a submitted order never carries a loose suggestion and the
  // audit says nobody was asked. This holds the error when that stamp
  // fails (the submit does not fire; the backend backstop would otherwise
  // pop the generic warning dialog).
  const [instructionStampError, setInstructionStampError] = useState<string | null>(null);

  // Source-email pane (modal mode only). Opening an order lands straight in
  // the email + order view; there is no separate "View Email" click (owner
  // request 2026-09-24). The pane is a sibling of the order panel inside
  // .workspace, so hiding/showing it never remounts the panel and unsaved
  // inline edits survive the toggle.
  const [emailPaneOpen, setEmailPaneOpen] = useState(true);
  const [emailPaneData, setEmailPaneData] = useState<MappedPaneEmail | null>(null);
  // Reply / Reply-all / Forward launched from the email pane; the compose
  // modal is hosted here because an embedded pane cannot open overlays.
  const [paneComposeAction, setPaneComposeAction] =
    useState<'reply' | 'reply_all' | 'forward' | null>(null);
  const [emailPaneLoading, setEmailPaneLoading] = useState(false);
  const [emailPaneError, setEmailPaneError] = useState<string | null>(null);

  // Customer change state
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState<Array<{ sage_customer_no: string; customer_name: string | null; salesperson_name: string | null }>>([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const customerSearchTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Ship-to change state
  const [showShipToSearch, setShowShipToSearch] = useState(false);
  const [showNewShipToForm, setShowNewShipToForm] = useState(false);
  // Merlin propose-new flow: "looks like a new customer/ship-to — create it?"
  const [showProposedCustomerModal, setShowProposedCustomerModal] = useState(false);
  const [newShipToPrefill, setNewShipToPrefill] = useState<Record<string, string> | null>(null);
  const [newShipToNote, setNewShipToNote] = useState<string | null>(null);
  // A failed ship-to PATCH must be SEEN (kanban 3677e207): the picker
  // closes either way, so without this the CSR believes the address stuck.
  const [shipToSaveError, setShipToSaveError] = useState<string | null>(null);

  // Documents tab — fetch lifecycle managed by useOrderDocuments hook
  const {
    documents: orderDocuments,
    loading: docsLoading,
    refetch: refetchDocuments,
  } = useOrderDocuments(resolvedOrderId, isOpen, activeTab === 'documents');

  // Pricing tab: filter
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  // Agreement expand state
  const [expandedAgreements, setExpandedAgreements] = useState<Set<string>>(new Set());

  // Warehouse options for dropdown
  const [warehouseOptions, setWarehouseOptions] = useState<WarehouseOption[]>([]);
  // Shipping carrier options for dropdown
  const [shippingCarrierOptions, setShippingCarrierOptions] = useState<ShippingCarrierOption[]>([]);
  const [salespersonOptions, setSalespersonOptions] = useState<SalespersonOption[]>([]);
  // Cancel/hold reason code options for dropdown
  const [cancelReasonCodes, setCancelReasonCodes] = useState<CancelReasonCodeOption[]>([]);

  // Sibling orders (multi-order per email)
  const [siblingOrders, setSiblingOrders] = useState<SiblingOrderSummary[]>([]);

  // Duplicate-PO warning state: orders that share this (customer, PO) pair.
  // Excludes the order being viewed (the order isn't a duplicate of itself).
  const [duplicatePoOrders, setDuplicatePoOrders] = useState<DuplicatePoOrderSummary[]>([]);

  // Authoritative weight summary from the Sage Freight API (with_quotes=false).
  // null until the first fetch resolves; thereafter holds the latest response —
  // ``success: true`` overrides the local sum, ``success: false`` triggers the
  // local-sum fallback with a tooltip explaining why.
  const [weightSummary, setWeightSummary] = useState<WeightSummaryResponse | null>(null);

  // Per-line Sage comments (read-only). Null until the first fetch resolves;
  // available=false for drafts and orders not yet synced from Sage.
  const [lineComments, setLineComments] = useState<OrderLineComments | null>(null);
  const [orderInstructions, setOrderInstructions] = useState<OrderInstruction[]>([]);

  // Print order: a dedicated OrderPrintDocument, never the interactive panel.
  // Sage notes are fetched at print time (the notes panel owns its own copy);
  // if they fail the order still prints, without the notes section.
  const { job: orderPrintJob, startPrint: startOrderPrint, handleReady: handleOrderPrintReady } =
    useIsolatedPrint<OrderPrintJob>();
  const handlePrintOrder = useCallback(async () => {
    if (!orderDetail) return;
    const notes = await portalOrderService.getSageNotes(orderDetail.order.id).catch((err: unknown) => {
      console.error('Failed to load Sage notes for print:', err);
      return null;
    });
    startOrderPrint(buildOrderPrintJob(orderDetail, orderInstructions, notes));
  }, [orderDetail, orderInstructions, startOrderPrint]);

  // Backorder prompts: still-offered "create a backorder for the remainder?"
  // offers, shown INLINE above the line items (deliberately no notification).
  // acceptedBackorderIds maps prompt id → created draft id so the banner can
  // flip to a success row with an "open" affordance after acceptance.
  const [backorderPrompts, setBackorderPrompts] = useState<BackorderPrompt[]>([]);
  // Line-scoped soft advisories returned by the add/update line-item
  // endpoints (never-bought SKU while the account buys a variant sibling).
  // Keyed by line item id; every mutation response REPLACES the entry for
  // its line, so fixing the SKU clears the notice on the next save.
  const [lineAdvisories, setLineAdvisories] = useState<Map<string, SubmissionFieldError[]>>(new Map());
  const [acceptedBackorderIds, setAcceptedBackorderIds] = useState<Map<string, string>>(new Map());
  const [backorderPromptBusy, setBackorderPromptBusy] = useState<string | null>(null);

  // ── Resolve emailId → orderId ──
  useEffect(() => {
    if (!isOpen) return;

    if (propOrderId) {
      setResolvedOrderId(propOrderId);
      return;
    }

    if (emailId) {
      setLoading(true);
      setError(null);
      portalOrderService.getOrderByEmailId(emailId).then((result) => {
        if (result && !hasOrderRecord(result)) {
          console.error('Order-by-email response is missing its order record', result);
          setResolvedOrderId(null);
          setLoading(false);
          setError(MISSING_ORDER_RECORD_MESSAGE);
          onOrderResolvedRef.current?.(null);
        } else if (result) {
          setResolvedOrderId(result.order.id);
          setOrderDetail(result);
          onOrderResolvedRef.current?.(result.order.id);
        } else {
          setResolvedOrderId(null);
          setLoading(false);
          setError('No order has been created for this email yet. The AI detected an order but it may still be processing.');
          onOrderResolvedRef.current?.(null);
        }
      }).catch(() => {
        setResolvedOrderId(null);
        setLoading(false);
        setError('No order has been created for this email yet. The AI detected an order but it may still be processing.');
        onOrderResolvedRef.current?.(null);
      });
    }
  }, [isOpen, propOrderId, emailId]);

  // ── Fetch sibling orders when we know the source email ──
  // Re-runs when the displayed order switches (resolvedOrderId) so a draft
  // created elsewhere — e.g. "Add Another Order" in the split-screen email
  // pane — shows up without closing and reopening the modal. Also invoked
  // imperatively when the split view closes, for the same reason.
  const refreshSiblings = useCallback(() => {
    const srcEmailId = emailId || orderDetail?.source_email_id || orderDetail?.order?.source_email_id;
    if (!srcEmailId || !isOpen) return;
    portalOrderService.getOrdersByEmailId(srcEmailId).then((resp) => {
      if (resp.total > 1) {
        setSiblingOrders(resp.orders);
      } else {
        setSiblingOrders([]);
      }
    }).catch(() => setSiblingOrders([]));
  }, [isOpen, emailId, orderDetail?.source_email_id, orderDetail?.order?.source_email_id]);

  useEffect(() => {
    refreshSiblings();
    // resolvedOrderId is intentionally a dependency (not read inside): the
    // sibling list must refetch when the displayed order switches, e.g. after
    // "Add Another Order" swaps the split-view order pane to the new draft.
  }, [refreshSiblings, resolvedOrderId]);

  // ── Duplicate-PO check: warn if (customer, PO) matches another order ──
  // Runs whenever the order's customer_no or po_number changes (debounced
  // so rapid edits don't spam the API). The current order is excluded so
  // it never flags itself.
  useEffect(() => {
    const order = orderDetail?.order;
    if (!isOpen || !order) {
      setDuplicatePoOrders([]);
      return;
    }
    const customerNo = order.customer_no?.trim();
    const poNumber = order.po_number?.trim();
    if (!customerNo || !poNumber) {
      setDuplicatePoOrders([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const res = await portalOrderService.checkDuplicatePoOrders(
          customerNo,
          poNumber,
          order.id,
        );
        setDuplicatePoOrders(res.duplicates);
      } catch {
        setDuplicatePoOrders([]);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [isOpen, orderDetail?.order]);

  const handleViewDuplicateOrder = useCallback((orderId: string) => {
    setResolvedOrderId(orderId);
    setOrderDetail(null);
    setActiveTab('order');
  }, []);

  // ── Backorder prompt handlers (state lives here; OrderTab renders) ──
  const handleQuantityReduced = useCallback(
    (orderId: string, lineItemId: string, prevQty: number, newQty: number) => {
      portalOrderService.createBackorderPrompt(orderId, {
        line_item_id: lineItemId,
        qty_before: String(prevQty),
        qty_after: String(newQty),
      }).then((prompt) => {
        // The server supersedes any prior offer for the same line; mirror it.
        setBackorderPrompts((prev) => [
          ...prev.filter((p) => p.line_item_id !== prompt.line_item_id),
          prompt,
        ]);
      }).catch(() => {
        // Offer is best-effort — a failed prompt never blocks the qty edit.
      });
    },
    [],
  );

  const handleAcceptBackorderPrompt = useCallback(
    async (orderId: string, promptId: string, quantity?: string) => {
      setBackorderPromptBusy(promptId);
      try {
        const resp = await portalOrderService.acceptBackorderPrompt(orderId, promptId, quantity);
        setAcceptedBackorderIds((prev) => {
          const next = new Map(prev);
          next.set(promptId, resp.backorder_order.id);
          return next;
        });
        setBackorderPrompts((prev) =>
          prev.map((p) => (p.id === promptId ? resp.prompt : p)),
        );
        onOrderAction?.('backorder_created', resp.backorder_order.id);
      } catch (err) {
        console.error('Failed to create backorder:', err);
      } finally {
        setBackorderPromptBusy(null);
      }
    },
    [onOrderAction],
  );

  const handleDeclineBackorderPrompt = useCallback(
    async (orderId: string, promptId: string) => {
      setBackorderPromptBusy(promptId);
      try {
        await portalOrderService.declineBackorderPrompt(orderId, promptId);
        setBackorderPrompts((prev) => prev.filter((p) => p.id !== promptId));
      } catch (err) {
        console.error('Failed to decline backorder prompt:', err);
      } finally {
        setBackorderPromptBusy(null);
      }
    },
    [],
  );

  // ── Fetch order detail + pricing context + warehouses ──
  //
  // The order itself is the only call that must succeed for the modal to be
  // usable; everything else (pricing context, warehouses, carriers, hold
  // reasons) is best-effort and rendered conditionally. Batching them all in
  // a single Promise.all used to suppress the order whenever any sibling
  // 500'd — most notably when pricing-context blew up on a draft whose line
  // items had no resolved unit_price — making the modal render "Order not
  // found" and hiding the Dismiss Draft button.
  const fetchData = useCallback(async (oid: string) => {
    setLoading(true);
    setError(null);
    let detail: PortalOrderDetailResponse;
    try {
      detail = await portalOrderService.getOrder(oid);
    } catch (err) {
      console.error('Failed to load order', err);
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(
        status === 404
          ? 'Order not found.'
          : 'Failed to load this order. Please refresh or contact support.',
      );
      setLoading(false);
      return;
    }
    if (!hasOrderRecord(detail)) {
      console.error('Order detail response is missing its order record', detail);
      setError(MISSING_ORDER_RECORD_MESSAGE);
      setLoading(false);
      return;
    }
    setOrderDetail(detail);
    const [pricing, warehouses, carriers, holdReasons, lineCmts, salespeople, instr] = await Promise.all([
      portalOrderService.getPricingContext(oid).catch((err) => {
        console.error('Failed to load pricing context', err);
        return null;
      }),
      portalOrderService.getWarehouses().catch(() => [] as WarehouseOption[]),
      portalOrderService.getShippingCarriers().catch(() => [] as ShippingCarrierOption[]),
      portalOrderService.getCancelReasonCodes({ includeInactive: true }).catch(() => [] as CancelReasonCodeOption[]),
      portalOrderService.getLineComments(oid).catch(() => null),
      import('../../services/portal')
        .then(({ portalService }) => portalService.listSalespersons())
        .catch(() => [] as SalespersonOption[]),
      // Optional-called so the many existing modal test mocks that predate
      // the instructions feature keep working; prod always has the method.
      Promise.resolve(portalOrderService.getInstructions?.(oid) ?? null).catch(
        () => null,
      ),
    ]);
    setPricingContext(pricing);
    setWarehouseOptions(warehouses);
    setShippingCarrierOptions(carriers);
    setCancelReasonCodes(holdReasons);
    setLineComments(lineCmts);
    setSalespersonOptions(salespeople);
    setOrderInstructions(instr?.instructions ?? []);
    setLoading(false);
  }, []);

  /** Refresh only pricing context in background (no loading spinner). */
  const refreshPricingContext = useCallback(async (oid: string) => {
    try {
      const pricing = await portalOrderService.getPricingContext(oid);
      setPricingContext(pricing);
    } catch {
      // Non-critical: pricing badges may be stale until next full refresh
    }
  }, []);

  /** Central handling of `resp.advisories` for EVERY add/update call site
   *  (inline cell edits, product switch, set-item-code, fill-with-weight,
   *  add-line flows all funnel through onLineItemAdded/onLineItemUpdated). */
  const applyLineAdvisories = useCallback((resp: LineItemMutationResponse) => {
    const next = resp.advisories ?? [];
    setLineAdvisories((prev) => {
      const had = prev.has(resp.line_item.id);
      if (next.length === 0 && !had) return prev;
      const map = new Map(prev);
      if (next.length === 0) map.delete(resp.line_item.id);
      else map.set(resp.line_item.id, next);
      return map;
    });
  }, []);

  const dismissLineAdvisory = useCallback((itemId: string) => {
    setLineAdvisories((prev) => {
      if (!prev.has(itemId)) return prev;
      const map = new Map(prev);
      map.delete(itemId);
      return map;
    });
  }, []);

  /** Optimistic: add a line item to local state, update totals, refresh pricing. */
  const handleLineItemAdded = useCallback((resp: LineItemMutationResponse) => {
    setOrderDetail((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        line_items: [...prev.line_items, resp.line_item],
        order: { ...prev.order, subtotal: resp.order_subtotal, total: resp.order_total },
      };
    });
    applyLineAdvisories(resp);
    if (resolvedOrderId) refreshPricingContext(resolvedOrderId);
  }, [resolvedOrderId, refreshPricingContext, applyLineAdvisories]);

  /** Optimistic: update a line item in local state, update totals, refresh pricing. */
  const handleLineItemUpdated = useCallback((resp: LineItemMutationResponse) => {
    setOrderDetail((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        line_items: prev.line_items.map((li) =>
          li.id === resp.line_item.id ? resp.line_item : li,
        ),
        order: { ...prev.order, subtotal: resp.order_subtotal, total: resp.order_total },
      };
    });
    applyLineAdvisories(resp);
    if (resolvedOrderId) refreshPricingContext(resolvedOrderId);
  }, [resolvedOrderId, refreshPricingContext, applyLineAdvisories]);

  /** Optimistic: remove a line item from local state, update totals, refresh pricing. */
  const handleLineItemRemoved = useCallback((removedItemId: string, totals: LineItemDeleteResponse) => {
    setOrderDetail((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        line_items: prev.line_items.filter((li) => li.id !== removedItemId),
        order: { ...prev.order, subtotal: totals.order_subtotal, total: totals.order_total },
      };
    });
    dismissLineAdvisory(removedItemId);
    if (resolvedOrderId) refreshPricingContext(resolvedOrderId);
  }, [resolvedOrderId, refreshPricingContext, dismissLineAdvisory]);

  /** The surcharge control set/changed/removed the /SURCHG line: the response
   *  carries the whole refreshed line set + totals (kanban 51aaddc5). */
  const handleSurchargeChanged = useCallback((resp: SurchargeSetResponse) => {
    setOrderDetail((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        line_items: resp.line_items,
        order: { ...prev.order, subtotal: resp.order_subtotal, total: resp.order_total },
      };
    });
    if (resolvedOrderId) refreshPricingContext(resolvedOrderId);
  }, [resolvedOrderId, refreshPricingContext]);

  // ── Authoritative weight summary (Sage Freight API, with_quotes=false) ──
  // Fetches whenever the order's line items change. The backend returns
  // success=false with a fallback_reason when it can't reach Sage or the
  // ship-to address is missing — we keep that response so the UI can
  // explain why we're showing the local sum instead.
  //
  // Re-keyed by a stable digest of (item_code, quantity) so we re-fetch
  // exactly when the inputs that affect the freight calculation change,
  // not on every unrelated re-render.
  const lineItemWeightDigest = useMemo(() => {
    if (!orderDetail) return '';
    return orderDetail.line_items
      .map((li) => `${li.item_code}:${li.quantity ?? ''}`)
      .join('|');
  }, [orderDetail]);

  const shipToZipDigest = useMemo(() => {
    const addr = (orderDetail?.order?.ship_to_address ?? null) as
      | { zip_code?: string | null; zip?: string | null }
      | null;
    return addr ? String(addr.zip_code ?? addr.zip ?? '') : '';
  }, [orderDetail]);

  useEffect(() => {
    if (!resolvedOrderId || !orderDetail || orderDetail.line_items.length === 0) {
      setWeightSummary(null);
      return;
    }
    let cancelled = false;
    portalOrderService
      .getWeightSummary(resolvedOrderId)
      .then((resp) => {
        if (!cancelled) setWeightSummary(resp);
      })
      .catch(() => {
        // Network / 5xx — fall through to the local sum without surfacing
        // a banner. The local sum is still informative for the operator.
        if (!cancelled) setWeightSummary(null);
      });
    return () => {
      cancelled = true;
    };
  }, [resolvedOrderId, lineItemWeightDigest, shipToZipDigest, orderDetail]);

  // Read/unread on the pane's email: the explicit header buttons, a sent
  // reply, and (since the 2026-10-15 revert, kanban a2888ff4) opening the
  // order with its email beside it, once per open. Both inbox views share
  // the same row, so this is what lets the desk clear an email from the
  // order instead of hunting it down again in All Emails.
  const handlePaneMarkRead = useCallback((emailIdToMark: string) => {
    setEmailPaneData((prev) => (prev ? { ...prev, isUnread: false } : prev));
    portalService.toggleReadStatus(emailIdToMark, true).catch((err) => {
      console.error('Failed to mark email as read:', err);
    });
  }, []);

  const handlePaneMarkUnread = useCallback((emailIdToMark: string) => {
    setEmailPaneData((prev) => (prev ? { ...prev, isUnread: true } : prev));
    portalService.toggleReadStatus(emailIdToMark, false).catch((err) => {
      console.error('Failed to mark email as unread:', err);
    });
  }, []);

  const handlePark = useCallback(async (reason: DraftParkReason, note: string, until: string | null) => {
    if (!orderDetail) return;
    setParkSubmitting(true);
    setParkError(null);
    try {
      const resp = await portalOrderService.parkOrder(orderDetail.order.id, reason, note || null, until);
      setOrderDetail((prev) => (prev ? { ...prev, order: resp.order } : prev));
      setShowParkDialog(false);
      onOrderAction?.('park', orderDetail.order.id);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setParkError(typeof detail === 'string' ? detail : 'Could not park this draft.');
    } finally {
      setParkSubmitting(false);
    }
  }, [orderDetail, onOrderAction]);

  const handleUnpark = useCallback(async () => {
    if (!orderDetail) return;
    setParkSubmitting(true);
    setParkError(null);
    try {
      const resp = await portalOrderService.unparkOrder(orderDetail.order.id);
      setOrderDetail((prev) => (prev ? { ...prev, order: resp.order } : prev));
      onOrderAction?.('unpark', orderDetail.order.id);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setParkError(typeof detail === 'string' ? detail : 'Could not unpark this draft.');
    } finally {
      setParkSubmitting(false);
    }
  }, [orderDetail, onOrderAction]);

  const handleOrderUpdated = useCallback((updatedOrder: OrderRecord) => {
    setOrderDetail((prev) => {
      if (!prev) return prev;
      return { ...prev, order: updatedOrder };
    });
  }, []);

  /** The truckload freight amount was set/edited — the PATCH response
   *  carries the fresh summary, so the Freight row updates in place without
   *  a reload (review 438b6037 bug). */
  const handleTruckloadFreightChanged = useCallback((summary: TruckloadFreightSummary | null) => {
    setOrderDetail((prev) => {
      if (!prev) return prev;
      return { ...prev, truckload_freight: summary };
    });
  }, []);

  // ── Open Quotes tab: attach feedback (kanban 43b3f27a) ──
  // The one-click Attach used to be silent: the button stayed put after a
  // successful attach and a second press hit the duplicate guard with no
  // visible change, so CSRs read it as broken. QuoteCard now derives an
  // "Attached" state from the order's UDF_QUOTE slots, the button shows an
  // in-flight state, and every exit of the handler (success, guard,
  // failure) surfaces a short notice on the tab.
  const [attachingQuoteId, setAttachingQuoteId] = useState<string | null>(null);
  const [detachingQuoteRef, setDetachingQuoteRef] = useState<string | null>(null);
  const [quoteAttachNotice, setQuoteAttachNotice] = useState<QuoteAttachNotice | null>(null);
  const dismissQuoteAttachNotice = useCallback(() => setQuoteAttachNotice(null), []);

  // Success/info notices clear themselves; an error stays until dismissed
  // so a CSR who looked away still sees that the attach did not land.
  useEffect(() => {
    if (!quoteAttachNotice || quoteAttachNotice.kind === 'error') return;
    const timer = setTimeout(() => setQuoteAttachNotice(null), QUOTE_ATTACH_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [quoteAttachNotice]);

  // A notice belongs to one order — drop it when the modal switches orders.
  useEffect(() => {
    setQuoteAttachNotice(null);
    setAttachingQuoteId(null);
    setDetachingQuoteRef(null);
  }, [resolvedOrderId]);

  const attachedQuoteRefs = useMemo(
    () => (orderDetail ? getAttachedQuoteRefs(orderDetail.order) : new Set<string>()),
    [orderDetail],
  );
  const quoteSlotsFull = orderDetail ? findEmptyQuoteSlot(orderDetail.order) == null : false;

  /** Attach a quote to the order: save quote ID to first empty UDF_QUOTE slot,
   *  then update overlapping line items' prices to match the quote. */
  const handleAttachQuote = useCallback(async (quoteSourceId: string) => {
    if (!orderDetail || !resolvedOrderId) return;
    const order = orderDetail.order;
    const label = `Quote #${quoteSourceId}`;
    // Already attached? The card hides Attach in this state; the guard stays
    // for stale renders and keyboard paths, and now says so instead of no-op.
    if (isQuoteAttached(order, quoteSourceId)) {
      setQuoteAttachNotice({ kind: 'info', text: `${label} is already attached to this order.` });
      return;
    }
    const emptySlot = findEmptyQuoteSlot(order);
    if (!emptySlot) {
      setQuoteAttachNotice({
        kind: 'error',
        text: `${label} was not attached — all 5 quote slots on this order are already used.`,
      });
      return;
    }

    const quote = pricingContext?.open_quotes?.find(
      (q) => normalizeQuoteRef(q.source_id) === normalizeQuoteRef(quoteSourceId),
    );
    // The one-click "Attach" is the action the Open Quotes tab exists for;
    // recorded at the point the CSR actually reaches it (guards passed).
    trackEvent('action', 'quote_apply', {
      order_id: order.id,
      quote_id: quoteSourceId,
      slot: emptySlot,
      overlapping_items: quote?.overlapping_items.length ?? 0,
    });

    setAttachingQuoteId(quoteSourceId);
    setQuoteAttachNotice(null);
    let pricesApplied = 0;
    const priceFailures: string[] = [];
    try {
      // 1. Save quote ID to UDF_QUOTE slot
      const updated = await portalOrderService.updateOrder(order.id, { [emptySlot]: quoteSourceId } as Record<string, string>);
      handleOrderUpdated(updated);

      // 2. Update overlapping line item prices to match the quote
      if (quote && quote.overlapping_items.length > 0) {
        for (const overlap of quote.overlapping_items) {
          const matchingLineItem = orderDetail.line_items.find(
            (li) => li.item_code === overlap.item_code,
          );
          if (matchingLineItem && overlap.price != null) {
            const quotePrice = String(overlap.price);
            // Skip if price already matches
            if (matchingLineItem.unit_price === quotePrice) continue;
            try {
              const resp = await portalOrderService.updateLineItem(
                order.id,
                matchingLineItem.id,
                { unit_price: quotePrice },
              );
              handleLineItemUpdated(resp);
              pricesApplied += 1;
            } catch (err) {
              // Best-effort: continue with remaining items, but tell the
              // CSR which line kept its old price and why.
              priceFailures.push(`${overlap.item_code}: ${describeSaveFailure(err)}`);
            }
          }
        }
      }
      const applied =
        pricesApplied > 0
          ? `${label} attached — ${pricesApplied} line price${pricesApplied === 1 ? '' : 's'} updated to the quote.`
          : `${label} attached.`;
      setQuoteAttachNotice(
        priceFailures.length > 0
          ? {
              kind: 'error',
              text: `${applied} ${priceFailures.length} line price${priceFailures.length === 1 ? '' : 's'} could NOT be updated — ${priceFailures.join('; ')}`,
            }
          : { kind: 'success', text: applied },
      );
      // The Referenced-on-this-order list comes from the pricing context.
      void refreshPricingContext(resolvedOrderId);
    } catch (err) {
      // The slot write failed — say so, with the server's reason; the
      // silent no-op was the bug here.
      setQuoteAttachNotice({ kind: 'error', text: `${label} could not be attached: ${describeSaveFailure(err)}` });
    } finally {
      setAttachingQuoteId(null);
    }
  }, [orderDetail, resolvedOrderId, pricingContext, handleOrderUpdated, handleLineItemUpdated, refreshPricingContext]);

  /** Remove a quote reference from the order by clearing the UDF_QUOTE slot
   *  that holds it (owner, review 3gsb7j: "we should have an unattach").
   *  Line prices are left as they are — re-pricing is the CSR's explicit
   *  call (Apply / manual price), never a silent side effect of a removal. */
  const handleDetachQuote = useCallback(async (quoteRef: string, slot?: QuoteSlot) => {
    if (!orderDetail || !resolvedOrderId) return;
    const order = orderDetail.order;
    const label = `Quote #${quoteRef}`;
    const targetSlot = slot ?? findQuoteSlot(order, quoteRef);
    if (!targetSlot) {
      setQuoteAttachNotice({ kind: 'info', text: `${label} is not attached to this order.` });
      return;
    }
    trackEvent('action', 'quote_detach', { order_id: order.id, quote_id: quoteRef, slot: targetSlot });
    setDetachingQuoteRef(quoteRef);
    setQuoteAttachNotice(null);
    try {
      const patch = { [targetSlot]: null } as PortalOrderUpdateRequest;
      const updated = await portalOrderService.updateOrder(order.id, patch);
      handleOrderUpdated(updated);
      setQuoteAttachNotice({
        kind: 'success',
        text: `${label} removed from this order. Line prices were left as they are.`,
      });
      void refreshPricingContext(resolvedOrderId);
    } catch (err) {
      setQuoteAttachNotice({ kind: 'error', text: `${label} could not be removed: ${describeSaveFailure(err)}` });
    } finally {
      setDetachingQuoteRef(null);
    }
  }, [orderDetail, resolvedOrderId, handleOrderUpdated, refreshPricingContext]);

  useEffect(() => {
    if (!isOpen || !resolvedOrderId) return;
    fetchData(resolvedOrderId);
  }, [isOpen, resolvedOrderId, fetchData]);

  // ── Backorder prompts: load unanswered offers for the open order ──
  useEffect(() => {
    // Advisories are per-edit, per-order: never carry them across orders.
    setLineAdvisories(new Map());
    if (!isOpen || !resolvedOrderId) {
      setBackorderPrompts([]);
      setAcceptedBackorderIds(new Map());
      return;
    }
    let cancelled = false;
    portalOrderService.listBackorderPrompts(resolvedOrderId)
      .then((prompts) => { if (!cancelled) setBackorderPrompts(prompts); })
      .catch(() => { if (!cancelled) setBackorderPrompts([]); });
    return () => { cancelled = true; };
  }, [isOpen, resolvedOrderId]);

  // ── Reset on close ──
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('order');
      setOrderDetail(null);
      setPricingContext(null);
      setError(null);
      setSageError(null);
      setFreightCarrierError(false);
      setResolvedOrderId(null);
      setLoading(true);
      setExpandedAgreements(new Set());
      setShowActiveOnly(true);
      setEmailPaneOpen(true);
      setEmailPaneData(null);
      setEmailPaneError(null);
      setShowCustomerSearch(false);
      setShowShipToSearch(false);
    }
  }, [isOpen]);

  // ── Keyboard escape ──
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Already spent by a control that acted on it (the ship-to search's
        // Cancel, an open dropdown popover, the reply composer): nothing to
        // do. React flushes that control's state change before this listener
        // runs, so the inner-overlay checks below would already read closed.
        if (e.defaultPrevented) return;
        // If the user is mid-edit on an inline line-item cell, let the cell's
        // own Escape handler cancel the edit instead of closing the modal.
        // Use e.target (not document.activeElement) — by the bubble phase the
        // input has already blurred, but e.target is still the original input.
        const target = e.target as HTMLElement | null;
        if (
          target &&
          (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') &&
          target.dataset.inlineEditCell === 'true'
        ) {
          return;
        }
        // Close inner modals first. preventDefault marks the key as spent so
        // a host that also closes on Escape (the split view, the email pane's
        // own handler) leaves this order and its panel alone (kanban 2c097936:
        // one Escape in the ship-to form used to close the whole order).
        if (showCustomerSearch) { e.preventDefault(); setShowCustomerSearch(false); setCustomerSearchQuery(''); setCustomerSearchResults([]); return; }
        if (showShipToSearch) { e.preventDefault(); setShowShipToSearch(false); return; }
        // Embedded in a split view the host owns closing.
        if (isEmbedded) return;
        // Something stacked above (the reply composer) may consume this same
        // key; every listener has run by the time the macrotask fires (a
        // microtask would run between listeners on a real key press).
        window.setTimeout(() => { if (!e.defaultPrevented) onClose(); }, 0);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, isEmbedded, onClose, showCustomerSearch, showShipToSearch]);

  // Next / previous shortcuts (kanban 97abd058). Off while any dialog this
  // modal owns is open, so a key pressed in a confirm, the customer or
  // ship-to search, the Sage editor or the reply composer never switches the
  // order underneath it. Typing in any field is suppressed inside the hook.
  const ownOverlayOpen =
    showCustomerSearch ||
    showShipToSearch ||
    showNewShipToForm ||
    showProposedCustomerModal ||
    showSageEdit ||
    showDismissConfirm ||
    showParkDialog ||
    showConfirmSage ||
    showUndoConfirmSage ||
    showCreditHoldConfirm ||
    showFreightConfirm ||
    paneComposeAction !== null;
  useDetailNavKeys(navigation, isOpen && renderMode !== 'embedded' && !ownOverlayOpen);

  // ── Actions ──
  const handleTransition = async (
    action: string,
    notes?: string,
    reason?: string,
    opts?: { acknowledgeWarnings?: boolean; sageOrderNo?: string | null },
  ): Promise<boolean> => {
    if (!resolvedOrderId) return false;
    setActionLoading(true);
    setSageError(null);
    setTransitionError(null);
    setSubmissionFieldErrors([]);
    setSubmissionWarnings(null);
    try {
      const result = await portalOrderService.transitionOrder(resolvedOrderId, {
        action,
        notes,
        reason,
        acknowledge_warnings: opts?.acknowledgeWarnings,
        sage_order_no: opts?.sageOrderNo ?? undefined,
      });
      setOrderDetail(result);
      onOrderAction?.(action, resolvedOrderId);
      // Submission feedback. A requires_acknowledgment result is the soft
      // ship-to "doesn't match" gate — show the "Submit anyway" confirm rather
      // than a hard error. Any other failure is a real error.
      if (result.sage_submission && !result.sage_submission.success) {
        if (result.sage_submission.requires_acknowledgment) {
          setSubmissionWarnings(result.sage_submission.submission_warnings ?? []);
        } else {
          setSageError(result.sage_submission.error_message || 'Sage submission failed');
          if (result.sage_submission.submission_field_errors?.length) {
            setSubmissionFieldErrors(result.sage_submission.submission_field_errors);
          }
        }
      }
      // Refresh pricing context too
      const pricing = await portalOrderService.getPricingContext(resolvedOrderId);
      setPricingContext(pricing);
      return true;
    } catch (err) {
      console.error('Transition failed', err);
      // The server's 400 reason (e.g. "Order is not in Sage — cannot confirm")
      // is the whole point of the guard; surface it in whichever dialog asked.
      const resp = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
      const detail = resp?.data?.detail;
      // A 404 on submit means another user dismissed the draft under us; a
      // 409 is the server's own account of a dismiss that landed mid-submit
      // (the Sage order exists, a red flag names it) and reads as-is.
      const message =
        resp?.status === 404 && action === 'submit'
          ? 'This draft was dismissed by another user and no longer exists in the portal.'
          : typeof detail === 'string'
            ? detail
            : 'The action failed. Please try again.';
      setTransitionError(message);
      // Submit has no dialog of its own: the Sage banner is where the rep
      // looks, and without this a refused submit showed nothing at all.
      if (action === 'submit') setSageError(message);
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmSage = async (sageOrderNo: string | null) => {
    const ok = await handleTransition('confirm_sage', undefined, undefined, { sageOrderNo });
    if (ok) setShowConfirmSage(false);
  };

  const handleUndoConfirmSage = async () => {
    const ok = await handleTransition('unconfirm_sage');
    if (ok) setShowUndoConfirmSage(false);
  };

  // Freight carrier (ship_via) is expected before submit. We intentionally do
  // NOT carve out truckload-pending (FRT) orders — a blank carrier always trips
  // the soft gate and the rep overrides it via "submit anyway" when freight is
  // handled out-of-band. That keeps the rule future-proof: no code change when
  // a new "carrier comes later" case shows up.
  const isFreightCarrierMissing = useMemo(() => {
    const order = orderDetail?.order;
    if (!order) return false;
    return !order.ship_via || order.ship_via.trim() === '';
  }, [orderDetail]);

  // Auto-clear the red highlight the moment a carrier is picked so it doesn't
  // linger after the rep resolves the blank.
  useEffect(() => {
    if (!isFreightCarrierMissing) setFreightCarrierError(false);
  }, [isFreightCarrierMissing]);

  // ── Review-answer submit gate ──
  // Unresolved BLOCKER/VERIFY review lines on a DRAFT hard-disable the
  // Submit button — the CSR answers them in the panel above the line table
  // first. The overridable submission-warning gate below stays as backstop.
  const pendingAnswerCount = useMemo(
    () =>
      orderDetail?.order?.status === 'draft'
        ? orderDetail.line_items.filter(lineNeedsAnswer).length
        : 0,
    [orderDetail],
  );

  // ── Revision-proposal submit gate (kanban 3db03d35) ──
  // A revised PO that arrived while this order was still a draft became a
  // Merlin edit proposal ON the draft. Submitting past it sends the
  // pre-revision lines to Sage (AU01927 shipped 34, the revision said 40),
  // so Submit is disabled until the CSR confirms or dismisses the card
  // above the line table. The backend submit route enforces the same rule.
  const proposalTargetIsDraft =
    orderDetail?.order?.status === 'draft' && !orderDetail.order.sage_order_no;
  const pendingProposalCount = proposalTargetIsDraft
    ? (orderDetail?.edit_proposals ?? []).length
    : 0;
  const submitGateNote =
    pendingAnswerCount > 0
      ? `Answer the ${pendingAnswerCount} review item${pendingAnswerCount === 1 ? '' : 's'} in the line table to submit`
      : pendingProposalCount > 0
        ? `Confirm or dismiss Merlin's proposed change${pendingProposalCount === 1 ? '' : 's'} from the customer's revised PO before submitting`
        : null;

  const runPriceMismatchGate = (): boolean => {
    // Returns true when a confirm dialog was opened (caller should
    // bail). Off-contract or PO-vs-waterfall price mismatches each
    // open the same dialog.
    if (!orderDetail) return false;
    const mismatches = findAllPriceMismatches(
      orderDetail.line_items,
      pricingContext?.line_items,
    );
    if (mismatches.length > 0) {
      setPriceMismatches(mismatches);
      return true;
    }
    return false;
  };

  // ── Leftover Merlin suggestions (stamped on the way out, no prompt) ──
  const suggestedSubmitRows = orderInstructions.filter((i) => i.sage_state === 'suggested');

  // Every earlier gate's "continue" lands here, so the stamp happens exactly
  // once, after the rep has cleared the other warnings. A failed stamp
  // stops the submit: the rows would otherwise reach the backend backstop
  // and resurface as the generic "submit anyway?" dialog.
  const submitAfterGates = async () => {
    if (resolvedOrderId && suggestedSubmitRows.length > 0) {
      setActionLoading(true);
      setInstructionStampError(null);
      try {
        const resp = await portalOrderService.decideInstructions(resolvedOrderId, {
          accept: [],
          skip: suggestedSubmitRows.map((r) => r.id),
          skip_context: 'left_off_at_submit',
        });
        setOrderInstructions(resp.instructions);
      } catch {
        setInstructionStampError(
          "Could not record Merlin's PO notes as left off. Please retry.",
        );
        setActionLoading(false);
        return;
      }
    }
    await handleTransition('submit');
  };

  const scrollToInstructions = () => {
    const section = document.querySelector('[data-testid="order-instructions-section"]');
    section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmit = () => {
    // Three gates run in sequence before the submit fires, each OVERRIDABLE:
    //   0. freight carrier — no ship_via: highlight the field red and ask
    //      "is it blank — submit anyway?" (the rep judges every case)
    //   1. credit_hold='Y' — accounting has frozen this customer; the
    //      portal warns the rep before Sage swallows the order silently
    //   2. price mismatches — off-contract or PO-vs-waterfall
    // Each opens a confirm modal the user acknowledges one at a time. Once all
    // clear, submitAfterGates stamps any leftover Merlin suggestion as left
    // off (no popup, kanban 88963483) and the transition fires.
    if (isFreightCarrierMissing) {
      setFreightCarrierError(true);
      setActiveTab('order');
      setShowFreightConfirm(true);
      return;
    }
    if (pricingContext?.customer_on_hold) {
      setShowCreditHoldConfirm(true);
      return;
    }
    if (runPriceMismatchGate()) return;
    void submitAfterGates();
  };

  const handleConfirmSubmitDespiteNoCarrier = () => {
    // Rep acknowledged the blank Ship Via. Continue down the submit pipeline —
    // the credit-hold and price-mismatch gates may still fire next.
    setShowFreightConfirm(false);
    if (pricingContext?.customer_on_hold) {
      setShowCreditHoldConfirm(true);
      return;
    }
    if (runPriceMismatchGate()) return;
    void submitAfterGates();
  };

  const handleConfirmSubmitDespiteHold = () => {
    // User acknowledged the credit-hold warning. Continue down the
    // submit pipeline — the price-mismatch gate may still fire next.
    setShowCreditHoldConfirm(false);
    if (runPriceMismatchGate()) return;
    void submitAfterGates();
  };

  const handleConfirmSubmitDespiteMismatch = () => {
    setPriceMismatches(null);
    void submitAfterGates();
  };

  const handleConfirmSubmitDespiteWarnings = async () => {
    // Rep acknowledged the soft submission warning(s) — ship-to "doesn't match"
    // and/or lines still flagged needs_review. Re-submit with the override flag
    // so the backend sends it to Sage and records the override on the audit.
    setSubmissionWarnings(null);
    await handleTransition('submit', undefined, undefined, { acknowledgeWarnings: true });
  };

  const handleDismissDraft = async () => {
    if (!resolvedOrderId) return;
    setActionLoading(true);
    setDismissError(null);
    try {
      await portalOrderService.dismissDraft(resolvedOrderId);
      onOrderAction?.('dismiss', resolvedOrderId);
      setShowDismissConfirm(false);
      onClose();
    } catch (err) {
      console.error('Dismiss draft failed', err);
      // Another user acted on the draft first (Saint Henry's, 2026-10-20):
      // a 400 means it is no longer a draft (submitted to Sage while this
      // modal was open), a 404 that it was already dismissed. Say so in the
      // confirm dialog instead of swallowing it, and re-read the order so
      // the modal shows its real state (the Dismiss button goes away).
      // A 409 means the database refused the delete because a linked record
      // still points at the draft (kanban 39cb008b, prod 2026-10-29): the
      // server names the relation; retrying will not help, reporting will.
      const resp = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
      const detail = typeof resp?.data?.detail === 'string' ? resp.data.detail : null;
      const message =
        resp?.status === 404
          ? 'This draft was already dismissed by another user.'
          : resp?.status === 400
            ? `This order is no longer a draft (another user acted on it first), so it cannot be dismissed. ${detail ?? ''}`.trim()
            : resp?.status === 409
              ? (detail ??
                'This draft still has linked records that block dismissal. Nothing was changed. Please report this draft.')
              : (detail ?? 'Dismiss failed. Please try again.');
      setDismissError(message);
      void fetchData(resolvedOrderId);
    } finally {
      setActionLoading(false);
    }
  };

  // ── Customer change handler ──
  // Gate (kanban 3019f212): on an agent-originated draft, a customer that
  // leaves the PO the draft was generated from first asks "is this a
  // different order?". The answer is deterministic: "different order" records
  // the ruling (detach-source-identity) and then saves; "same order" just
  // saves; Cancel keeps the original. Fail-open: a failed check never blocks.
  const [customerIdentityWarning, setCustomerIdentityWarning] = useState<
    PendingHeaderIdentityWarning<{ customerNo: string; customerName: string }> | null
  >(null);
  const handleCustomerChange = async (customerNo: string, customerName: string) => {
    if (!resolvedOrderId || !orderDetail) return;
    const check = await runHeaderIdentityCheck(orderDetail.order, { customer_no: customerNo });
    if (check) {
      // Close the picker first: both overlays share a z-index and the picker
      // is a later sibling, so left open it would sit on top of the warning
      // and swallow its clicks.
      setShowCustomerSearch(false);
      setCustomerSearchQuery('');
      setCustomerSearchResults([]);
      setCustomerIdentityWarning({ check, pending: { customerNo, customerName } });
      return;
    }
    await applyCustomerChange(customerNo, customerName);
  };
  const applyCustomerChange = async (
    customerNo: string,
    customerName: string,
    ruling?: HeaderIdentityRuling,
  ) => {
    if (!resolvedOrderId) return;
    setActionLoading(true);
    try {
      if (ruling?.differentOrder) {
        await portalOrderService.detachSourceIdentity(resolvedOrderId, {
          reason: 'different_order',
          note: ruling.note?.trim() || null,
        });
      }
      // Backend auto-derives salesperson from customer
      const updated = await portalOrderService.updateOrder(resolvedOrderId, {
        customer_no: customerNo,
        customer_name: customerName,
      });
      setCustomerIdentityWarning(null);
      setOrderDetail((prev) => prev ? { ...prev, order: updated } : prev);
      setShowCustomerSearch(false);
      setCustomerSearchQuery('');
      setCustomerSearchResults([]);
      // Refresh pricing context for new customer
      if (resolvedOrderId) {
        const pricing = await portalOrderService.getPricingContext(resolvedOrderId);
        setPricingContext(pricing);
      }
    } catch (err) {
      console.error('Failed to update customer', err);
    } finally {
      setActionLoading(false);
    }
  };

  // ── Merlin propose-new handlers ──
  // The proposals ride llm_extraction_data verbatim; these just open the
  // EXISTING create flows prefilled. The CSR click is the only gate.
  const proposedNewCustomer = orderDetail?.order?.llm_extraction_data?.proposed_new_customer ?? null;
  const proposedNewShipTo = orderDetail?.order?.llm_extraction_data?.proposed_new_ship_to ?? null;

  const handleProposedCustomerCreated = async (customerNo: string, customerName?: string) => {
    setShowProposedCustomerModal(false);
    await handleCustomerChange(customerNo, customerName || proposedNewCustomer?.name || customerNo);
  };

  const handleCreateProposedShipTo = () => {
    if (!proposedNewShipTo) return;
    setNewShipToPrefill({
      name: proposedNewShipTo.name ?? '',
      address1: proposedNewShipTo.address1 ?? '',
      address2: proposedNewShipTo.address2 ?? '',
      city: proposedNewShipTo.city ?? '',
      state: proposedNewShipTo.state ?? '',
      zip: proposedNewShipTo.zip ?? '',
    });
    setShowShipToSearch(true);
    setShowNewShipToForm(true);
  };

  const handleCustomerSearchInput = (query: string) => {
    setCustomerSearchQuery(query);
    if (customerSearchTimeout.current) clearTimeout(customerSearchTimeout.current);
    if (query.length < 2) {
      setCustomerSearchResults([]);
      return;
    }
    setCustomerSearchLoading(true);
    customerSearchTimeout.current = setTimeout(async () => {
      try {
        const { portalService } = await import('../../services/portal');
        const results = await portalService.searchCustomers(query, 10);
        setCustomerSearchResults(results);
      } catch {
        setCustomerSearchResults([]);
      } finally {
        setCustomerSearchLoading(false);
      }
    }, 300);
  };

  // ── Ship-to change handler ──
  const handleShipToSelect = async (addr: ShipToResult) => {
    if (!resolvedOrderId) return;
    try {
      const updated = await portalOrderService.updateOrder(resolvedOrderId, {
        ship_to_address: {
          ship_to_code: addr.ship_to_code,
          ship_to_name: addr.ship_to_name,
          address1: addr.address1,
          address2: addr.address2 || '',
          address3: addr.address3 || '',
          city: addr.city,
          state: addr.state,
          zip_code: addr.zip_code,
          warehouse_code: addr.warehouse_code || '',
        },
      });
      setOrderDetail((prev) => prev ? { ...prev, order: updated } : prev);
      setShipToSaveError(null);
      setShowShipToSearch(false);
    } catch (err) {
      console.error('Failed to update ship-to', err);
      setShipToSaveError(formatHeaderSaveError(err, 'The ship-to address'));
      setShowShipToSearch(false);
    }
  };

  // ── New-ship-to created: bind the fresh code + address onto the order ──
  const handleNewShipToCreated = async (addr: CreatedShipTo) => {
    if (!resolvedOrderId) return;
    const draftPhase = orderDetail?.order?.status === 'draft'
      || orderDetail?.order?.status === 'submission_failed';
    if (!draftPhase) {
      // Sage-landed order: the local record mirrors Sage, so it is NOT
      // patched here. The address now exists under the customer; putting it
      // on THIS order goes through the audited ✎ Edit in Sage path.
      setNewShipToNote(
        (addr.edited
          ? `Ship-to ${addr.ship_to_code} updated in Sage (syncs in ~2 min).`
          : `New ship-to ${addr.ship_to_code} queued to Sage (syncs in ~2 min).`) +
          (addr.tax_schedule_warning ? ` ⚠ ${addr.tax_schedule_warning}` : '') +
          (addr.edited
            ? ''
            : ' To use it on this order, open ✎ Edit in Sage and pick the new address.'),
      );
      setShowNewShipToForm(false);
      setShowShipToSearch(false);
      void fetchData(resolvedOrderId);
      return;
    }
    try {
      const updated = await portalOrderService.updateOrder(resolvedOrderId, {
        ship_to_address: {
          ship_to_code: addr.ship_to_code,
          ship_to_name: addr.ship_to_name,
          address1: addr.address1,
          address2: addr.address2,
          address3: addr.address3,
          city: addr.city,
          state: addr.state,
          zip_code: addr.zip_code,
        },
      });
      setOrderDetail((prev) => prev ? { ...prev, order: updated } : prev);
      setNewShipToNote(
        (addr.edited
          ? `Ship-to ${addr.ship_to_code} updated in Sage (syncs in ~2 min).`
          : `New ship-to ${addr.ship_to_code} queued to Sage (syncs in ~2 min).`) +
          (addr.tax_schedule_warning ? ` ⚠ ${addr.tax_schedule_warning}` : ''),
      );
      setShipToSaveError(null);
      setShowNewShipToForm(false);
      setShowShipToSearch(false);
    } catch (err) {
      console.error('Failed to bind new ship-to onto order', err);
      // The Sage address exists; only the binding to THIS draft failed.
      setShipToSaveError(
        formatHeaderSaveError(err, `The new ship-to ${addr.ship_to_code}`)
          + ' (The address itself was created in Sage; pick it again from the ship-to list.)',
      );
      setShowNewShipToForm(false);
      setShowShipToSearch(false);
    }
  };

  // ── Source email pane ──
  const sourceEmailId = orderDetail?.source_email_id || orderDetail?.order?.source_email_id || null;
  const emailPaneActive = !isEmbedded && emailPaneOpen && !!sourceEmailId;

  // Load the source email whenever the pane is showing for an order. Re-runs
  // when the pane is re-shown so a reply that landed meanwhile is picked up.
  // Showing the email marks it read, but only on the first load per open of
  // this order, whether or not the email was unread then: Hide Email / Show
  // Email after a Mark as unread must not flip it back (the unread mark has
  // to stick until a fresh open). The ref records that this open has had
  // its one chance, so it is set on the first load even for a read email.
  const autoReadChanceUsedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isOpen) autoReadChanceUsedForRef.current = null;
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen || !emailPaneActive || !sourceEmailId) return;
    let cancelled = false;
    setEmailPaneLoading(true);
    setEmailPaneError(null);
    (async () => {
      try {
        const { portalService } = await import('../../services/portal');
        const emailData = await portalService.getEmailDetail(sourceEmailId);
        if (cancelled) return;
        setEmailPaneData(emailData ? mapEmailToComponent(emailData) : null);
        const firstLoadThisOpen = autoReadChanceUsedForRef.current !== sourceEmailId;
        autoReadChanceUsedForRef.current = sourceEmailId;
        if (emailData?.is_unread && firstLoadThisOpen) {
          handlePaneMarkRead(sourceEmailId);
        }
      } catch (err) {
        console.error('Failed to load source email', err);
        if (!cancelled) setEmailPaneError('Failed to load email');
      } finally {
        if (!cancelled) setEmailPaneLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, emailPaneActive, sourceEmailId, handlePaneMarkRead]);

  // "Add Another Order" in the email pane: swap the order panel to the new
  // draft (fetchData re-runs on resolvedOrderId, and the sibling bar refetches
  // on the same change) and let the page behind refresh its list. orderDetail
  // is deliberately NOT cleared here: the sibling shares this source email, so
  // keeping it avoids unmounting the email pane for a frame.
  const handleOrderCreatedFromEmailPane = useCallback(
    (_emailId: string, newOrderId: string) => {
      setResolvedOrderId(newOrderId);
      setActiveTab('order');
      onOrderAction?.('created', newOrderId);
    },
    [onOrderAction],
  );

  const handleShipToCustomerMismatch = (_addr: ShipToResult, ownerNo: string, ownerName: string) => {
    alert(`This address belongs to ${ownerName} (${ownerNo}), which is not the current customer.`);
  };

  // ── Helpers ──
  const formatPrice = (val: string | number | null | undefined): string => {
    if (val === null || val === undefined) return '-';
    const num = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(num)) return '-';
    return `$${num.toFixed(2)}`;
  };

  const formatDateLocal = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    const result = formatDate(dateStr);
    return result === 'Never' ? '-' : result;
  };

  const getStatusClass = (status: OrderStatus): string => {
    const map: Record<string, string> = {
      draft: styles.statusDraft,
      submission_failed: styles.statusCancelled,  // red styling, same as cancelled
      submitted_to_sage: styles.statusSubmittedToSage,
      confirmed_in_sage: styles.statusConfirmedInSage,
      accepted: styles.statusAccepted,
      shipped: styles.statusShipped,
      order_completed: styles.statusCompleted,
      cancelled: styles.statusCancelled,
    };
    return map[status] || '';
  };

  /**
   * Resolve the effective special price for a line item.
   *
   * Flat rows: returns `special_price`.
   * Tiered rows (`pricing_type='tiered'`): picks the tier price matching
   * the line's quantity — tier 3 if qty >= tier_price_3_min, else tier 2
   * if qty >= tier_price_2_min, else tier 1. Returns null if nothing can
   * be resolved (row isn't special, or tiered row has no tier prices).
   */
  const getEffectiveSpecialPrice = (
    pItem: LineItemPricingContext,
  ): { price: number | null; isTiered: boolean; tier: 1 | 2 | 3 | null } => {
    if (!pItem.has_special_pricing) return { price: null, isTiered: false, tier: null };
    // Tiered pricing takes priority over flat special_price
    if ((pItem.pricing_type ?? '').toLowerCase() === 'tiered') {
      const qty = parseFloat(pItem.quantity ?? '');
      const resolved = resolveTierPrice(pItem, qty);
      if (resolved.price !== null) {
        return { price: resolved.price, isTiered: true, tier: resolved.tier };
      }
    }
    if (pItem.special_price !== null) {
      return { price: pItem.special_price, isTiered: false, tier: null };
    }
    return { price: null, isTiered: false, tier: null };
  };

  /**
   * Render a hover-tooltip badge for a tiered special price, showing the
   * active tier (T1/T2/T3) inline and the full tier breakdown on hover.
   */
  const renderTierBadge = (
    pItem: LineItemPricingContext,
    activeTier: 1 | 2 | 3,
  ) => {
    const tiers: Array<{
      n: 1 | 2 | 3;
      price: number | null;
      min: number | null;
    }> = [
      { n: 1, price: pItem.tier_price_1, min: null },
      { n: 2, price: pItem.tier_price_2, min: pItem.tier_price_2_min },
      { n: 3, price: pItem.tier_price_3, min: pItem.tier_price_3_min },
    ];
    return (
      <span className={styles.tooltipWrap} style={{ marginLeft: '4px' }}>
        <span className={styles.tierBadge}>{`T${activeTier}`}</span>
        <span className={styles.tierTooltip}>
          <div className={styles.tierTooltipTitle}>Tiered special pricing</div>
          {tiers.map((t) => {
            if (t.price === null) return null;
            const isActive = t.n === activeTier;
            const rangeLabel =
              t.n === 1
                ? t.min !== null
                  ? `< ${t.min}`
                  : 'base'
                : `${t.min ?? '?'}+`;
            return (
              <div
                key={t.n}
                className={`${styles.tierTooltipRow} ${isActive ? styles.tierTooltipRowActive : ''}`}
              >
                <span className={styles.tierTooltipLabel}>T{t.n}</span>
                <span className={styles.tierTooltipPrice}>{formatPrice(t.price)}</span>
                <span className={styles.tierTooltipRange}>{rangeLabel}</span>
              </div>
            );
          })}
          <div className={styles.tierTooltipFooter}>
            Active tier selected by line qty
            {pItem.quantity ? ` (${pItem.quantity})` : ''}
          </div>
        </span>
      </span>
    );
  };

  /** Get the "recommended price" for a line item using the price resolution waterfall */
  const getRecommendedPrice = (pItem: LineItemPricingContext): { price: string | null; source: string } => {
    // Use resolved price from the backend waterfall when available
    if (pItem.resolved_price !== null && pItem.resolved_price !== undefined) {
      return {
        price: formatPrice(pItem.resolved_price),
        source: pItem.price_source ?? 'unknown',
      };
    }
    // Fallback to legacy logic if resolve_price fields are not populated
    const effective = getEffectiveSpecialPrice(pItem);
    if (effective.price !== null) {
      return {
        price: formatPrice(effective.price),
        source: effective.isTiered ? 'tiered' : 'special',
      };
    }
    if (pItem.standard_price !== null) {
      return { price: formatPrice(pItem.standard_price), source: 'standard' };
    }
    return { price: null, source: 'none' };
  };

  /** Check if a line item's order price matches recommended price */
  const getPricingStatus = (
    lineItem: OrderLineItem,
    pItem: LineItemPricingContext | undefined,
  ): 'match' | 'mismatch' | 'no-pricing' => {
    if (!pItem) return 'no-pricing';
    const orderPrice = parseFloat(lineItem.unit_price ?? '');
    if (isNaN(orderPrice)) return 'no-pricing';

    // Use resolved price from waterfall when available
    if (pItem.resolved_price !== null && pItem.resolved_price !== undefined) {
      const resolved = parseFloat(pItem.resolved_price);
      if (!isNaN(resolved)) {
        return Math.abs(orderPrice - resolved) < 0.01 ? 'match' : 'mismatch';
      }
    }

    // Fallback to legacy special pricing check
    const effective = getEffectiveSpecialPrice(pItem);
    if (effective.price !== null) {
      return Math.abs(orderPrice - effective.price) < 0.01 ? 'match' : 'mismatch';
    }
    return 'no-pricing';
  };

  /** Count items with multiple active special pricings (duplicates) */
  const getMultiPricingWarnings = (): string[] => {
    if (!pricingContext) return [];
    // This check would require backend to return duplicate info.
    // For now, check if the same item code appears in multiple agreements.
    // We'll rely on the line_items data which only takes the first active match.
    // Future enhancement: backend returns warning flags.
    return [];
  };

  const toggleAgreement = (id: string) => {
    setExpandedAgreements((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // ── Derived data ──
  const pricingMap = new Map<string, LineItemPricingContext>();
  if (pricingContext) {
    for (const p of pricingContext.line_items ?? []) {
      pricingMap.set(p.item_code, p);
    }
  }

  // Items in the order (for highlighting in agreement products)
  const orderItemCodes = new Set(
    orderDetail?.line_items.map((li) => li.item_code) ?? [],
  );

  // Filter agreements
  const filteredAgreements = pricingContext?.agreements.filter(
    (a) => !showActiveOnly || a.is_active,
  ) ?? [];

  // Warnings
  const multiPricingWarnings = getMultiPricingWarnings();

  if (!isOpen) return null;

  const panel = (
      <div
        className={isEmbedded ? styles.embeddedPanel : styles.modal}
        data-testid="order-detail-panel"
        data-target-order-id={resolvedOrderId ?? undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={styles.header} data-section="order_header" data-target-order-id={resolvedOrderId ?? undefined}>
          <div className={styles.headerLeft}>
            {orderDetail ? (
              <>
                <div className={styles.title}>
                  <span
                    style={{ cursor: 'pointer', color: 'var(--primary, #0B4FA3)' }}
                    title="View customer profile"
                    onClick={() => navigate(`/portal/customers/${orderDetail.order.customer_no}`)}
                  >
                    {orderDetail.order.customer_name}
                  </span>
                  {canManageOrders && (orderDetail.order.status === 'draft' || orderDetail.order.status === 'submission_failed') && (
                    <button
                      type="button"
                      className={styles.headerEditBtn}
                      onClick={() => setShowCustomerSearch(true)}
                      title="Change customer"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 3a2.83 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                      </svg>
                    </button>
                  )}
                </div>
                <div className={styles.subtitle}>
                  <span>{orderDetail.order.customer_no}</span>
                  <span>&middot;</span>
                  <span className={`${styles.statusBadge} ${getStatusClass(orderDetail.order.status)}`}>
                    {ORDER_STATUS_LABELS[orderDetail.order.status]}
                  </span>
                  {orderDetail.order.due_urgency && (
                    <>
                      <span>&middot;</span>
                      <DueBadge order={orderDetail.order} />
                    </>
                  )}
                  {orderDetail.order.parked_reason && (
                    <>
                      <span>&middot;</span>
                      <ParkedBadge order={orderDetail.order} />
                    </>
                  )}
                  {isAwaitingSageAccount(orderDetail.order) && (
                    <>
                      <span>&middot;</span>
                      <AwaitingSageAccountBadge order={orderDetail.order} />
                    </>
                  )}
                  {hasResolvedItems(orderDetail.order) && (
                    <>
                      <span>&middot;</span>
                      <ItemsNowInSageBadge order={orderDetail.order} />
                    </>
                  )}
                  {orderDetail.order.deleted_in_sage && (
                    <>
                      <span>&middot;</span>
                      <DeletedInSageBadge
                        detectedAt={orderDetail.order.deleted_in_sage_detected_at}
                      />
                    </>
                  )}
                  <span>&middot;</span>
                  <span>
                    {orderDetail.order.source_type === 'email_llm' ? 'AI Detected' : 'Manual'}
                  </span>
                  {/* Embedded (inbox split view) already shows the email on
                      the left, so the toggle only exists in modal mode. */}
                  {!isEmbedded && sourceEmailId && (
                    <>
                      <span>&middot;</span>
                      <button
                        type="button"
                        className={styles.viewEmailBtn}
                        onClick={() => setEmailPaneOpen((open) => !open)}
                        aria-pressed={emailPaneOpen}
                        aria-expanded={emailPaneOpen}
                        title={emailPaneOpen ? 'Hide the source email pane' : 'Show the source email beside the order'}
                        data-testid="order-email-pane-toggle"
                        data-action="order_source_email_toggle"
                        data-target-order-id={resolvedOrderId ?? undefined}
                        data-target-email-id={sourceEmailId ?? undefined}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                          <polyline points="22,6 12,13 2,6" />
                        </svg>
                        {emailPaneOpen ? 'Hide Email' : 'Show Email'}
                      </button>
                    </>
                  )}
                  {isAdmin && (orderDetail.source_email_id || orderDetail.order.source_email_id) && (
                    <>
                      <span>&middot;</span>
                      <button
                        type="button"
                        className={styles.viewEmailBtn}
                        onClick={() => navigate(`/email-monitor-decisions?emailId=${orderDetail.source_email_id || orderDetail.order.source_email_id}`)}
                        title="View agent decision for this email (admin only)"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                        </svg>
                        View Agent Run
                      </button>
                    </>
                  )}
                  {isAdmin && orderDetail.order.llm_extraction_data?.extractor === 'merlin'
                    && orderDetail.order.llm_extraction_data?.merlin_audit?.conversation_id && (
                    <>
                      <span>&middot;</span>
                      <button
                        type="button"
                        className={styles.viewEmailBtn}
                        data-testid="view-merlin-run"
                        onClick={() => navigate(`/agent-console?conversation=${orderDetail.order.llm_extraction_data?.merlin_audit?.conversation_id}`)}
                        title="View Merlin's full extraction run — every query he ran and why (admin only)"
                      >
                        <span aria-hidden="true" style={{ fontSize: 12 }}>&#10024;</span>
                        View Merlin's Run
                      </button>
                    </>
                  )}
                </div>
                {orderDetail.order.parked_reason && (
                  <div
                    data-testid="parked-banner"
                    style={{
                      marginTop: 6, padding: '6px 10px', borderRadius: 6, fontSize: 12,
                      background: '#f5f3ff', border: '1px solid #c4b5fd', color: '#4c1d95',
                      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                    }}
                  >
                    <span>
                      <strong>Parked by {formatParkedBy(orderDetail.order)}</strong>
                      {' — '}
                      {PARK_REASON_LABELS[orderDetail.order.parked_reason] ?? orderDetail.order.parked_reason}
                      {orderDetail.order.parked_until ? ` (come back ${formatIsoDate(orderDetail.order.parked_until)})` : ''}
                      {orderDetail.order.parked_note ? `: ${orderDetail.order.parked_note}` : ''}
                    </span>
                    {canManageOrders && (
                      <>
                        <button
                          type="button"
                          data-testid="park-edit-btn"
                          onClick={() => { setParkError(null); setShowParkDialog(true); }}
                          disabled={parkSubmitting}
                          style={{ marginLeft: 'auto', fontSize: 12, background: 'none', border: 'none', color: '#6d28d9', cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          data-testid="unpark-btn"
                          onClick={handleUnpark}
                          disabled={parkSubmitting}
                          style={{ fontSize: 12, background: '#fff', border: '1px solid #c4b5fd', color: '#4c1d95', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}
                        >
                          {parkSubmitting ? 'Working…' : 'Unpark'}
                        </button>
                      </>
                    )}
                    {parkError && !showParkDialog && (
                      <span role="alert" style={{ color: '#b91c1c' }}>{parkError}</span>
                    )}
                  </div>
                )}
                {orderDetail.order.ship_to_address ? (
                  <div className={styles.subtitle} style={{ marginTop: 2, fontSize: 12, color: '#6b7280' }}>
                    <span>Ship to: </span>
                    <span style={{ fontWeight: 500 }}>
                      {(orderDetail.order.ship_to_address as Record<string, string>).ship_to_name || ''}
                    </span>
                    {' '}
                    <span>
                      {[
                        (orderDetail.order.ship_to_address as Record<string, string>).address1,
                        (orderDetail.order.ship_to_address as Record<string, string>).address2,
                        (orderDetail.order.ship_to_address as Record<string, string>).address3,
                        (orderDetail.order.ship_to_address as Record<string, string>).city,
                        (orderDetail.order.ship_to_address as Record<string, string>).state,
                        (orderDetail.order.ship_to_address as Record<string, string>).zip_code,
                      ].filter(Boolean).join(', ')}
                    </span>
                    {canManageOrders && (orderDetail.order.status === 'draft' || orderDetail.order.status === 'submission_failed') && (
                      <button
                        type="button"
                        className={styles.headerEditBtn}
                        onClick={() => setShowShipToSearch(true)}
                        title="Change ship-to"
                        style={{ marginLeft: 4 }}
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 3a2.83 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                        </svg>
                      </button>
                    )}
                    {canManageOrders
                      && !(orderDetail.order.status === 'draft' || orderDetail.order.status === 'submission_failed')
                      && orderDetail.editable_in_sage && (
                      <button
                        type="button"
                        className={styles.viewEmailBtn}
                        onClick={() => { setShowShipToSearch(true); setShowNewShipToForm(true); }}
                        title="Create a new Sage ship-to address for this customer (use ✎ Edit in Sage to put it on this order)"
                        style={{ marginLeft: 6 }}
                        data-testid="confirmed-new-ship-to-btn"
                      >
                        + New Sage ship-to
                      </button>
                    )}
                  </div>
                ) : canManageOrders && (orderDetail.order.status === 'draft' || orderDetail.order.status === 'submission_failed') ? (
                  <div className={styles.subtitle} style={{ marginTop: 2, fontSize: 12 }}>
                    <button
                      type="button"
                      className={styles.viewEmailBtn}
                      onClick={() => setShowShipToSearch(true)}
                    >
                      + Add Ship-To Address
                    </button>
                  </div>
                ) : canManageOrders && orderDetail.editable_in_sage ? (
                  <div className={styles.subtitle} style={{ marginTop: 2, fontSize: 12 }}>
                    <button
                      type="button"
                      className={styles.viewEmailBtn}
                      onClick={() => { setShowShipToSearch(true); setShowNewShipToForm(true); }}
                      data-testid="confirmed-new-ship-to-btn"
                    >
                      + New Sage ship-to
                    </button>
                  </div>
                ) : null}
                {newShipToNote && (
                  <div style={{ marginTop: 2, fontSize: 12, color: '#059669' }} data-testid="new-ship-to-note">
                    ✓ {newShipToNote}
                  </div>
                )}
                {shipToSaveError && (
                  <div
                    role="alert"
                    data-testid="ship-to-save-error"
                    style={{
                      marginTop: 4,
                      padding: '6px 10px',
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: 6,
                      fontSize: 12,
                      color: '#991b1b',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 6,
                    }}
                  >
                    <span aria-hidden="true">&#9888;</span>
                    <span style={{ flex: 1 }}>{shipToSaveError}</span>
                    <button
                      type="button"
                      onClick={() => setShipToSaveError(null)}
                      aria-label="Dismiss"
                      style={{ border: 'none', background: 'none', color: '#991b1b', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}
                    >
                      &times;
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className={styles.title}>Order Detail</div>
            )}
          </div>
          <div className={styles.headerButtons}>
            {navigation && !isEmbedded && (
              <DetailNavButtons navigation={navigation} actionPrefix="order" noun="order" />
            )}
            <button
              className={styles.printBtn}
              onClick={() => void handlePrintOrder()}
              type="button"
              title="Print order"
              disabled={!orderDetail}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
            </button>
            {/* Embedded (split view): the outer Split View bar owns the single
                close control — a second × per pane reads as window-in-window. */}
            {!isEmbedded && (
              <button className={styles.closeBtn} onClick={onClose} type="button" title="Close" data-action="order_close" data-outcome="close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Sibling order switcher (multi-order per email or thread) */}
        {siblingOrders.length > 1 && (
          <div className={styles.siblingBar}>
            <span className={styles.siblingBarLabel}>
              {new Set(siblingOrders.map((s) => s.source_email_id ?? null)).size > 1
                ? 'Orders in this thread:'
                : 'Orders from this email:'}
            </span>
            {siblingOrders.map((sib) => {
              const isActive = sib.id === resolvedOrderId;
              const statusLabel = ORDER_STATUS_LABELS[sib.status as OrderStatus] ?? sib.status;
              return (
                <button
                  key={sib.id}
                  type="button"
                  className={`${styles.siblingTab} ${isActive ? styles.siblingTabActive : ''}`}
                  onClick={() => {
                    if (!isActive) {
                      setResolvedOrderId(sib.id);
                      setOrderDetail(null);
                      setActiveTab('order');
                    }
                  }}
                >
                  <span
                    className={styles.siblingDot}
                    style={{
                      background: sib.status === 'draft' ? '#f59e0b'
                        : sib.status === 'submitted_to_sage' ? '#3b82f6'
                        : sib.status === 'confirmed_in_sage' ? '#10b981'
                        : sib.status === 'shipped' ? '#0d9488'
                        : sib.status === 'cancelled' ? '#ef4444'
                        : '#6b7280',
                    }}
                  />
                  {sib.po_number ? `PO: ${sib.po_number}` : `Order`}
                  <span className={styles.siblingStatusLabel}>{statusLabel}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Tab Bar */}
        <div className={styles.tabBar} data-testid="order-tab-bar" data-section="order_tabs" data-target-order-id={resolvedOrderId ?? undefined}>
          <button
            className={`${styles.tab} ${activeTab === 'order' ? styles.tabActive : ''}`}
            onClick={() => openTab('order')}
            type="button"
            data-action="order_detail_tab"
            value="order"
            data-record-value="tab"
            aria-pressed={activeTab === 'order'}
          >
            Order
            {pricingContext && pricingContext.items_with_special_pricing > 0 && (
              <span className={`${styles.tabBadge} ${styles.tabBadgeGood}`}>
                {pricingContext.items_with_special_pricing}
              </span>
            )}
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'pricing' ? styles.tabActive : ''}`}
            onClick={() => openTab('pricing')}
            type="button"
            data-action="order_detail_tab"
            value="pricing"
            data-record-value="tab"
            aria-pressed={activeTab === 'pricing'}
          >
            Special Pricing
            {pricingContext && (
              <span className={`${styles.tabBadge} ${
                pricingContext.has_any_special_pricing ? styles.tabBadgeGood : styles.tabBadgeWarn
              }`}>
                {pricingContext.agreements.filter((a) => a.is_active).length}
              </span>
            )}
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'quotes' ? styles.tabActive : ''}`}
            onClick={() => openTab('quotes')}
            type="button"
            data-action="order_detail_tab"
            value="quotes"
            data-record-value="tab"
            aria-pressed={activeTab === 'quotes'}
          >
            Open Quotes
            {pricingContext &&
              ((pricingContext.open_quotes?.length || 0) +
                (pricingContext.referenced_quotes?.length || 0)) > 0 && (
              <span className={`${styles.tabBadge} ${styles.tabBadgeGood}`}>
                {(pricingContext.open_quotes?.length || 0) +
                  (pricingContext.referenced_quotes?.length || 0)}
              </span>
            )}
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'documents' ? styles.tabActive : ''}`}
            onClick={() => openTab('documents')}
            type="button"
            data-action="order_detail_tab"
            value="documents"
            data-record-value="tab"
            aria-pressed={activeTab === 'documents'}
          >
            Documents
            {orderDocuments && orderDocuments.length > 0 && (
              <span className={styles.tabBadge}>
                {orderDocuments.length}
              </span>
            )}
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'timeline' ? styles.tabActive : ''}`}
            onClick={() => openTab('timeline')}
            type="button"
            data-action="order_detail_tab"
            value="timeline"
            data-record-value="tab"
            aria-pressed={activeTab === 'timeline'}
          >
            Timeline
            {orderDetail?.history && orderDetail.history.length > 0 && (
              <span className={styles.tabBadge}>
                {orderDetail.history.length}
              </span>
            )}
          </button>
        </div>

        {/* Sage Error Banner */}
        {sageError && (
          <div style={{
            margin: '0 24px',
            padding: '10px 14px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            fontSize: '13px',
            color: '#991b1b',
          }}>
            <span style={{ fontSize: '16px', lineHeight: '1' }}>&#9888;</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: '2px' }}>Sage Submission Failed</div>
              <div style={{ color: '#b91c1c' }}>{sageError}</div>
              {submissionFieldErrors.length > 0 && (
                <ul style={{ margin: '6px 0 0 0', padding: '0 0 0 18px', fontSize: '12px', color: '#b91c1c' }}>
                  {submissionFieldErrors.map((e, i) => (
                    <li key={i}>{e.message}</li>
                  ))}
                </ul>
              )}
            </div>
            <button
              type="button"
              onClick={() => { setSageError(null); setSubmissionFieldErrors([]); }}
              style={{
                marginLeft: 'auto',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#991b1b',
                fontSize: '16px',
                lineHeight: '1',
                padding: '0 2px',
              }}
            >
              &times;
            </button>
          </div>
        )}

        {/* Sage Processing Messages Banner */}
        {orderDetail?.sage_processing_messages && orderDetail.sage_processing_messages.length > 0 && (
          <div style={{
            margin: '0 24px',
            padding: '10px 14px',
            background: orderDetail.sage_processing_messages.some(m => m.severity === 'error') ? '#fef2f2' : '#fffbeb',
            border: `1px solid ${orderDetail.sage_processing_messages.some(m => m.severity === 'error') ? '#fecaca' : '#fde68a'}`,
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            fontSize: '13px',
            color: orderDetail.sage_processing_messages.some(m => m.severity === 'error') ? '#991b1b' : '#92400e',
          }}>
            <span style={{ fontSize: '16px', lineHeight: '1' }}>&#9888;</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: '2px' }}>
                Sage Processing {orderDetail.sage_processing_messages.some(m => m.severity === 'error') ? 'Errors' : 'Warnings'}
              </div>
              {orderDetail.sage_processing_messages.length === 1 ? (
                <div>{orderDetail.sage_processing_messages[0].message}</div>
              ) : (
                <ul style={{ margin: '4px 0 0 0', padding: '0 0 0 18px', fontSize: '12px' }}>
                  {orderDetail.sage_processing_messages.map((msg, i) => (
                    <li key={i}>{msg.message}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Body */}
        <div className={styles.body} data-testid="order-detail-body" data-section="order_body" data-target-order-id={resolvedOrderId ?? undefined}>
          {loading ? (
            <div className={styles.loadingState}>Loading order details...</div>
          ) : error ? (
            <div className={styles.errorState}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5">
                <path d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className={styles.errorTitle}>Order Not Available</div>
              <div className={styles.errorText}>{error}</div>
            </div>
          ) : !orderDetail ? (
            <div className={styles.loadingState}>Order not found</div>
          ) : activeTab === 'order' ? (
            <OrderTab
              canEdit={canManageOrders}
              orderDetail={orderDetail}
              onCreateProposedCustomer={() => setShowProposedCustomerModal(true)}
              onCreateProposedShipTo={handleCreateProposedShipTo}
              pricingContext={pricingContext}
              onRefreshPricingContext={() => { if (resolvedOrderId) refreshPricingContext(resolvedOrderId); }}
              pricingMap={pricingMap}
              multiPricingWarnings={multiPricingWarnings}
              warehouseOptions={warehouseOptions}
              shippingCarrierOptions={shippingCarrierOptions}
              salespersonOptions={salespersonOptions}
              cancelReasonCodes={cancelReasonCodes}
              formatPrice={formatPrice}
              formatDate={formatDateLocal}
              getRecommendedPrice={getRecommendedPrice}
              getPricingStatus={getPricingStatus}
              getEffectiveSpecialPrice={getEffectiveSpecialPrice}
              renderTierBadge={renderTierBadge}
              onLineItemAdded={handleLineItemAdded}
              onLineItemUpdated={handleLineItemUpdated}
              onLineItemRemoved={handleLineItemRemoved}
              onOrderUpdated={handleOrderUpdated}
              onTruckloadFreightChanged={handleTruckloadFreightChanged}
              onSurchargeChanged={handleSurchargeChanged}
              onProposalResolved={() => { void fetchData(orderDetail.order.id); }}
              onRecommendationDecided={() => { void fetchData(orderDetail.order.id); }}
              weightSummary={weightSummary}
              duplicatePoOrders={duplicatePoOrders}
              onViewDuplicateOrder={handleViewDuplicateOrder}
              lineComments={lineComments}
              orderInstructions={orderInstructions}
              onInstructionsChanged={setOrderInstructions}
              freightCarrierError={freightCarrierError}
              backorderPrompts={backorderPrompts}
              acceptedBackorderIds={acceptedBackorderIds}
              backorderPromptBusy={backorderPromptBusy}
              onQuantityReduced={handleQuantityReduced}
              onAcceptBackorderPrompt={handleAcceptBackorderPrompt}
              onDeclineBackorderPrompt={handleDeclineBackorderPrompt}
              lineAdvisories={lineAdvisories}
              onDismissLineAdvisory={dismissLineAdvisory}
            />
          ) : activeTab === 'pricing' ? (
            <PricingTab
              pricingContext={pricingContext}
              filteredAgreements={filteredAgreements}
              showActiveOnly={showActiveOnly}
              setShowActiveOnly={setShowActiveOnly}
              expandedAgreements={expandedAgreements}
              toggleAgreement={toggleAgreement}
              orderItemCodes={orderItemCodes}
              formatPrice={formatPrice}
              formatDate={formatDateLocal}
            />
          ) : activeTab === 'quotes' ? (
            <QuotesTab
              pricingContext={pricingContext}
              formatPrice={formatPrice}
              formatDate={formatDateLocal}
              isDraft={canManageOrders && (orderDetail?.order?.status === 'draft' || orderDetail?.order?.status === 'submission_failed')}
              onAttachQuote={handleAttachQuote}
              attachedQuoteRefs={attachedQuoteRefs}
              quoteSlotsFull={quoteSlotsFull}
              attachingQuoteId={attachingQuoteId}
              onDetachQuote={handleDetachQuote}
              detachingQuoteRef={detachingQuoteRef}
              attachNotice={quoteAttachNotice}
              onDismissAttachNotice={dismissQuoteAttachNotice}
              orderLineItems={orderDetail?.line_items || []}
              onApplyQuotePrice={async (itemCode: string, price: number) => {
                if (!orderDetail) return;
                const matchingLineItem = orderDetail.line_items.find(
                  (li) => li.item_code === itemCode,
                );
                if (!matchingLineItem) return;
                const quotePrice = String(price);
                if (matchingLineItem.unit_price === quotePrice) return;
                trackEvent('action', 'quote_price_apply', {
                  order_id: orderDetail.order.id,
                  item_code: itemCode,
                  price,
                });
                try {
                  const resp = await portalOrderService.updateLineItem(
                    orderDetail.order.id,
                    matchingLineItem.id,
                    { unit_price: quotePrice },
                  );
                  handleLineItemUpdated(resp);
                } catch {
                  // Best-effort
                }
              }}
            />
          ) : activeTab === 'documents' && resolvedOrderId ? (
            <DocumentsPanel
              scope={{ kind: 'order', orderId: resolvedOrderId }}
              documents={orderDocuments}
              loading={docsLoading}
              onRefetch={refetchDocuments}
            />
          ) : activeTab === 'timeline' && orderDetail ? (
            <>
              {/* 8-step "order story" strip: Email → … → Shipped + Invoiced.
                  Lives on the Timeline tab (moved out of the Order tab in the
                  2026-07 review-noise pass) — it summarizes the same lifecycle
                  the event list below details. */}
              <OrderStoryStrip
                order={orderDetail.order}
                hasInvoice={orderDetail.invoice != null}
                formatDate={formatDateLocal}
              />
              <OrderTimelineTab orderId={orderDetail.order.id} />
            </>
          ) : null}
        </div>

        {/* Footer Actions — orders:manage only; viewers get no action strip */}
        {orderDetail && canManageOrders && (
          <div className={styles.footer} data-testid="order-detail-footer" data-section="order_footer" data-target-order-id={resolvedOrderId ?? undefined}>
            <div className={styles.footerLeft}>
              {orderDetail.allowed_transitions.includes('submit') && (
                <button
                  className={`${styles.actionBtn} ${styles.actionSubmit}`}
                  onClick={handleSubmit}
                  disabled={actionLoading || submitGateNote !== null}
                  type="button"
                  data-action="order_submit_to_sage"
                  data-outcome="request"
                  title={submitGateNote ?? undefined}
                >
                  {orderDetail.order.status === 'submission_failed' ? 'Retry Submit to Sage' : 'Submit to Sage'}
                </button>
              )}
              {orderDetail.allowed_transitions.includes('submit') && submitGateNote !== null && (
                <span
                  data-testid="submit-review-gate-note"
                  style={{ fontSize: '12px', color: '#92400e', alignSelf: 'center' }}
                >
                  {submitGateNote}
                </span>
              )}
              {/* Leftover Merlin PO notes: one quiet line instead of a popup
                  (kanban 88963483). Review jumps to the Order Instructions
                  section; Submit stamps them as left off, no prompt. */}
              {orderDetail.allowed_transitions.includes('submit') && suggestedSubmitRows.length > 0 && (
                <span
                  data-testid="instruction-submit-note"
                  style={{ fontSize: '12px', color: '#92400e', alignSelf: 'center' }}
                >
                  Merlin found {suggestedSubmitRows.length} note
                  {suggestedSubmitRows.length === 1 ? '' : 's'} on the PO not on this order ·{' '}
                  <button
                    type="button"
                    onClick={scrollToInstructions}
                    data-testid="instruction-submit-note-review"
                    data-action="order_instruction_note_review"
                    data-target-order-id={resolvedOrderId ?? undefined}
                    data-suggested-count={suggestedSubmitRows.length}
                    data-outcome="none"
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      font: 'inherit',
                      fontWeight: 600,
                      color: '#92400e',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    Review
                  </button>
                </span>
              )}
              {instructionStampError && (
                <span
                  data-testid="instruction-stamp-error"
                  style={{ fontSize: '12px', color: '#b91c1c', alignSelf: 'center' }}
                >
                  {instructionStampError}
                </span>
              )}
              {orderDetail.editable_in_sage && (
                <button
                  className={styles.actionBtn}
                  style={{ background: '#2563eb', color: '#fff', border: '1px solid #2563eb', fontWeight: 600 }}
                  data-action="order_sage_edit_open"
                  data-outcome="open"
                  onClick={() => setShowSageEdit(true)}
                  disabled={actionLoading}
                  type="button"
                  title="Edit this order and push the change to Sage"
                >
                  ✎ Edit in Sage
                </button>
              )}
              {orderDetail.allowed_transitions.includes('confirm_sage') && (
                <button
                  className={`${styles.actionBtn} ${styles.actionConfirmSage}`}
                  data-testid="confirm-sage-btn"
                  data-action="order_confirm_in_sage_open"
                  data-outcome="open"
                  onClick={() => { setTransitionError(null); setShowConfirmSage(true); }}
                  disabled={actionLoading}
                  type="button"
                  title={
                    orderDetail.open_in_sage
                      ? `Mark this order confirmed under Sage order ${orderDetail.order.sage_order_no}`
                      : 'This order is not in Sage yet — you will be asked for the Sage order number'
                  }
                >
                  Confirm in Sage
                </button>
              )}
              {orderDetail.allowed_transitions.includes('unconfirm_sage') && (
                <button
                  className={styles.actionBtn}
                  data-testid="undo-confirm-sage-btn"
                  style={{ background: '#fffbeb', color: '#92400e', border: '1px solid #f59e0b', fontWeight: 600 }}
                  data-action="order_undo_confirm_in_sage_open"
                  data-outcome="open"
                  onClick={() => { setTransitionError(null); setShowUndoConfirmSage(true); }}
                  disabled={actionLoading}
                  type="button"
                  title="This order was confirmed by hand but is not in Sage — move it back to Submission Failed so it can be edited and resubmitted"
                >
                  Undo confirm
                </button>
              )}
              {orderDetail.allowed_transitions.includes('accept') && (
                <button
                  className={`${styles.actionBtn} ${styles.actionAccept}`}
                  onClick={() => handleTransition('accept')}
                  disabled={actionLoading}
                  type="button"
                  data-action="order_mark_accepted"
                  data-outcome="request"
                >
                  Mark Accepted
                </button>
              )}
              {orderDetail.allowed_transitions.includes('complete') && (
                <button
                  className={`${styles.actionBtn} ${styles.actionComplete}`}
                  onClick={() => handleTransition('complete')}
                  disabled={actionLoading}
                  type="button"
                  data-action="order_mark_completed"
                  data-outcome="request"
                >
                  Mark Completed
                </button>
              )}
            </div>
            <div className={styles.footerRight}>
              {(orderDetail.order.status === 'draft' || orderDetail.order.status === 'submission_failed')
                && !orderDetail.order.parked_reason && (
                <button
                  className={`${styles.actionBtn} ${styles.actionCancel}`}
                  data-testid="park-draft-btn"
                  data-action="order_park_open"
                  data-outcome="open"
                  onClick={() => { setParkError(null); setShowParkDialog(true); }}
                  disabled={actionLoading || parkSubmitting}
                  type="button"
                  title="Leave this draft un-keyed on purpose and say why; the morning report and alerts will know"
                >
                  Park draft…
                </button>
              )}
              {(orderDetail.order.status === 'draft' || orderDetail.order.status === 'submission_failed') && (
                <button
                  className={`${styles.actionBtn} ${styles.actionDismiss}`}
                  onClick={() => {
                    setDismissError(null);
                    setShowDismissConfirm(true);
                  }}
                  disabled={actionLoading}
                  type="button"
                  data-action="order_dismiss_open"
                  data-outcome="open"
                >
                  Dismiss Draft
                </button>
              )}
              {/* "Cancel Order" was removed here: it only flipped OUR order
                  record to CANCELLED (silently 400ing without a reason) and
                  never touched the Sage SO, so it misled CSRs into thinking
                  the order was cancelled upstream. Real Sage cancellation is
                  tracked as a backlog feature. */}
            </div>
          </div>
        )}
      </div>
  );

  const extras = (
    <>
      {/* Customer Search Dropdown */}
      {showCustomerSearch && (
        <div className={styles.confirmOverlay} onClick={() => { setShowCustomerSearch(false); setCustomerSearchQuery(''); setCustomerSearchResults([]); }}>
          <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()} style={{ width: 460 }}>
            <div className={styles.confirmTitle}>Change Customer</div>
            <input
              type="text"
              className={styles.inlineSearchInput}
              placeholder="Search by name or number..."
              value={customerSearchQuery}
              onChange={(e) => handleCustomerSearchInput(e.target.value)}
              autoFocus
            />
            {customerSearchLoading && (
              <div className={styles.inlineSearchLoading}>Searching...</div>
            )}
            {!customerSearchLoading && customerSearchQuery.length >= 2 && customerSearchResults.length === 0 && (
              <div className={styles.inlineSearchNoResults}>No customers found</div>
            )}
            {customerSearchResults.length > 0 && (
              <div className={styles.inlineSearchResults} style={{ marginTop: 8 }}>
                {customerSearchResults.map((c) => (
                  <button
                    key={c.sage_customer_no}
                    type="button"
                    className={styles.inlineSearchItem}
                    onClick={() => handleCustomerChange(c.sage_customer_no, c.customer_name ?? '')}
                  >
                    <span className={styles.inlineSearchItemCode}>{c.sage_customer_no}</span>
                    <span className={styles.inlineSearchItemDesc}>{c.customer_name ?? ''}</span>
                    {c.salesperson_name && (
                      <span className={styles.inlineSearchItemPrice}>{c.salesperson_name}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
            <div className={styles.confirmActions} style={{ marginTop: 12 }}>
              <button
                className={`${styles.actionBtn} ${styles.actionCancel}`}
                onClick={() => { setShowCustomerSearch(false); setCustomerSearchQuery(''); setCustomerSearchResults([]); }}
                type="button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* "Is this a different order?" — customer left the extraction's identity; rendered AFTER the picker so it stacks on top */}
      {customerIdentityWarning && (
        <HeaderIdentityWarningModal
          warning={customerIdentityWarning.check}
          busy={actionLoading}
          onCancel={() => setCustomerIdentityWarning(null)}
          onSameOrder={() => {
            const { customerNo, customerName } = customerIdentityWarning.pending;
            void applyCustomerChange(customerNo, customerName, { differentOrder: false });
          }}
          onDifferentOrder={(note) => {
            const { customerNo, customerName } = customerIdentityWarning.pending;
            void applyCustomerChange(customerNo, customerName, { differentOrder: true, note });
          }}
        />
      )}

      {/* Ship-To picker / new-address form. Docked bottom-right with no
          backdrop (kanban 2c097936) so the order and the email beside it stay
          readable while the address is typed; the overlay passes pointer
          events through, so there is no click-off cancel. Escape and Cancel
          close it. */}
      {showShipToSearch && orderDetail && (
        <div className={styles.shipToOverlay} data-testid="ship-to-overlay">
          <div
            className={styles.shipToPanel}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="false"
            aria-label="Ship-to address"
            data-testid="ship-to-panel"
          >
            <div className={styles.confirmTitle}>
              {!(orderDetail.order.status === 'draft' || orderDetail.order.status === 'submission_failed')
                ? 'New Sage Ship-To Address'
                : orderDetail.order.ship_to_address ? 'Change Ship-To Address' : 'Add Ship-To Address'}
            </div>
            <div className={styles.shipToHint}>
              The order and email stay visible behind this panel. Esc or Cancel closes it.
            </div>
            {showNewShipToForm && resolvedOrderId ? (
              <NewSageShipToForm
                orderId={resolvedOrderId}
                customerNo={orderDetail.order.customer_no}
                initial={newShipToPrefill ?? undefined}
                cancelLabel={
                  (orderDetail.order.status === 'draft' || orderDetail.order.status === 'submission_failed')
                    ? undefined
                    : 'Cancel'
                }
                onCreated={(addr) => { setNewShipToPrefill(null); void handleNewShipToCreated(addr); }}
                onCancel={() => {
                  setNewShipToPrefill(null);
                  setShowNewShipToForm(false);
                  // Sage-landed orders have no picker behind the form (their
                  // ship-to changes go through ✎ Edit in Sage) — close fully.
                  if (!(orderDetail.order.status === 'draft' || orderDetail.order.status === 'submission_failed')) {
                    setShowShipToSearch(false);
                  }
                }}
              />
            ) : (
              <>
                <ShipToSearch
                  customerNo={orderDetail.order.customer_no}
                  customerName={orderDetail.order.customer_name}
                  onSelect={handleShipToSelect}
                  onCustomerMismatch={handleShipToCustomerMismatch}
                  onCancel={() => setShowShipToSearch(false)}
                  onCreateNew={() => setShowNewShipToForm(true)}
                  closeOnClickOutside={false}
                  dropdownPlacement="inline"
                />
              </>
            )}
          </div>
        </div>
      )}

      {/* Merlin propose-new customer: the EXISTING Create-in-Sage modal,
          prefilled from the proposal block. onCreated binds the fresh
          account onto this draft via the normal customer-change path. */}
      {showProposedCustomerModal && proposedNewCustomer && (
        <AddCustomerModal
          initialName={proposedNewCustomer.name}
          initialEmail={proposedNewCustomer.email ?? undefined}
          initialAddress1={proposedNewCustomer.address1 ?? undefined}
          initialAddress2={proposedNewCustomer.address2 ?? undefined}
          initialCity={proposedNewCustomer.city ?? undefined}
          initialState={proposedNewCustomer.state ?? undefined}
          initialZip={proposedNewCustomer.zip ?? undefined}
          initialPhone={proposedNewCustomer.phone ?? undefined}
          onCreated={(customerNo, customerName) => {
            void handleProposedCustomerCreated(customerNo, customerName);
          }}
          onCancel={() => setShowProposedCustomerModal(false)}
        />
      )}

      {/* Price Mismatch Confirmation (Submit to Sage) */}
      {priceMismatches && priceMismatches.length > 0 && (
        <PriceMismatchConfirmModal
          mismatches={priceMismatches}
          orderId={resolvedOrderId}
          onCancel={() => setPriceMismatches(null)}
          onConfirm={() => void handleConfirmSubmitDespiteMismatch()}
          submitting={actionLoading}
          formatPrice={formatPrice}
        />
      )}

      {/* Freight carrier blank — soft "submit anyway?" gate (Submit to Sage) */}
      {showSageEdit && orderDetail && (
        <SageEditModal
          orderDetail={orderDetail}
          onClose={() => setShowSageEdit(false)}
          onSaved={() => { void fetchData(orderDetail.order.id); }}
        />
      )}
      {showFreightConfirm && (
        <div
          className={styles.confirmOverlay}
          onClick={() => setShowFreightConfirm(false)}
          data-testid="freight-carrier-confirm"
        >
          <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmTitle}>No freight carrier selected</div>
            <p className={styles.confirmText}>
              This order has no Ship Via carrier selected. Pick a carrier first if the
              freight method is known, or submit anyway if freight is handled separately
              (e.g. a truckload quote ops attaches later).
            </p>
            <div className={styles.confirmActions}>
              <button
                className={`${styles.actionBtn} ${styles.actionCancel}`}
                onClick={() => setShowFreightConfirm(false)}
                type="button"
              >
                Cancel
              </button>
              <button
                className={`${styles.actionBtn} ${styles.actionDismiss}`}
                data-action="order_submit_without_carrier_confirm"
                data-target-order-id={resolvedOrderId ?? undefined}
                data-outcome="request"
                onClick={handleConfirmSubmitDespiteNoCarrier}
                disabled={actionLoading}
                type="button"
                data-testid="freight-carrier-confirm-submit"
              >
                {actionLoading ? 'Submitting...' : 'Submit anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credit Hold Confirmation (Submit to Sage) */}
      {showCreditHoldConfirm && (
        <div
          className={styles.confirmOverlay}
          onClick={() => setShowCreditHoldConfirm(false)}
          data-testid="credit-hold-confirm"
        >
          <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmTitle}>Customer is on Credit Hold</div>
            <p className={styles.confirmText}>
              {pricingContext?.customer_name} ({pricingContext?.customer_no}) is flagged
              credit_hold='Y' in Sage. The order will still be submitted, but Sage will
              likely block it from shipping until accounting clears the hold. Confirm
              with accounting before proceeding.
            </p>
            <div className={styles.confirmActions}>
              <button
                className={`${styles.actionBtn} ${styles.actionCancel}`}
                onClick={() => setShowCreditHoldConfirm(false)}
                type="button"
              >
                Cancel
              </button>
              <button
                className={`${styles.actionBtn} ${styles.actionDismiss}`}
                data-action="order_submit_despite_credit_hold"
                data-target-order-id={resolvedOrderId ?? undefined}
                data-outcome="request"
                onClick={handleConfirmSubmitDespiteHold}
                disabled={actionLoading}
                type="button"
                data-testid="credit-hold-confirm-submit"
              >
                {actionLoading ? 'Submitting...' : 'Submit anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Soft, overridable submission warnings — "submit anyway?" gate.
          Covers ship-to "address doesn't match" AND lines still flagged
          needs_review; lists every warning so the rep sees each one. */}
      {submissionWarnings !== null && (
        <div
          className={styles.confirmOverlay}
          onClick={() => setSubmissionWarnings(null)}
          data-testid="submission-warning-confirm"
        >
          <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmTitle}>
              {submissionWarnings.length > 1
                ? 'Please confirm before submitting'
                : 'Please confirm before submitting to Sage'}
            </div>
            {submissionWarnings.length > 0 ? (
              <ul className={styles.confirmText} data-testid="submission-warning-list">
                {submissionWarnings.map((w, i) => (
                  <li key={`${w.field}-${w.line_item_position ?? i}`}>
                    {renderInlineBold(w.message)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className={styles.confirmText}>
                This order has unresolved warnings.
              </p>
            )}
            <p className={styles.confirmText}>
              You can submit anyway and fix it in Sage, or go back and resolve
              these first.
            </p>
            <div className={styles.confirmActions}>
              <button
                className={`${styles.actionBtn} ${styles.actionCancel}`}
                onClick={() => setSubmissionWarnings(null)}
                type="button"
              >
                Go back &amp; edit
              </button>
              <button
                className={`${styles.actionBtn} ${styles.actionDismiss}`}
                data-action="order_submit_despite_warnings"
                data-target-order-id={resolvedOrderId ?? undefined}
                data-outcome="request"
                onClick={handleConfirmSubmitDespiteWarnings}
                disabled={actionLoading}
                type="button"
                data-testid="submission-warning-confirm-submit"
              >
                {actionLoading ? 'Submitting...' : 'Submit anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Park draft (reason + note) */}
      {showParkDialog && orderDetail && (
        <ParkDraftDialog
          subject={`${orderDetail.order.customer_name}${orderDetail.order.po_number ? `, PO ${orderDetail.order.po_number}` : ''}`}
          initialReason={orderDetail.order.parked_reason ?? null}
          initialNote={orderDetail.order.parked_note ?? ''}
          initialUntil={orderDetail.order.parked_until ?? null}
          submitting={parkSubmitting}
          error={parkError}
          onCancel={() => setShowParkDialog(false)}
          onSubmit={handlePark}
        />
      )}

      {showConfirmSage && orderDetail && (
        <ConfirmSageDialog
          sageOrderNo={orderDetail.order.sage_order_no ?? null}
          openInSage={orderDetail.open_in_sage === true}
          submitting={actionLoading}
          error={transitionError}
          onCancel={() => setShowConfirmSage(false)}
          onConfirm={(no) => { void handleConfirmSage(no); }}
        />
      )}

      {showUndoConfirmSage && orderDetail && (
        <UndoConfirmSageDialog
          submitting={actionLoading}
          error={transitionError}
          onCancel={() => setShowUndoConfirmSage(false)}
          onConfirm={() => { void handleUndoConfirmSage(); }}
        />
      )}

      {/* Dismiss Draft Confirmation */}
      {showDismissConfirm && (
        <div className={styles.confirmOverlay} onClick={() => setShowDismissConfirm(false)}>
          <div
            className={styles.confirmModal}
            data-testid="order-confirm-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.confirmTitle}>Dismiss Draft</div>
            <p className={styles.confirmText}>
              This will permanently delete this draft order and all its line items.
              This cannot be undone.
            </p>
            {dismissError && (
              <p
                role="alert"
                data-testid="order-dismiss-error"
                style={{ color: '#b91c1c', fontSize: 13, marginTop: 0 }}
              >
                {dismissError}
              </p>
            )}
            <div className={styles.confirmActions}>
              <button
                className={`${styles.actionBtn} ${styles.actionCancel}`}
                onClick={() => setShowDismissConfirm(false)}
                type="button"
                data-action="order_dismiss_cancel"
                data-target-order-id={resolvedOrderId ?? undefined}
                data-outcome="close"
              >
                Back
              </button>
              <button
                className={`${styles.actionBtn} ${styles.actionDismiss}`}
                onClick={handleDismissDraft}
                disabled={actionLoading}
                type="button"
                data-action="order_dismiss_confirm"
                data-target-order-id={resolvedOrderId ?? undefined}
                data-outcome="request"
              >
                {actionLoading ? 'Dismissing...' : 'Dismiss Draft'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  const printDocument = orderPrintJob && (
    <OrderPrintDocument job={orderPrintJob} onReady={handleOrderPrintReady} />
  );

  if (isEmbedded) {
    return (
      <>
        {panel}
        {extras}
        {printDocument}
      </>
    );
  }

  // Modal mode: the order panel sits in a workspace beside the source-email
  // pane. `.workspace` is display:contents (no box of its own), so an order
  // without an email looks exactly as before, and toggling the pane never
  // moves the panel in the tree (no remount, no lost inline edits).
  // The backdrop does not close the order (kanban 2c097936): a click that
  // lands outside the panel while reading or editing must never dismiss it.
  // The header's close button and Escape remain the ways out.
  return (
    <div className={styles.overlay}>
      <div
        className={emailPaneActive ? styles.workspaceSplit : styles.workspace}
        data-testid="order-workspace"
      >
        {emailPaneActive && sourceEmailId && (
          <div className={styles.emailPane} data-testid="order-email-pane">
            {emailPaneLoading && !emailPaneData ? (
              <div className={styles.emailPaneState}>Loading email...</div>
            ) : emailPaneError ? (
              <div className={styles.emailPaneState}>{emailPaneError}</div>
            ) : emailPaneData ? (
              <Suspense fallback={<div className={styles.emailPaneState}>Loading...</div>}>
                <EmailViewModal
                  email={emailPaneData}
                  emailId={sourceEmailId}
                  onClose={onClose}
                  onOrderAction={onOrderAction}
                  onOrderCreated={handleOrderCreatedFromEmailPane}
                  onReplyClick={(action) => setPaneComposeAction(action)}
                  onMarkRead={handlePaneMarkRead}
                  onMarkUnread={handlePaneMarkUnread}
                  isUnread={!!emailPaneData.isUnread}
                  renderMode="embedded"
                />
              </Suspense>
            ) : (
              <div className={styles.emailPaneState}>Email not found</div>
            )}
          </div>
        )}
        {panel}
      </div>
      {extras}
      {printDocument}
      {paneComposeAction && sourceEmailId && (
        <Suspense fallback={null}>
          <EmailComposeModal
            emailId={sourceEmailId}
            action={paneComposeAction}
            onClose={() => setPaneComposeAction(null)}
            onSent={() => {
              setPaneComposeAction(null);
              // Replying is the other thing (besides Mark as read) that clears unread.
              handlePaneMarkRead(sourceEmailId);
            }}
          />
        </Suspense>
      )}
    </div>
  );
};

// ─── Order Tab (sub-component) ───────────────────────────────────────────

interface OrderTabProps {
  /** orders:manage — false renders the tab fully read-only. */
  canEdit: boolean;
  orderDetail: PortalOrderDetailResponse;
  /** Merlin propose-new: open the Create-in-Sage customer modal prefilled. */
  onCreateProposedCustomer?: () => void;
  /** Merlin propose-new: open the new-ship-to form prefilled. */
  onCreateProposedShipTo?: () => void;
  pricingContext: OrderPricingContextResponse | null;
  /** Re-pull the pricing context (the expired-quote banner's
   *  "Check for reply" button — the alert status rides that payload). */
  onRefreshPricingContext?: () => void;
  pricingMap: Map<string, LineItemPricingContext>;
  multiPricingWarnings: string[];
  warehouseOptions: WarehouseOption[];
  shippingCarrierOptions: ShippingCarrierOption[];
  salespersonOptions: SalespersonOption[];
  cancelReasonCodes: CancelReasonCodeOption[];
  formatPrice: (val: string | number | null | undefined) => string;
  formatDate: (dateStr: string | null | undefined) => string;
  getRecommendedPrice: (pItem: LineItemPricingContext) => { price: string | null; source: string };
  getPricingStatus: (lineItem: OrderLineItem, pItem: LineItemPricingContext | undefined) => 'match' | 'mismatch' | 'no-pricing';
  getEffectiveSpecialPrice: (pItem: LineItemPricingContext) => { price: number | null; isTiered: boolean; tier: 1 | 2 | 3 | null };
  renderTierBadge: (pItem: LineItemPricingContext, activeTier: 1 | 2 | 3) => React.ReactNode;
  /** Optimistic callbacks for line item mutations (include order totals) */
  onLineItemAdded?: (resp: LineItemMutationResponse) => void;
  onLineItemUpdated?: (resp: LineItemMutationResponse) => void;
  onLineItemRemoved?: (itemId: string, totals: LineItemDeleteResponse) => void;
  /** Callback when order-level fields are updated (e.g. jobsite, warehouse) */
  onOrderUpdated?: (updatedOrder: OrderRecord) => void;
  /** Fired after the truckload freight amount is set/edited — the parent
   *  merges the fresh summary into orderDetail so the row re-renders. */
  onTruckloadFreightChanged?: (summary: TruckloadFreightSummary | null) => void;
  /** Fired after the surcharge control changed the /SURCHG line — the parent
   *  replaces line_items + totals from the response. */
  onSurchargeChanged?: (resp: SurchargeSetResponse) => void;
  /** Fired after a Merlin edit proposal is confirmed/dismissed — the parent
   *  refetches so the card leaves and the pending-writes banner appears. */
  onProposalResolved?: () => void;
  /** Fired after the CSR accepted / edited / ignored a Merlin recommendation
   *  on the Freight row — the parent refetches (lines, totals, pending
   *  writes and the recommendation's new state all come from the server). */
  onRecommendationDecided?: () => void;
  /** Authoritative weight envelope from Sage's freight API. ``null`` while
   *  the first fetch is in flight; the renderer falls back to the local
   *  qty × unit-weight sum in that case. */
  weightSummary: WeightSummaryResponse | null;
  /** Other orders sharing this (customer, PO) pair — empty when none. */
  duplicatePoOrders: DuplicatePoOrderSummary[];
  /** Switch the modal to view another order (used from the duplicate banner). */
  onViewDuplicateOrder: (orderId: string) => void;
  /** Per-line Sage comments envelope. ``null`` while the first fetch is in
   *  flight; the render walks ``bundles`` and matches by item_code (then by
   *  position) to attach the read-only annotation block under each line. */
  lineComments: OrderLineComments | null;
  /** Order-level instructions (Merlin-extracted + CSR-added) — each becomes
   *  a standalone /C comment row on the Sage order. */
  orderInstructions: OrderInstruction[];
  /** Replace the instruction list after a mutation (responses return the
   *  refreshed list). */
  onInstructionsChanged: (instructions: OrderInstruction[]) => void;
  /** True when the rep tried to submit with no carrier selected — highlights
   *  the Ship Via field red. The overridable "submit anyway?" confirm lives on
   *  the parent component. */
  freightCarrierError?: boolean;
  /** Still-offered (or just-accepted) backorder prompts for this order. */
  backorderPrompts: BackorderPrompt[];
  /** prompt id → created draft id, set after an in-session acceptance. */
  acceptedBackorderIds: Map<string, string>;
  /** Prompt id currently awaiting an accept/decline API response. */
  backorderPromptBusy: string | null;
  /** Fired after a quantity REDUCTION on the inline qty cell. */
  onQuantityReduced?: (orderId: string, lineItemId: string, prevQty: number, newQty: number) => void;
  onAcceptBackorderPrompt?: (orderId: string, promptId: string, quantity?: string) => void;
  onDeclineBackorderPrompt?: (orderId: string, promptId: string) => void;
  /** Line-scoped soft advisories from the last add/update of each line
   *  (never-bought SKU while the account buys a variant sibling), keyed by
   *  line item id. Rendered as an amber notice under the item cell. */
  lineAdvisories?: Map<string, SubmissionFieldError[]>;
  onDismissLineAdvisory?: (itemId: string) => void;
}

const OrderTab: React.FC<OrderTabProps> = ({
  canEdit,
  orderDetail,
  onCreateProposedCustomer,
  onCreateProposedShipTo,
  pricingContext,
  onRefreshPricingContext,
  pricingMap,
  multiPricingWarnings,
  warehouseOptions,
  shippingCarrierOptions,
  salespersonOptions,
  cancelReasonCodes,
  formatPrice,
  formatDate,
  getRecommendedPrice,
  getPricingStatus,
  getEffectiveSpecialPrice,
  renderTierBadge,
  onLineItemAdded,
  onLineItemUpdated,
  onLineItemRemoved,
  onOrderUpdated,
  onTruckloadFreightChanged,
  onSurchargeChanged,
  onProposalResolved,
  onRecommendationDecided,
  weightSummary,
  duplicatePoOrders,
  onViewDuplicateOrder,
  lineComments,
  orderInstructions,
  onInstructionsChanged,
  freightCarrierError,
  backorderPrompts,
  acceptedBackorderIds,
  backorderPromptBusy,
  onQuantityReduced,
  onAcceptBackorderPrompt,
  onDeclineBackorderPrompt,
  lineAdvisories,
  onDismissLineAdvisory,
}) => {
  const navigate = useNavigate();
  const order = orderDetail.order;
  // Every inline edit affordance in this tab keys off isDraft; orders:read
  // viewers (canEdit=false) get the read-only rendering regardless of status.
  const isDraft = canEdit && (order.status === 'draft' || order.status === 'submission_failed');
  // Merlin's current freight concession recommendation on this order
  // (kanban 8d83c042). Null for most orders; the Freight row renders it as
  // the inline chip (proposed), the "from Merlin" mark (accepted / edited)
  // or the faint dot (ignored).
  const freightRecommendation =
    orderDetail.recommendations?.find((r) => r.exception_class === 'freightdis') ?? null;

  // Highlight the Ship Via field when a submit tripped the blank-carrier gate.
  // No FRT carve-out — every blank carrier highlights, and the rep clears it by
  // picking a carrier or overriding via "submit anyway".
  const showFreightFieldError = !!freightCarrierError && !order.ship_via;

  // Map each order line to its matching Sage line-comment bundle. Match by
  // item_code first; when an order has multiple lines for the same SKU, fall
  // back to position-based matching against the already-consumed bundles.
  // Shared with SageEditModal so both surfaces agree on which Sage comment
  // belongs to which line (item_code first, positional fallback).
  const lineCommentBundleByLineId = useMemo(
    () => matchLineCommentBundles(orderDetail.line_items, lineComments?.bundles),
    [lineComments, orderDetail.line_items],
  );

  // Freight quote modal state (local to OrderTab)
  const [showFreightModal, setShowFreightModal] = useState(false);

  type SageHeaderField = 'udf_jobsite' | 'order_type' | 'warehouse_code' | 'po_number' | 'ship_via' | 'udf_bl' | 'requested_ship_date' | 'requested_ship_date_confirmed' | 'cancel_reason_code';

  const HEADER_FIELD_LABELS: Record<SageHeaderField, string> = {
    udf_jobsite: 'The jobsite flag',
    order_type: 'The order type',
    warehouse_code: 'The warehouse',
    po_number: 'The PO number',
    ship_via: 'The ship-via carrier',
    udf_bl: 'The bill-of-lading type',
    requested_ship_date: 'The requested ship date',
    requested_ship_date_confirmed: 'The ship-date confirmation',
    cancel_reason_code: 'The cancel reason',
  };

  // A failed header PATCH used to reach console.error only, so the field kept
  // showing the typed value while nothing was stored (kanban 3677e207: a CSR
  // lost a whole draft's PO + ship-to to swallowed HTTP 503s). Every header
  // save now reports failure here, and callers revert what they show.
  const [headerSaveError, setHeaderSaveError] = useState<string | null>(null);

  /** PATCH one header field. Resolves true when the server accepted it. */
  const handleSageFieldChange = async (field: SageHeaderField, value: string | boolean | null): Promise<boolean> => {
    // Field name only — never the typed value (cards 964045bb / b91c17bf).
    trackEvent('action', 'order_field_save', { order_id: order.id, field, outcome: 'request' });
    try {
      const updated = await portalOrderService.updateOrder(order.id, { [field]: value });
      onOrderUpdated?.(updated);
      setHeaderSaveError(null);
      return true;
    } catch (err) {
      console.error(`Failed to update ${field}:`, err);
      setHeaderSaveError(formatHeaderSaveError(err, HEADER_FIELD_LABELS[field]));
      return false;
    }
  };

  // The PO input is controlled so a failed save can put the stored value
  // back instead of leaving the CSR's unsaved text on screen.
  const [poDraft, setPoDraft] = useState<string>(order.po_number || '');
  useEffect(() => {
    setPoDraft(order.po_number || '');
  }, [order.id, order.po_number]);

  // Gate (kanban 3019f212): a PO that leaves the one this draft was generated
  // from first asks "is this a different order?" — see handleCustomerChange.
  const [poIdentityWarning, setPoIdentityWarning] = useState<PendingHeaderIdentityWarning<string> | null>(null);
  const [poIdentityBusy, setPoIdentityBusy] = useState(false);
  const commitPoNumber = async () => {
    const val = poDraft.trim();
    if (val === (order.po_number || '')) return;
    const check = await runHeaderIdentityCheck(order, { po_number: val });
    if (check) {
      setPoIdentityWarning({ check, pending: val });
      return;
    }
    await savePoNumber(val);
  };
  const savePoNumber = async (val: string, ruling?: HeaderIdentityRuling) => {
    setPoIdentityBusy(true);
    try {
      if (ruling?.differentOrder) {
        try {
          await portalOrderService.detachSourceIdentity(order.id, {
            reason: 'different_order',
            note: ruling.note?.trim() || null,
          });
        } catch (err) {
          console.error('Failed to record the different-order ruling:', err);
          setHeaderSaveError(formatHeaderSaveError(err, HEADER_FIELD_LABELS.po_number));
          setPoDraft(order.po_number || '');
          return;
        }
      }
      const ok = await handleSageFieldChange('po_number', val);
      if (!ok) setPoDraft(order.po_number || '');
    } finally {
      setPoIdentityBusy(false);
      setPoIdentityWarning(null);
    }
  };

  // Salesperson is a two-field update (code + display name) so it can't go
  // through handleSageFieldChange's single-field PATCH.
  const handleSalespersonChange = async (spNo: string) => {
    if (!spNo || spNo === (order.salesperson_no || '')) return;
    const sp = salespersonOptions.find((s) => s.salesperson_no === spNo);
    try {
      const updated = await portalOrderService.updateOrder(order.id, {
        salesperson_no: spNo,
        salesperson_name: sp?.name || '',
      });
      onOrderUpdated?.(updated);
      setHeaderSaveError(null);
    } catch (err) {
      console.error('Failed to update salesperson:', err);
      setHeaderSaveError(formatHeaderSaveError(err, 'The salesperson'));
    }
  };

  // ── Total / unpriced lines (display only; see utils/orderTotals.ts) ──
  const unpricedLineCount = useMemo(
    () => countUnpricedLines(orderDetail.line_items),
    [orderDetail.line_items],
  );
  const totalView = useMemo(() => orderTotalFromDetail(orderDetail), [orderDetail]);

  // ── Charge-freight flag (bill freight to the customer at invoicing) ──
  // Editable on drafts and on orders still open in Sage; the backend locks
  // it once the sage_invoices mirror shows the order invoiced. Waiving
  // keeps a DO NOT BILL FREIGHT /C instruction on the order, so the
  // response carries a refreshed instruction list.
  const [chargeFreightBusy, setChargeFreightBusy] = useState(false);
  const [chargeFreightMessage, setChargeFreightMessage] = useState<{
    kind: 'ok' | 'error';
    text: string;
  } | null>(null);
  // Latest push of the waiver to the sales site: the invoice build reads
  // this, not the /C line. Seeded from the detail response, replaced by the
  // toggle response when a push was attempted.
  const [waiverPush, setWaiverPush] = useState<FreightWaiverPushInfo | null>(
    orderDetail.freight_waiver_push ?? null,
  );
  useEffect(() => {
    setWaiverPush(orderDetail.freight_waiver_push ?? null);
  }, [orderDetail.freight_waiver_push]);

  const handleChargeFreightToggle = async (value: boolean) => {
    if (chargeFreightBusy) return;
    setChargeFreightBusy(true);
    setChargeFreightMessage(null);
    try {
      const resp = await portalOrderService.setChargeFreight(order.id, value);
      onInstructionsChanged(resp.instructions);
      setChargeFreightMessage({
        kind: resp.succeeded && resp.push?.outcome !== 'submit_failed' ? 'ok' : 'error',
        text: resp.message,
      });
      if (resp.succeeded) {
        onOrderUpdated?.({ ...order, charge_freight: resp.charge_freight });
        if (resp.waiver_push) setWaiverPush(resp.waiver_push);
      }
    } catch {
      setChargeFreightMessage({
        kind: 'error',
        text: 'Failed to update freight billing. Please retry.',
      });
    } finally {
      setChargeFreightBusy(false);
    }
  };

  // ── Dropdown data: warehouse (hardcoded + API counts) and ship_via (API only) ──
  const warehouseItems = useMemo(
    () => buildWarehouseDropdownItems(warehouseOptions),
    [warehouseOptions],
  );

  // Ship_via items come from SO_ShippingRateHeader (143+ carriers). Only the
  // handful in HIGHLIGHTED_SHIP_VIA_CODES — the ones that actually appeared
  // on orders in the past year — surface in the "Active" group; the rest land
  // in the collapsed "Other" group behind the Show-all toggle.
  const shipViaItems: DropdownItem[] = useMemo(
    () =>
      shippingCarrierOptions.map((c) => ({
        value: c.shipping_code,
        label: c.description || c.shipping_code,
        highlighted: isHighlightedShipVia(c.shipping_code),
      })),
    [shippingCarrierOptions],
  );

  const salespersonItems: DropdownItem[] = useMemo(
    () =>
      salespersonOptions.map((s) => ({
        value: s.salesperson_no,
        label: s.name || s.salesperson_no,
        // Single flat group — with none highlighted the whole list would
        // collapse behind the "Show all" toggle.
        highlighted: true,
      })),
    [salespersonOptions],
  );

  // ── Inline Add Line Item state ──
  const [showAddSearch, setShowAddSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ProductSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [addSuccess, setAddSuccess] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  // Product-catalog search failure (5xx / 401 / network). Distinct from an
  // empty result so operators can retry instead of reading "No products found".
  const [searchError, setSearchError] = useState<string | null>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Monotonic id of the latest search request; a slower, older request that
  // settles after a newer keystroke must not overwrite its results or error.
  const searchSeq = useRef(0);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // ── "Fill with this product" inline form state ──
  // When the user clicks "Fill with…" on a search result, we show a small
  // labeled form where they enter a target total weight. We compute the
  // quantity from the product's unit ship_weight and show a live preview.
  const [fillWithProduct, setFillWithProduct] = useState<ProductSearchResult | null>(null);

  // Per-prompt quantity drafts for the inline backorder offer — the CSR can
  // edit the suggested remainder before accepting. Keyed by prompt id;
  // absent key means "use the suggestion as-is".
  const [backorderQtyDrafts, setBackorderQtyDrafts] = useState<Map<string, string>>(new Map());
  // Expired-quote alert banner → the Nudge button: the alert id awaiting the
  // API, and a per-alert line when the nudge was refused or failed.
  const [nudgeBusy, setNudgeBusy] = useState<string | null>(null);
  const [nudgeNotes, setNudgeNotes] = useState<Map<string, string>>(new Map());
  const nudgeSalesperson = async (alertId: string) => {
    const note = (text: string | null) => setNudgeNotes((prev) => {
      const next = new Map(prev);
      if (text) next.set(alertId, text); else next.delete(alertId);
      return next;
    });
    setNudgeBusy(alertId);
    note(null);
    try {
      await portalOrderService.nudgeExpiredQuoteAlert(order.id, alertId);
      onRefreshPricingContext?.();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      const message = detail && typeof detail === 'object' && 'message' in detail
        ? String((detail as { message: unknown }).message)
        : 'The nudge could not be sent. Try again, or contact them directly.';
      note(message);
      onRefreshPricingContext?.();
    } finally {
      setNudgeBusy(null);
    }
  };
  const [fillTargetInput, setFillTargetInput] = useState('');
  const [fillError, setFillError] = useState<string | null>(null);
  const fillInputRef = useRef<HTMLInputElement>(null);

  // ── Manual-price entry state ──
  // When the pricing waterfall returns no authoritative price for a product
  // (resolved.source === 'investigation'), we open this inline form so the
  // CSR can enter a unit price and qty manually instead of being blocked by
  // a toast they can't act on. Hit on customers like ARMST03 that have SP
  // on a 5-gal pail SKU but none on the matching 1-gal can sibling.
  const [manualPriceProduct, setManualPriceProduct] =
    useState<ProductSearchResult | null>(null);
  const [manualPriceQty, setManualPriceQty] = useState('1');
  const [manualPriceInput, setManualPriceInput] = useState('');
  const [manualPriceReason, setManualPriceReason] = useState<string | null>(null);
  const [manualPriceError, setManualPriceError] = useState<string | null>(null);
  const manualPriceInputRef = useRef<HTMLInputElement>(null);

  // ── Confirm-price step (kanban e271f10d) ──
  // After a search result is picked, the waterfall price is NOT applied
  // silently any more. This panel shows the price the line will get, where
  // it came from (SP / Quote #n / Std), and every other price on file for
  // the customer, so the rep can confirm or switch before the line lands
  // ("idk if it's giving me the special price or not" — Emily, ER).
  const [priceConfirm, setPriceConfirm] = useState<PriceConfirmState | null>(null);
  // Index into priceConfirm.choices of the highlighted option (Enter adds it).
  const [priceConfirmPick, setPriceConfirmPick] = useState<number | null>(null);
  // Re-pricing in flight after a quantity edit (tiered SP depends on qty).
  const [priceConfirmLoading, setPriceConfirmLoading] = useState(false);
  const priceConfirmQtyRef = useRef<HTMLInputElement>(null);
  const priceConfirmPanelRef = useRef<HTMLDivElement>(null);
  const priceConfirmQtyTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Monotonic id so a slow re-price can't overwrite a newer one.
  const priceConfirmSeq = useRef(0);

  // ── Inline edit state ──
  const [editingCell, setEditingCell] = useState<{ itemId: string; field: 'quantity' | 'unit_price' } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  // When true, the next blur on the edit input is a programmatic transition
  // (e.g. Tab/Enter navigation) and should not trigger a second saveEdit().
  const skipBlurRef = useRef(false);

  // ── Optional "why?" after a price saved away from the PO price ──
  // Owner ask 2026-10-29 (review amn8ne, AU02584): once a line price is saved
  // to something other than what the PO said, offer a one-line note. The
  // price is already saved; this never blocks keying. One prompt at a time:
  // the newest qualifying save replaces an older unanswered one.
  const [priceReasonPrompt, setPriceReasonPrompt] = useState<{
    itemId: string;
    itemCode: string;
    poPrice: string;
    newPrice: string;
  } | null>(null);
  const [priceReasonSaving, setPriceReasonSaving] = useState(false);

  // ── Sage-bound line comment editor ──
  // CSR-entered comment written to the Sage detail line's native CommentText
  // on submission (distinct from `notes`, which stays internal). One line
  // edits at a time; Save PATCHes sage_comment_text through updateLineItem.
  const [commentEditId, setCommentEditId] = useState<string | null>(null);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentSavingId, setCommentSavingId] = useState<string | null>(null);
  const saveSageComment = async (itemId: string) => {
    if (!orderDetail) return;
    const trimmed = commentDraft.trim();
    const existing = (orderDetail.line_items.find((x) => x.id === itemId)?.sage_comment_text ?? '').trim();
    // Unchanged text (incl. Tab-through with nothing typed) just closes the
    // editor — don't PATCH '' onto a line that never had a comment.
    if (trimmed === existing) {
      setCommentEditId(null);
      return;
    }
    setCommentSavingId(itemId);
    try {
      const resp = await portalOrderService.updateLineItem(order.id, itemId, {
        sage_comment_text: trimmed,
      });
      onLineItemUpdated?.(resp);
      setCommentEditId(null);
    } catch {
      // Keep the editor open so the CSR can retry or copy their text out.
    } finally {
      setCommentSavingId(null);
    }
  };

  // ── Order-level instructions (standalone Sage /C lines) ──
  const [instructionAddOpen, setInstructionAddOpen] = useState(false);
  const [instructionDraft, setInstructionDraft] = useState('');
  const [instructionEditId, setInstructionEditId] = useState<string | null>(null);
  const [instructionEditDraft, setInstructionEditDraft] = useState('');
  const [instructionBusy, setInstructionBusy] = useState(false);
  const [instructionMessage, setInstructionMessage] = useState<{
    kind: 'ok' | 'error';
    text: string;
  } | null>(null);

  const applyInstructionResponse = (resp: InstructionMutationResponse) => {
    onInstructionsChanged(resp.instructions);
    setInstructionMessage({
      kind: resp.succeeded && resp.push?.outcome !== 'submit_failed' ? 'ok' : 'error',
      text: resp.message,
    });
  };

  const submitNewInstruction = async () => {
    const trimmed = instructionDraft.trim();
    if (!trimmed || instructionBusy) return;
    setInstructionBusy(true);
    setInstructionMessage(null);
    try {
      const resp = await portalOrderService.addInstruction(order.id, trimmed);
      applyInstructionResponse(resp);
      if (resp.succeeded) {
        setInstructionDraft('');
        setInstructionAddOpen(false);
      }
    } catch {
      setInstructionMessage({
        kind: 'error',
        text: 'Failed to add the instruction. Please retry.',
      });
    } finally {
      setInstructionBusy(false);
    }
  };

  const submitInstructionEdit = async (instructionId: string) => {
    const trimmed = instructionEditDraft.trim();
    if (!trimmed || instructionBusy) return;
    setInstructionBusy(true);
    setInstructionMessage(null);
    try {
      const resp = await portalOrderService.updateInstruction(
        order.id,
        instructionId,
        trimmed,
      );
      applyInstructionResponse(resp);
      if (resp.succeeded) setInstructionEditId(null);
    } catch {
      setInstructionMessage({
        kind: 'error',
        text: 'Failed to update the instruction. Please retry.',
      });
    } finally {
      setInstructionBusy(false);
    }
  };

  const removeInstruction = async (instructionId: string) => {
    if (instructionBusy) return;
    setInstructionBusy(true);
    setInstructionMessage(null);
    try {
      const resp = await portalOrderService.deleteInstruction(order.id, instructionId);
      applyInstructionResponse(resp);
    } catch {
      setInstructionMessage({
        kind: 'error',
        text: 'Failed to remove the instruction. Please retry.',
      });
    } finally {
      setInstructionBusy(false);
    }
  };

  const retryInstructionPush = async () => {
    if (instructionBusy) return;
    setInstructionBusy(true);
    setInstructionMessage(null);
    try {
      const resp = await portalOrderService.pushInstructions(order.id);
      applyInstructionResponse(resp);
    } catch {
      setInstructionMessage({
        kind: 'error',
        text: 'Failed to send instructions to Sage. Please retry.',
      });
    } finally {
      setInstructionBusy(false);
    }
  };

  // Suggested (Merlin) lines are opt-in: they never ride a Sage payload
  // until a CSR accepts them (2026-08-24 ruling — the desk deleted 100% of
  // the auto-sent lines, and /C text prints on the customer invoice and
  // the carrier BOL).
  const acceptInstruction = async (instructionId: string) => {
    if (instructionBusy) return;
    setInstructionBusy(true);
    setInstructionMessage(null);
    try {
      const resp = await portalOrderService.acceptInstruction(order.id, instructionId);
      applyInstructionResponse(resp);
    } catch {
      setInstructionMessage({
        kind: 'error',
        text: 'Failed to add the instruction. Please retry.',
      });
    } finally {
      setInstructionBusy(false);
    }
  };

  const acceptAllInstructions = async () => {
    if (instructionBusy) return;
    setInstructionBusy(true);
    setInstructionMessage(null);
    try {
      const resp = await portalOrderService.acceptAllInstructions(order.id);
      applyInstructionResponse(resp);
    } catch {
      setInstructionMessage({
        kind: 'error',
        text: 'Failed to add the suggested instructions. Please retry.',
      });
    } finally {
      setInstructionBusy(false);
    }
  };

  const suggestedInstructionCount = orderInstructions.filter(
    (i) => i.sage_state === 'suggested',
  ).length;

  // ── Inline item code switch state ──
  const [switchingItemId, setSwitchingItemId] = useState<string | null>(null);
  const [switchQuery, setSwitchQuery] = useState('');
  const [switchResults, setSwitchResults] = useState<ProductSearchResult[]>([]);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [switchSaving, setSwitchSaving] = useState(false);
  const switchTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const switchInputRef = useRef<HTMLInputElement>(null);

  // ── "Set item code" target ──
  // When the salesperson clicks "Set item code" on a description-only draft
  // line, we reuse the Add-Line-Item search panel for parity with that flow.
  // This id captures which line to UPDATE (not add) when the next product
  // selection happens. handleAddProduct and submitManualPrice check this:
  // when set, they call updateLineItem on the target line and preserve its
  // existing quantity, instead of creating a new line.
  const [setItemCodeTargetId, setSetItemCodeTargetId] = useState<string | null>(null);

  // ── User-priced lines (this session) ──
  // Line ids whose unit_price the user explicitly typed (inline price edit
  // or the manual-price form). An investigation-classified price the user
  // set deliberately renders as a muted "Custom" badge instead of the amber
  // REVIEW pill — the user already looked at it.
  const [userPricedIds, setUserPricedIds] = useState<Set<string>>(() => new Set());
  const markUserPriced = (itemId: string) => {
    setUserPricedIds((prev) => {
      const next = new Set(prev);
      next.add(itemId);
      return next;
    });
  };

  // ── Review answer panel state ──
  // Panel row with an in-flight resolve/undo PATCH (disables its buttons).
  const [reviewBusyId, setReviewBusyId] = useState<string | null>(null);
  // Panel row with the inline "Fix quantity…" input open, and its value.
  const [reviewFixQtyId, setReviewFixQtyId] = useState<string | null>(null);
  const [reviewFixQtyValue, setReviewFixQtyValue] = useState('');
  // Issue expander: the answer card lives INLINE under its own line-table
  // row (reviewer feedback, 2026-07: "the item lines themselves should have
  // just like a 'X issues' with a down arrow" — the row already shows what
  // we extracted, so the card carries only the delta). Minimized at first;
  // at most ONE line's card is open. Null = all collapsed. Resolving a line
  // still auto-opens the next flagged one (the stepper flow).
  const [expandedAnswerId, setExpandedAnswerId] = useState<string | null>(null);
  // Sub-stepper WITHIN a card (reviewer feedback, 2026-07): when a line has
  // several distinct questions (reason fragments), show ONE at a time —
  // "Looks good" on a non-final question slides to the next; only the final
  // answer resolves the line. Keyed by line id; reset on resolve/undo.
  const [cardSubStep, setCardSubStep] = useState<Record<string, number>>({});
  const setSubStep = (itemId: string, step: number) =>
    setCardSubStep((prev) => ({ ...prev, [itemId]: step }));
  // After a card resolves, advance the stepper to the next unanswered line.
  const advanceAnswerStepper = (resolvedId: string) => {
    setSubStep(resolvedId, 0);
    const next = orderDetail.line_items.find(
      (li) => li.id !== resolvedId && lineNeedsAnswer(li),
    );
    setExpandedAnswerId(next ? next.id : null);
  };

  // Focus search input when opened
  useEffect(() => {
    if (showAddSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showAddSearch]);

  // Focus the target-weight input when the Fill form opens
  useEffect(() => {
    if (fillWithProduct && fillInputRef.current) {
      fillInputRef.current.focus();
    }
  }, [fillWithProduct]);

  // Focus the unit-price input when the manual-price form opens
  useEffect(() => {
    if (manualPriceProduct && manualPriceInputRef.current) {
      manualPriceInputRef.current.focus();
    }
  }, [manualPriceProduct]);

  // Focus the confirm-price panel when it opens: qty input when editable
  // (type a qty, Enter adds), else the panel itself so Enter/Esc/arrows work.
  const priceConfirmProductId = priceConfirm?.product.id ?? null;
  useEffect(() => {
    if (!priceConfirmProductId) return;
    if (priceConfirmQtyRef.current && !priceConfirmQtyRef.current.disabled) {
      priceConfirmQtyRef.current.focus();
      priceConfirmQtyRef.current.select();
    } else {
      priceConfirmPanelRef.current?.focus();
    }
  }, [priceConfirmProductId]);

  // Focus edit input when editing
  useEffect(() => {
    if (editingCell && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingCell]);

  // Focus switch search input when opened
  useEffect(() => {
    if (switchingItemId && switchInputRef.current) {
      switchInputRef.current.focus();
    }
  }, [switchingItemId]);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (!addSuccess) return;
    const timer = setTimeout(() => setAddSuccess(null), 3000);
    return () => clearTimeout(timer);
  }, [addSuccess]);

  // Clear add-line-item errors after 8 seconds
  useEffect(() => {
    if (!addError) return;
    const timer = setTimeout(() => setAddError(null), 8000);
    return () => clearTimeout(timer);
  }, [addError]);

  // Debounced product search
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setSearchError(null);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    const seq = ++searchSeq.current;

    if (query.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await portalProductService.searchProducts(query, { limit: 50 });
        if (seq !== searchSeq.current) return;
        setSearchResults(res.products);
      } catch (err) {
        if (seq !== searchSeq.current) return;
        console.error('Product search failed', err);
        setSearchResults([]);
        const message = formatProductSearchError(err);
        setSearchError(message);
        // Audit row: the search term is kept (recorder value key), the
        // outcome is `none` because nothing opened or navigated, and the
        // error text rides along for the backend event queue.
        trackEvent('action', 'order_line_search_failed', {
          order_id: order.id,
          query,
          error: message,
          outcome: 'none',
        });
      } finally {
        if (seq === searchSeq.current) setSearchLoading(false);
      }
    }, 300);
  }, [order.id]);

  // Add product as line item (optionally with a pre-computed quantity).
  //
  // Pricing must come from the authoritative backend waterfall
  // (resolve_price: quote → SP → hybrid → investigation) — NEVER from
  // product.standard_unit_price. Previously this code fell back to the
  // Sage list price when the narrow SP-flat lookup missed, which caused
  // the AU00249 overcharge ($702.71 list instead of $144.95 quote).
  const handleAddProduct = async (
    product: ProductSearchResult,
    quantityOverride?: string,
  ) => {
    setAddingItem(true);
    setAddError(null);
    setAddSuccess(null);
    // When the user clicked "Set item code" on a description-only line, this
    // flow updates THAT line rather than adding a new one. Look up the
    // target line so we preserve its existing quantity for the pricing
    // waterfall lookup (quantity affects tiered SP).
    const targetLineId = setItemCodeTargetId;
    const targetLine = targetLineId
      ? orderDetail.line_items.find((li) => li.id === targetLineId) ?? null
      : null;
    try {
      const quantity =
        quantityOverride ??
        (targetLine?.quantity != null ? String(targetLine.quantity) : '1');

      // Ask the backend to run the full waterfall for this
      // (item_code, customer_no, quantity). Its resolved_price is what
      // the submission pipeline would use.
      const resolved = await lookupResolvedPrice(product, quantity);

      // If the waterfall could not find an authoritative price, do NOT
      // seed from standard_unit_price — that's how bad prices slip
      // through. Open an inline manual-price form so the operator can
      // enter the price explicitly before the line is created.
      if (!resolved || !resolved.resolved_price || resolved.source === 'investigation') {
        openManualPriceForm(product, quantityOverride ?? '1', resolved?.investigation_reason ?? null);
        return;
      }

      // Authoritative price found: show it, its source, and the other
      // prices on file. The line is created by submitPriceConfirm.
      const choices = buildPriceChoices(resolved);
      setPriceConfirm({
        product,
        quantity,
        // "Set item code" keeps the line's own quantity (not sent in the
        // update), so it isn't editable here.
        quantityLocked: targetLineId != null,
        targetLineId,
        resolved,
        choices,
      });
      setPriceConfirmPick(recommendedChoiceIndex(choices));
    } catch (err) {
      console.error('Failed to price line item', err);
      setAddError(formatAddLineItemError(err, product.sage_item_code));
    } finally {
      setAddingItem(false);
    }
  };

  /** Backend waterfall lookup; null when the request itself fails (the
   *  caller then forces operator entry — never a client-side fallback). */
  const lookupResolvedPrice = async (
    product: ProductSearchResult,
    quantity: string,
  ): Promise<ResolvedPriceInfo | null> => {
    try {
      const lookup = await portalProductService.lookupProductWithPricing(
        product.sage_item_code,
        order.customer_no,
        quantity,
        order.id,
      );
      return lookup.resolved;
    } catch {
      return null;
    }
  };

  const openManualPriceForm = (
    product: ProductSearchResult,
    quantity: string,
    reason: string | null,
  ) => {
    setManualPriceProduct(product);
    setManualPriceQty(quantity);
    setManualPriceInput('');
    setManualPriceReason(reason);
    setManualPriceError(null);
    setAddSuccess(null);
    setAddError(null);
  };

  /**
   * Create the line (or, on the "Set item code" path, update the target
   * line) at an explicitly chosen price. ``sourceLabel`` names where the
   * price came from for the success line ("… @ $144.95 (Special Pricing)").
   */
  const commitLineAdd = async (
    product: ProductSearchResult,
    quantity: string,
    unitPrice: string,
    sourceLabel: string,
    targetLineId: string | null,
  ) => {
    setAddingItem(true);
    setAddError(null);
    setAddSuccess(null);
    const price = Number(unitPrice);
    try {
      if (targetLineId) {
        // "Set item code" path: UPDATE the existing description-only line
        // with the chosen Sage product code and the chosen price. Quantity
        // already lives on the line — don't overwrite it.
        const targetLine = orderDetail.line_items.find((li) => li.id === targetLineId) ?? null;
        const updateData: LineItemUpdateRequest = {
          item_code: product.sage_item_code,
          item_description:
            product.udf_fulldesc ||
            product.item_code_desc ||
            product.sage_item_code,
          product_line: product.product_line || undefined,
          unit_price: unitPrice,
        };
        // Setting a product on a flagged line answers its review card —
        // resolve it as 'fixed' in the same PATCH so the card clears.
        const answersCard = targetLine != null && lineNeedsAnswer(targetLine);
        if (answersCard) {
          updateData.needs_review = false;
          updateData.review_resolution = 'fixed';
        }
        const resp = await portalOrderService.updateLineItem(
          order.id,
          targetLineId,
          updateData,
        );
        setAddSuccess(
          formatAddSuccess(product.sage_item_code, quantity, price, sourceLabel, 'set'),
        );
        setSearchQuery('');
        setSearchResults([]);
        setSetItemCodeTargetId(null);
        onLineItemUpdated?.(resp);
        if (answersCard) {
          advanceAnswerStepper(targetLineId);
        }
      } else {
        const createData: LineItemCreateRequest = {
          item_code: product.sage_item_code,
          item_description: product.udf_fulldesc || product.item_code_desc || product.sage_item_code,
          product_line: product.product_line || undefined,
          quantity,
          unit_price: unitPrice,
        };

        trackEvent('action', 'order_line_added', { order_id: order.id, item_code: createData.item_code, outcome: 'request' });
        const resp = await portalOrderService.addLineItem(order.id, createData);

        setAddSuccess(
          formatAddSuccess(product.sage_item_code, quantity, price, sourceLabel),
        );
        setSearchQuery('');
        setSearchResults([]);
        onLineItemAdded?.(resp);
      }
    } catch (err) {
      console.error('Failed to add line item', err);
      setAddError(formatAddLineItemError(err, product.sage_item_code));
    } finally {
      setAddingItem(false);
    }
  };

  // ── Confirm-price panel handlers ──
  const cancelPriceConfirm = () => {
    if (priceConfirmQtyTimeout.current) clearTimeout(priceConfirmQtyTimeout.current);
    priceConfirmSeq.current += 1;
    setPriceConfirm(null);
    setPriceConfirmPick(null);
    setPriceConfirmLoading(false);
  };

  const submitPriceConfirm = async () => {
    if (!priceConfirm || priceConfirmLoading) return;
    const choice =
      priceConfirmPick != null ? priceConfirm.choices[priceConfirmPick] : undefined;
    if (!choice) return;
    const qty = parseFloat(priceConfirm.quantity.trim());
    if (!isFinite(qty) || qty <= 0) {
      setAddError('Enter a positive quantity.');
      return;
    }
    const { product, targetLineId } = priceConfirm;
    cancelPriceConfirm();
    await commitLineAdd(product, String(qty), choice.price.toFixed(2), choice.label, targetLineId);
  };

  /**
   * Quantity edit on the panel: re-run the waterfall for the new qty
   * (debounced) because tiered special pricing depends on it. A re-price
   * that comes back as investigation hands off to the manual-price form.
   */
  const handlePriceConfirmQtyChange = (value: string) => {
    setPriceConfirm((prev) => (prev ? { ...prev, quantity: value } : prev));
    setAddError(null);
    if (priceConfirmQtyTimeout.current) clearTimeout(priceConfirmQtyTimeout.current);
    const qty = parseFloat(value.trim());
    if (!isFinite(qty) || qty <= 0 || !priceConfirm) return;
    const { product } = priceConfirm;
    const seq = ++priceConfirmSeq.current;
    priceConfirmQtyTimeout.current = setTimeout(async () => {
      setPriceConfirmLoading(true);
      const resolved = await lookupResolvedPrice(product, String(qty));
      if (seq !== priceConfirmSeq.current) return; // superseded / cancelled
      setPriceConfirmLoading(false);
      if (!resolved || !resolved.resolved_price || resolved.source === 'investigation') {
        cancelPriceConfirm();
        openManualPriceForm(product, String(qty), resolved?.investigation_reason ?? null);
        return;
      }
      const choices = buildPriceChoices(resolved);
      setPriceConfirm((prev) => (prev ? { ...prev, resolved, choices } : prev));
      setPriceConfirmPick(recommendedChoiceIndex(choices));
    }, 300);
  };

  const handlePriceConfirmKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!priceConfirm) return;
    if (e.key === 'Escape') {
      // Keep the modal's window-level Escape handler from closing the
      // whole modal — Esc here only cancels the confirm step.
      e.preventDefault();
      e.stopPropagation();
      cancelPriceConfirm();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const count = priceConfirm.choices.length;
      if (count === 0) return;
      e.preventDefault();
      const dir = e.key === 'ArrowDown' ? 1 : -1;
      setPriceConfirmPick((prev) => {
        const cur = prev ?? (dir === 1 ? -1 : count);
        return (cur + dir + count) % count;
      });
      return;
    }
    if (e.key === 'Enter') {
      // Buttons (Cancel / Add / an option row) handle their own Enter as a
      // click; everywhere else — the qty input, the panel — Enter adds.
      if ((e.target as HTMLElement).tagName === 'BUTTON') return;
      e.preventDefault();
      void submitPriceConfirm();
    }
  };

  /**
   * "Fill with this product" — open an inline form labeled "Target weight:"
   * so the user can enter the weight the customer asked for (e.g. "fill
   * with 1000 lbs of CM-DURAL50"). The form shows a live preview of the
   * computed quantity and rounded actual weight before the user commits.
   */
  const handleFillWithProduct = (product: ProductSearchResult) => {
    const unitWeight = parseWeight(resolveShipWeight(product));
    if (unitWeight == null || unitWeight <= 0) {
      setFillError(
        `Cannot "fill with" ${product.sage_item_code}: no unit weight on file for this product.`,
      );
      return;
    }
    setFillError(null);
    setFillTargetInput('');
    setFillWithProduct(product);
  };

  const cancelFillWith = () => {
    setFillWithProduct(null);
    setFillTargetInput('');
    setFillError(null);
  };

  const submitFillWith = async () => {
    if (!fillWithProduct) return;
    const unitWeight = parseWeight(resolveShipWeight(fillWithProduct));
    const target = parseFloat(fillTargetInput.trim());
    const qty = quantityForTargetWeight(target, unitWeight);
    if (qty == null) {
      setFillError('Enter a positive target weight (lbs).');
      return;
    }
    const product = fillWithProduct;
    // Close the form before the async call so the success banner from
    // handleAddProduct isn't hidden behind it.
    cancelFillWith();
    await handleAddProduct(product, String(qty));
  };

  // ── Manual-price form handlers ──
  const cancelManualPrice = () => {
    setManualPriceProduct(null);
    setManualPriceQty('1');
    setManualPriceInput('');
    setManualPriceReason(null);
    setManualPriceError(null);
  };

  const submitManualPrice = async () => {
    if (!manualPriceProduct) return;
    const qty = parseFloat(manualPriceQty.trim());
    if (!isFinite(qty) || qty <= 0) {
      setManualPriceError('Enter a positive quantity.');
      return;
    }
    const price = parseFloat(manualPriceInput.trim());
    if (!isFinite(price) || price <= 0) {
      setManualPriceError('Enter a positive unit price.');
      return;
    }
    const product = manualPriceProduct;
    const qtyStr = String(qty);
    const priceStr = price.toFixed(2);
    // Capture the "Set item code" target before cancelManualPrice() — note
    // that cancelManualPrice does not touch setItemCodeTargetId, but reading
    // it up-front keeps the branch unambiguous.
    const targetLineId = setItemCodeTargetId;
    cancelManualPrice();
    setAddingItem(true);
    setAddError(null);
    setAddSuccess(null);
    try {
      if (targetLineId) {
        // "Set item code" path: update the existing line. Don't overwrite
        // the line's existing quantity with the manual-price form's qty —
        // the operator entered that qty only to drive pricing.
        const updateData: LineItemUpdateRequest = {
          item_code: product.sage_item_code,
          item_description:
            product.udf_fulldesc ||
            product.item_code_desc ||
            product.sage_item_code,
          product_line: product.product_line || undefined,
          unit_price: priceStr,
        };
        // Setting a product on a flagged line answers its review card —
        // resolve it as 'fixed' in the same PATCH so the card clears.
        const manualTargetLine = orderDetail.line_items.find(
          (li) => li.id === targetLineId,
        );
        if (manualTargetLine && lineNeedsAnswer(manualTargetLine)) {
          updateData.needs_review = false;
          updateData.review_resolution = 'fixed';
        }
        const resp = await portalOrderService.updateLineItem(
          order.id,
          targetLineId,
          updateData,
        );
        setAddSuccess(
          `Set ${product.sage_item_code} on existing line (@ $${priceStr}, manual price)`,
        );
        setSearchQuery('');
        setSearchResults([]);
        setSetItemCodeTargetId(null);
        onLineItemUpdated?.(resp);
        // Hand-typed price — muted Custom badge, not the amber REVIEW pill.
        markUserPriced(targetLineId);
        // A manual-price product fix answers the line's card too — step on.
        advanceAnswerStepper(targetLineId);
      } else {
        const createData: LineItemCreateRequest = {
          item_code: product.sage_item_code,
          item_description:
            product.udf_fulldesc ||
            product.item_code_desc ||
            product.sage_item_code,
          product_line: product.product_line || undefined,
          quantity: qtyStr,
          unit_price: priceStr,
        };
        trackEvent('action', 'order_line_added', { order_id: order.id, item_code: product.sage_item_code, outcome: 'request' });
        const resp = await portalOrderService.addLineItem(order.id, createData);
        setAddSuccess(
          `Added ${product.sage_item_code} (qty ${qtyStr} @ $${priceStr}, manual price)`,
        );
        setSearchQuery('');
        setSearchResults([]);
        onLineItemAdded?.(resp);
        // Hand-typed price — muted Custom badge, not the amber REVIEW pill.
        markUserPriced(resp.line_item.id);
      }
    } catch (err) {
      console.error('Failed to submit manual-price line item', err);
      setAddError(formatAddLineItemError(err, product.sage_item_code));
    } finally {
      setAddingItem(false);
    }
  };

  // ── Inline edit handlers ──
  const startEdit = (itemId: string, field: 'quantity' | 'unit_price', currentValue: string) => {
    setEditingCell({ itemId, field });
    setEditValue(currentValue);
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  /**
   * Compute the next editable cell for horizontal keyboard navigation.
   *
   * Field order within a row: quantity → unit_price. After unit_price, we
   * advance to the next row's quantity (and symmetrically for Shift+Tab).
   * Returns null when the user has navigated past the first/last editable
   * cell — the caller should treat this as "exit edit mode".
   */
  const getNextEditableCell = (
    current: { itemId: string; field: 'quantity' | 'unit_price' },
    direction: 1 | -1,
  ): { itemId: string; field: 'quantity' | 'unit_price' } | null => {
    const items = orderDetail.line_items;
    if (items.length === 0) return null;
    const FIELDS: Array<'quantity' | 'unit_price'> = ['quantity', 'unit_price'];
    const itemIdx = items.findIndex((x) => x.id === current.itemId);
    if (itemIdx === -1) return null;
    const fieldIdx = FIELDS.indexOf(current.field);
    let nextFieldIdx = fieldIdx + direction;
    let nextItemIdx = itemIdx;
    if (nextFieldIdx >= FIELDS.length) {
      nextFieldIdx = 0;
      nextItemIdx += 1;
    } else if (nextFieldIdx < 0) {
      nextFieldIdx = FIELDS.length - 1;
      nextItemIdx -= 1;
    }
    if (nextItemIdx < 0 || nextItemIdx >= items.length) return null;
    return { itemId: items[nextItemIdx].id, field: FIELDS[nextFieldIdx] };
  };

  const saveEdit = async (
    nextCell?: { itemId: string; field: 'quantity' | 'unit_price' } | null,
  ) => {
    if (!editingCell) return;
    const trimmed = editValue.trim();
    const isValid = trimmed !== '' && !isNaN(parseFloat(trimmed));

    // Advance edit focus: either to the next horizontal cell, or exit entirely.
    const advance = () => {
      if (nextCell) {
        const nextItem = orderDetail.line_items.find((x) => x.id === nextCell.itemId);
        const initVal =
          nextCell.field === 'quantity'
            ? nextItem?.quantity ?? ''
            : nextItem?.unit_price ?? '';
        setEditingCell(nextCell);
        setEditValue(initVal);
      } else {
        setEditingCell(null);
        setEditValue('');
      }
    };

    if (!isValid) {
      // Invalid input: discard without persisting, but still honor navigation.
      advance();
      return;
    }
    setEditSaving(true);
    // Capture the pre-edit quantity for backorder-prompt detection BEFORE the
    // optimistic update replaces the line in local state.
    const editedItem = orderDetail.line_items.find((x) => x.id === editingCell.itemId);
    const prevQty = editedItem?.quantity != null ? parseFloat(String(editedItem.quantity)) : null;
    // Field name only — never the typed quantity or price.
    trackEvent('action', 'order_line_edit', {
      order_id: order.id,
      item_id: editingCell.itemId,
      field: editingCell.field,
      outcome: 'request',
    });
    try {
      const resp = await portalOrderService.updateLineItem(order.id, editingCell.itemId, {
        [editingCell.field]: trimmed,
      });
      onLineItemUpdated?.(resp);
      // A hand-typed price is a deliberate user decision — downgrade the
      // amber REVIEW pill to the muted Custom badge for this line.
      if (editingCell.field === 'unit_price') {
        markUserPriced(editingCell.itemId);
        // Saved away from the PO price → offer the optional "why?" row under
        // the line (owner ask 2026-10-29). Back onto the PO price, or no PO
        // price at all (manual line) → nothing to ask; also drop a stale
        // prompt for this line.
        if (
          editedItem
          && shouldPromptForPriceReason({
            previousUnitPrice: editedItem.unit_price,
            newUnitPrice: trimmed,
            extractedPrice: editedItem.extracted_price,
          })
        ) {
          setPriceReasonPrompt({
            itemId: editingCell.itemId,
            itemCode: editedItem.item_code,
            poPrice: String(editedItem.extracted_price),
            newPrice: trimmed,
          });
        } else if (priceReasonPrompt?.itemId === editingCell.itemId) {
          setPriceReasonPrompt(null);
        }
      }
      // Offer a backorder for the remainder when a quantity was REDUCED on
      // the inline cell. Inline-only by design — no notification/alert.
      // Other quantity-touching flows (product switch, fill-with-weight)
      // change quantities for re-coding reasons, not shortfalls, so only
      // the explicit qty cell edit triggers the offer.
      const newQty = parseFloat(trimmed);
      if (
        editingCell.field === 'quantity'
        && prevQty != null && Number.isFinite(prevQty) && Number.isFinite(newQty)
        && newQty >= 0 && newQty < prevQty
      ) {
        onQuantityReduced?.(order.id, editingCell.itemId, prevQty, newQty);
      }
    } catch {
      // Silently fail — user can retry
    } finally {
      setEditSaving(false);
      advance();
    }
  };

  /** Save the CSR's note on the price just keyed: PATCH price_change_reason
   *  alone (the price is already saved) → its own append-only audit row. */
  const savePriceReason = async (reason: string) => {
    if (!priceReasonPrompt) return;
    const { itemId } = priceReasonPrompt;
    setPriceReasonSaving(true);
    // Action + ids only — never the note text.
    trackEvent('action', 'order_line_price_reason', {
      order_id: order.id,
      item_id: itemId,
      outcome: 'request',
    });
    try {
      const resp = await portalOrderService.updateLineItem(order.id, itemId, {
        price_change_reason: reason,
      });
      onLineItemUpdated?.(resp);
      setPriceReasonPrompt(null);
    } catch {
      // Keep the prompt open so the CSR can retry or skip.
    } finally {
      setPriceReasonSaving(false);
    }
  };

  const skipPriceReason = () => {
    if (!priceReasonPrompt) return;
    trackEvent('action', 'order_line_price_reason_skip', {
      order_id: order.id,
      item_id: priceReasonPrompt.itemId,
      outcome: 'nothing',
    });
    setPriceReasonPrompt(null);
  };

  const handleEditBlur = () => {
    // Programmatic transition from Tab/Enter nav — saveEdit was already called.
    if (skipBlurRef.current) {
      skipBlurRef.current = false;
      return;
    }
    saveEdit();
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      const direction: 1 | -1 = e.shiftKey ? -1 : 1;
      const nextCell = editingCell ? getNextEditableCell(editingCell, direction) : null;
      // Suppress the blur that fires when the current input unmounts during
      // the navigation transition — we're already saving inline here.
      skipBlurRef.current = true;
      saveEdit(nextCell);
    } else if (e.key === 'Escape') {
      // The modal's window-level Escape handler checks for an active
      // `data-inline-edit-cell` input and skips onClose in that case, so
      // we only need to cancel the edit here.
      e.preventDefault();
      cancelEdit();
    }
  };

  const handleDeleteLineItem = async (itemId: string) => {
    setDeletingItemId(itemId);
    try {
      const totals = await portalOrderService.removeLineItem(order.id, itemId);
      onLineItemRemoved?.(itemId, totals);
    } catch {
      // Silently fail
    } finally {
      setDeletingItemId(null);
    }
  };

  // ── Inline item code switch handlers ──
  const startSwitch = (itemId: string) => {
    setSwitchingItemId(itemId);
    setSwitchQuery('');
    setSwitchResults([]);
    // Close any other edit
    cancelEdit();
  };

  const cancelSwitch = () => {
    setSwitchingItemId(null);
    setSwitchQuery('');
    setSwitchResults([]);
    if (switchTimeout.current) clearTimeout(switchTimeout.current);
  };

  const handleSwitchSearch = (query: string) => {
    setSwitchQuery(query);
    if (switchTimeout.current) clearTimeout(switchTimeout.current);

    if (query.length < 2) {
      setSwitchResults([]);
      return;
    }

    setSwitchLoading(true);
    switchTimeout.current = setTimeout(async () => {
      try {
        const res = await portalProductService.searchProducts(query, { limit: 50 });
        setSwitchResults(res.products);
      } catch {
        setSwitchResults([]);
      } finally {
        setSwitchLoading(false);
      }
    }, 300);
  };

  const handleSwitchSelect = async (product: ProductSearchResult) => {
    if (!switchingItemId) return;
    setSwitchSaving(true);
    try {
      // Re-price the switched item against the full waterfall (quote → SP
      // tier/flat → hybrid). Reading `lookup.special_pricing.special_price`
      // directly is wrong because: (1) tiered SP rows store a flat fallback
      // in `special_price` that should never win over an applicable tier,
      // and (2) an open customer quote that beats SP is bypassed entirely.
      // See AU00309 incident — Meredith Brothers were quoted from SP-flat
      // ($159.60 / $376.84) when a tier (T3=$142.28) and a quote
      // ($152.00) should have applied.
      const lineItem = orderDetail?.line_items.find((li) => li.id === switchingItemId);
      const quantity = lineItem?.quantity ?? '1';

      let resolvedPrice: string | null = null;
      try {
        const lookup = await portalProductService.lookupProductWithPricing(
          product.sage_item_code,
          order.customer_no,
          quantity,
          order.id,
        );
        if (
          lookup.resolved?.resolved_price &&
          lookup.resolved.source !== 'investigation'
        ) {
          resolvedPrice = lookup.resolved.resolved_price;
        }
      } catch {
        // Backend lookup failed — leave unit_price untouched. The line
        // keeps its prior price; the operator can edit it inline if the
        // switch needs a different price.
      }

      const updateData: LineItemUpdateRequest = {
        item_code: product.sage_item_code,
        item_description: product.udf_fulldesc || product.item_code_desc || product.sage_item_code,
        product_line: product.product_line || undefined,
      };

      if (resolvedPrice) {
        updateData.unit_price = resolvedPrice;
      }

      // Switching the product on a flagged line answers its review card —
      // resolve it as 'fixed' in the same PATCH so the card clears.
      if (lineItem && lineNeedsAnswer(lineItem)) {
        updateData.needs_review = false;
        updateData.review_resolution = 'fixed';
      }

      const resp = await portalOrderService.updateLineItem(order.id, switchingItemId, updateData);
      onLineItemUpdated?.(resp);
    } catch {
      // Silently fail — user can retry
    } finally {
      setSwitchSaving(false);
      cancelSwitch();
    }
  };

  const closeSearch = () => {
    setShowAddSearch(false);
    setSearchQuery('');
    setSearchResults([]);
    setSearchError(null);
    setSetItemCodeTargetId(null);
    cancelFillWith();
    cancelPriceConfirm();
  };

  /**
   * Open the Add-Line-Item search panel pre-armed to UPDATE the given line
   * instead of creating a new one. Used by the "Set item code" placeholder
   * on description-only draft lines so the salesperson gets the exact same
   * search + pricing UX as the Add Line Item button.
   */
  const startSetItemCode = (itemId: string) => {
    setSetItemCodeTargetId(itemId);
    setShowAddSearch(true);
    setSearchQuery('');
    setSearchResults([]);
    setSearchError(null);
    cancelEdit();
    cancelSwitch();
  };

  // ── Review answer panel handlers ──

  /**
   * Answer-panel PATCH: resolve or undo a review flag on a line. Flows
   * through the same optimistic onLineItemUpdated path as inline edits so
   * the line table below reflects the change immediately.
   */
  const patchReviewLine = async (itemId: string, data: LineItemUpdateRequest) => {
    setReviewBusyId(itemId);
    try {
      const resp = await portalOrderService.updateLineItem(order.id, itemId, data);
      onLineItemUpdated?.(resp);
      // Resolving a card minimizes it and steps to the next unanswered one
      // (undo — needs_review back to true — re-expands the undone card).
      if (data.needs_review === false) {
        advanceAnswerStepper(itemId);
      } else if (data.needs_review === true) {
        setExpandedAnswerId(itemId);
        setSubStep(itemId, 0);
      }
    } catch {
      // Silently fail — user can retry
    } finally {
      setReviewBusyId(null);
    }
  };

  const startReviewFixQty = (itemId: string, currentQty: string | null) => {
    setReviewFixQtyId(itemId);
    setReviewFixQtyValue(currentQty != null ? String(currentQty) : '');
  };

  const cancelReviewFixQty = () => {
    setReviewFixQtyId(null);
    setReviewFixQtyValue('');
  };

  const saveReviewFixQty = async (itemId: string) => {
    const trimmed = reviewFixQtyValue.trim();
    const num = parseFloat(trimmed);
    if (!Number.isFinite(num) || num <= 0) return;
    await patchReviewLine(itemId, {
      quantity: trimmed,
      needs_review: false,
      review_resolution: 'fixed',
    });
    cancelReviewFixQty();
  };

  return (
    <>
      {/* Pushed-to-Sage, awaiting-confirmation banner. An Edit-in-Sage write is
          applied to our record right away, but until the sage_writes reconciler
          confirms the on-box drain landed it in Sage (~2 min), we surface the
          pushed change here as PENDING — so the CSR sees "here's what we pushed,
          not yet confirmed" rather than silently trusting it. Clears on confirm;
          a drain failure raises a SAGE_WRITE_NOT_CONFIRMED red flag (never
          silently dropped). Populated by pending_sage_writes on the detail. */}
      {(orderDetail.pending_sage_writes?.length ?? 0) > 0 && (
        <div
          data-testid="sage-pending-writes-banner"
          role="status"
          style={{
            display: 'flex',
            gap: '10px',
            padding: '12px 14px',
            marginBottom: '12px',
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '8px',
            color: '#1e3a8a',
          }}
        >
          <span style={{ fontSize: '16px', lineHeight: 1.2 }}>&#8987;</span>
          <div style={{ fontSize: '13px', lineHeight: 1.5 }}>
            <div style={{ fontWeight: 700, marginBottom: '4px' }}>
              {orderDetail.pending_sage_writes!.length === 1
                ? 'A change was pushed to Sage — awaiting confirmation'
                : `${orderDetail.pending_sage_writes!.length} changes were pushed to Sage — awaiting confirmation`}
            </div>
            {orderDetail.pending_sage_writes!.map((w) => (
              <div
                key={w.id}
                data-testid="sage-pending-write"
                style={{ marginBottom: '4px' }}
              >
                {formatPendingSageWrite(w)}
              </div>
            ))}
            <div style={{ marginTop: '2px', opacity: 0.8 }}>
              Sage usually confirms within ~2 minutes. Until then these are the
              values we pushed, not yet confirmed landed.
            </div>
          </div>
        </div>
      )}

      {/* Merlin edit proposals — change-order emails Merlin translated into a
          confirmable old→new diff against THIS order: an open Sage SO, or a
          portal DRAFT the revision reached before submission (kanban
          3db03d35). Confirm routes through the audited
          /edit-proposals/{id}/confirm path (one-shot proposal_key; on a draft
          it edits the draft, no Sage push); dismiss is a terminal decline.
          Refetch on either so the card leaves and the pending-writes banner
          (or the re-enabled Submit button) appears. */}
      {(orderDetail.edit_proposals ?? []).map((proposal) => (
        <MerlinEditProposalCard
          key={proposal.id}
          orderId={orderDetail.order.id}
          proposal={proposal}
          targetIsDraft={
            orderDetail.order.status === 'draft' && !orderDetail.order.sage_order_no
          }
          onResolved={() => onProposalResolved?.()}
          lineItems={orderDetail.line_items.map((li) => ({
            id: li.id,
            item_code: li.item_code,
            item_description: li.item_description,
          }))}
        />
      ))}

      {/* Multi-pricing warnings */}
      {multiPricingWarnings.length > 0 && (
        <div className={styles.warningBanner}>
          <span className={styles.warningIcon}>&#9888;</span>
          <div className={styles.warningText}>
            <div className={styles.warningTitle}>Multiple Active Pricings Detected</div>
            {multiPricingWarnings.map((w, i) => <div key={i}>{w}</div>)}
          </div>
        </div>
      )}

      {/* Credit-hold alert — the customer is frozen in Sage. Submit-to-Sage
          still works, but Sage will block the order from shipping until
          accounting clears the hold. */}
      {pricingContext?.customer_on_hold && (
        <div
          className={styles.creditHoldAlert}
          data-testid="credit-hold-banner"
          role="alert"
        >
          <span className={styles.alertIcon}>&#9888;</span>
          <div>
            <div className={styles.alertTitle}>Customer on Credit Hold</div>
            <div className={styles.alertText}>
              {pricingContext.customer_name} ({pricingContext.customer_no}) is on credit hold
              in Sage. Verify with accounting before submitting.
            </div>
          </div>
        </div>
      )}

      {/* Merlin-extractor notice — this draft IS Merlin's own extraction.
          Kept deliberately quiet (Roblyn's verbosity feedback, kanban
          8f1eb421): with questions it is just "Merlin's questions" + the
          bullets; without, a one-line attribution. Per-line detail lives on
          the flagged lines. */}
      {order.llm_extraction_data?.extractor === 'merlin' && (
        <div
          className={styles.warningBanner}
          data-testid="merlin-extractor-panel"
          role="status"
        >
          <span className={styles.warningIcon}>&#10024;</span>
          <div className={styles.warningText}>
            {(order.llm_extraction_data.extraction_concerns ?? []).length > 0 ? (
              <>
                <div className={styles.warningTitle}>Merlin&apos;s questions</div>
                <ExtractionConcernsBanner
                  concerns={order.llm_extraction_data.extraction_concerns ?? []}
                  label=""
                  embedded
                />
              </>
            ) : (
              <div className={styles.warningTitle}>Extracted by Merlin</div>
            )}
          </div>
        </div>
      )}

      {/* Merlin propose-new cards — "this looks like a new customer/ship-to,
          create it?" One click opens the EXISTING Create-in-Sage flows
          prefilled with Merlin's block; the CSR remains the only gate.
          The customer card hides once the proposed account is bound onto the
          order (its name then matches the order's customer_name), or once the
          hourly relink sweep bound the account the desk created
          (llm_extraction_data.customer_relink carries the match evidence). */}
      {order.llm_extraction_data?.proposed_new_customer &&
        isDraft &&
        !order.llm_extraction_data.customer_relink &&
        order.llm_extraction_data.proposed_new_customer.name.trim().toLowerCase()
          !== (order.customer_name ?? '').trim().toLowerCase() && (
        <div
          className={styles.warningBanner}
          data-testid="proposed-new-customer-card"
          role="status"
        >
          <span className={styles.warningIcon}>&#128100;</span>
          <div className={styles.warningText}>
            <div className={styles.warningTitle}>
              Merlin thinks this is a NEW customer:{' '}
              {order.llm_extraction_data.proposed_new_customer.name}
            </div>
            <div>
              {[
                order.llm_extraction_data.proposed_new_customer.address1,
                order.llm_extraction_data.proposed_new_customer.city,
                order.llm_extraction_data.proposed_new_customer.state,
              ].filter(Boolean).join(', ') || 'No address extracted'}
            </div>
            {order.llm_extraction_data.proposed_new_customer.evidence && (
              <div style={{ fontStyle: 'italic', marginTop: '2px' }}>
                {order.llm_extraction_data.proposed_new_customer.evidence}
              </div>
            )}
            {onCreateProposedCustomer && (
              <button
                type="button"
                className={styles.actionBtn}
                data-testid="create-proposed-customer-btn"
                style={{ marginTop: '6px' }}
                onClick={onCreateProposedCustomer}
              >
                Create this customer in Sage…
              </button>
            )}
          </div>
        </div>
      )}

      {order.llm_extraction_data?.proposed_new_ship_to &&
        isDraft &&
        (order.llm_extraction_data.proposed_new_ship_to.address1 ?? '').trim().toLowerCase()
          !== String(order.ship_to_address?.address1 ?? '').trim().toLowerCase() && (
        <div
          className={styles.warningBanner}
          data-testid="proposed-new-ship-to-card"
          role="status"
        >
          <span className={styles.warningIcon}>&#128230;</span>
          <div className={styles.warningText}>
            <div className={styles.warningTitle}>
              Merlin thinks this ships to a NEW address
            </div>
            <div>
              {[
                order.llm_extraction_data.proposed_new_ship_to.name,
                order.llm_extraction_data.proposed_new_ship_to.address1,
                order.llm_extraction_data.proposed_new_ship_to.city,
                order.llm_extraction_data.proposed_new_ship_to.state,
              ].filter(Boolean).join(', ')}
            </div>
            {order.llm_extraction_data.proposed_new_ship_to.evidence && (
              <div style={{ fontStyle: 'italic', marginTop: '2px' }}>
                {order.llm_extraction_data.proposed_new_ship_to.evidence}
              </div>
            )}
            {onCreateProposedShipTo && (
              <button
                type="button"
                className={styles.actionBtn}
                data-testid="create-proposed-ship-to-btn"
                style={{ marginTop: '6px' }}
                onClick={onCreateProposedShipTo}
              >
                Create this ship-to in Sage…
              </button>
            )}
          </div>
        </div>
      )}

      {/* Pipeline-rewrite notice — the deterministic pipeline automatically
          converted one or more extracted line values (item code or UOM
          quantity) between the email/PO and the persisted lines. Surfaced
          read-only as an informational notice so the rep can see what was
          changed. It does NOT gate or block submit-to-Sage. */}
      {order.pipeline_rewrites && order.pipeline_rewrites.length > 0 && (
        <div
          className={styles.warningBanner}
          data-testid="pipeline-rewrites-panel"
          role="status"
        >
          <span className={styles.warningIcon}>&#9888;</span>
          <div className={styles.warningText}>
            <div className={styles.warningTitle}>Automatic changes applied</div>
            <div style={{ marginBottom: '4px' }}>
              The system automatically converted these extracted values when the
              draft was created. They're shown here so you can see what changed.
            </div>
            {order.pipeline_rewrites.map((rw, i) => (
              <div key={i} data-testid="pipeline-rewrite-row">
                Line {rw.line_index + 1}
                {rw.item_code ? ` (${rw.item_code})` : ''}:{' '}
                {rw.field === 'item_code' ? 'item code' : 'quantity'} changed from{' '}
                <strong>{rw.before}</strong> to <strong>{rw.after}</strong>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No special pricing — neutral one-line note, not a warning (only
          ~6% of orders; standard pricing is the normal case for these
          customers). The per-line "No SP" chips are suppressed when this
          note shows, so the fact is stated exactly once. */}
      {pricingContext && !pricingContext.has_any_special_pricing && (
        <div
          data-testid="no-sp-note"
          style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 8px' }}
        >
          No special pricing on file for {pricingContext.customer_no} ({pricingContext.customer_name}) — standard catalog pricing applies.
        </div>
      )}

      {/* Summary badges */}
      {pricingContext && (pricingContext.line_items?.length ?? 0) > 0 && (
        <div className={styles.summaryRow}>
          {pricingContext.items_with_special_pricing > 0 && (
            <span className={`${styles.summaryBadge} ${styles.summaryBadgeGood}`}>
              {pricingContext.items_with_special_pricing} item{pricingContext.items_with_special_pricing !== 1 ? 's' : ''} with special pricing
            </span>
          )}
          {pricingContext.items_without_special_pricing > 0 && (
            <span className={`${styles.summaryBadge} ${styles.summaryBadgeWarn}`}>
              {pricingContext.items_without_special_pricing} item{pricingContext.items_without_special_pricing !== 1 ? 's' : ''} at standard price
            </span>
          )}
        </div>
      )}

      {/* Standing customer order notes (Sage AR_Customer.UDF_ORDER_NOTES).
          Carrier arrangements, carrier bans, packaging constraints — the
          instructions that used to surface only when the PO printed from
          Sage. Shown for every order status so nothing is discovered late. */}
      {orderDetail?.customer_order_notes && (
        <div
          style={{
            margin: '0 0 12px 0',
            padding: '10px 12px',
            background: '#fffbeb',
            border: '1px solid #f59e0b',
            borderRadius: '6px',
            fontSize: '13px',
            color: '#78350f',
            whiteSpace: 'pre-wrap',
          }}
          data-testid="customer-order-notes-banner"
        >
          <span style={{ fontWeight: 700, marginRight: '6px' }}>
            📌 Customer notes (from Sage):
          </span>
          {orderDetail.customer_order_notes}
        </div>
      )}

      {poIdentityWarning && (
        <HeaderIdentityWarningModal
          warning={poIdentityWarning.check}
          busy={poIdentityBusy}
          onCancel={() => {
            setPoIdentityWarning(null);
            setPoDraft(order.po_number || '');
          }}
          onSameOrder={() => { void savePoNumber(poIdentityWarning.pending, { differentOrder: false }); }}
          onDifferentOrder={(note) => { void savePoNumber(poIdentityWarning.pending, { differentOrder: true, note }); }}
        />
      )}

      {headerSaveError && (
        <div
          className={styles.addLineItemSuccess}
          role="alert"
          data-testid="header-save-error"
          style={{ background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', display: 'flex', alignItems: 'flex-start', gap: 6 }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }}>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
          <span style={{ flex: 1 }}>{headerSaveError}</span>
          <button
            type="button"
            onClick={() => setHeaderSaveError(null)}
            aria-label="Dismiss"
            style={{ border: 'none', background: 'none', color: '#991b1b', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}
          >
            &times;
          </button>
        </div>
      )}

      {/* Order Info */}
      <div className={styles.section} data-section="order_info" data-target-order-id={order.id}>
        <div className={styles.sectionTitle}>Order Info</div>
        <div className={styles.infoGrid} data-testid="order-info-grid">
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Salesperson</span>
            <span className={styles.infoValue}>
              {isDraft ? (
                <SearchableDropdown
                  value={order.salesperson_no || ''}
                  onChange={(next) => {
                    if (next) void handleSalespersonChange(next);
                  }}
                  items={salespersonItems}
                  placeholder="Select salesperson"
                  maxLength={4}
                  inputClassName={styles.inlineEditInput}
                  inputStyle={{ width: '180px' }}
                  testId="order-detail-salesperson"
                  record={{ action: 'order_salesperson_pick', valueKey: 'salesperson_no', targets: { 'order-id': order.id } }}
                />
              ) : (
                resolveSalespersonDisplay(
                  order.salesperson_name,
                  order.salesperson_no,
                  salespersonOptions,
                )
              )}
            </span>
          </div>
          {/* The in-house ChemMasters salesperson the order's rep code reports
              to (regional_rep_mappings, same source as the customers list's
              "Regional Resp." column). Half of orders carry an outside agency
              code, so the CSR otherwise cannot tell whose account it is.
              Resolved from the salespersons reference list so it follows the
              dropdown on drafts without a second request (kanban 0a59332f). */}
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Regional Resp.</span>
            <span
              className={styles.infoValue}
              data-testid="order-detail-regional-responsible"
              title="Regional responsible: the in-house salesperson this rep code reports to"
            >
              {resolveRegionalResponsible(order.salesperson_no, salespersonOptions) ?? '—'}
            </span>
          </div>
          {/* The subtotal sums line_total, which is NULL until a line has a
              price, so it silently leaves unpriced lines out. Say so next to
              the amount, and show a total once freight / surcharge / tax are
              known rather than letting "Subtotal" pass for the order's cost
              (owner-approved finding N5, review aa4424a4). Display only. */}
          {/* The qualifier sits on its own line under the amount: inline after
              the label it wrapped mid-phrase at the grid's column width
              (kanban 081dfb99). */}
          <div className={`${styles.infoRow} ${styles.infoRowStacked}`}>
            <span className={styles.infoLabel}>Subtotal</span>
            <span className={styles.infoValue} data-testid="order-subtotal">
              ${order.subtotal}
              <UnpricedLinesMarker count={unpricedLineCount} placement="order-header" />
            </span>
            {totalView.kind === 'subtotal' && (
              <span className={styles.infoRowHint} data-testid="order-subtotal-hint">
                before {joinComponents(totalView.pending)}
              </span>
            )}
          </div>
          {totalView.kind === 'total' && (
            <div
              // .infoRowTotal pins it to the right column so Total sits directly
              // under Subtotal; the pin lifts on a phone's single-column grid.
              className={`${styles.infoRow} ${styles.infoRowStacked} ${styles.infoRowTotal}`}
              data-testid="order-total-row"
            >
              <span className={styles.infoLabel}>Total</span>
              <span className={styles.infoValue} data-testid="order-total">
                {formatPrice(totalView.amount)}
                <UnpricedLinesMarker count={unpricedLineCount} placement="order-total" />
              </span>
              <span className={styles.infoRowHint} data-testid="order-total-hint">
                {totalView.fromInvoice ? 'from the invoice' : 'incl.'}
                {totalView.included.length > 0 && (
                  <>
                    {totalView.fromInvoice ? ': ' : ' '}
                    {totalView.included
                      .map((c) => `${c.component} ${formatPrice(c.amount)}`)
                      .join(', ')}
                  </>
                )}
                {totalView.pending.length > 0 && (
                  <> · before {joinComponents(totalView.pending)}</>
                )}
              </span>
            </div>
          )}
          {/* The 5% surcharge as an explicit /SURCHG line the desk sets per
              order; its amount overrides the invoice-time surcharge downstream
              (kanban 51aaddc5). A tiny "Surcharge override" disclosure, closed
              by default — most orders never touch it (owner, 2026-10-02).
              Read-only for orders:read viewers and for orders the portal can
              no longer change. */}
          <div className={styles.infoRow} style={{ gridColumn: '1 / -1', alignItems: 'flex-start', padding: '2px 0' }}>
            <span className={styles.infoValue} style={{ flex: 1, minWidth: 0 }}>
              <OrderSurchargeControl
                orderId={order.id}
                canEdit={canEdit}
                lineItemsKey={orderDetail.line_items
                  .map((li) => `${li.id}:${li.item_code}:${li.quantity}:${li.unit_price}`)
                  .join('|')}
                formatPrice={formatPrice}
                onChanged={(resp) => onSurchargeChanged?.(resp)}
              />
            </span>
          </div>
          {order.payment_terms && (
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Terms</span>
              <span className={styles.infoValue}>{order.payment_terms}</span>
            </div>
          )}
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>PO Number</span>
            <span className={styles.infoValue}>
              {isDraft ? (
                <input
                  type="text"
                  value={poDraft}
                  onChange={(e) => setPoDraft(e.target.value)}
                  onBlur={() => { void commitPoNumber(); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                  }}
                  className={styles.inlineEditInput}
                  placeholder="Enter PO number"
                  style={{ width: '160px' }}
                  data-testid="order-detail-po-input"
                />
              ) : (
                order.po_number || '-'
              )}
            </span>
          </div>
          {/* Per-order buyer contact(s): who gets the confirmation + invoice copy
              (kanban 67b70825). Editable in every live status — a buyer can be
              added after submit so the invoice copy still goes out. */}
          <div
            className={styles.infoRow}
            style={{ gridColumn: '1 / -1', alignItems: 'flex-start' }}
            data-testid="order-contacts-row"
          >
            <span className={styles.infoLabel} style={{ paddingTop: 4, marginRight: 12, whiteSpace: 'nowrap' }}>
              Confirmation contacts
            </span>
            <span className={styles.infoValue} style={{ flex: 1, minWidth: 0 }}>
              <OrderContactsField
                orderId={order.id}
                customerNo={order.customer_no}
                shipToCode={
                  typeof order.ship_to_address?.ship_to_code === 'string'
                    ? order.ship_to_address.ship_to_code
                    : null
                }
                contacts={order.notification_contacts ?? []}
                canEdit={canEdit && order.status !== 'cancelled'}
                onSaved={(contacts) =>
                  onOrderUpdated?.({ ...order, notification_contacts: contacts })
                }
              />
            </span>
          </div>
          {duplicatePoOrders.length > 0 && (
            <div className={styles.infoRow} style={{ gridColumn: '1 / -1' }}>
              <DuplicatePoWarning
                duplicates={duplicatePoOrders}
                onViewOrder={onViewDuplicateOrder}
              />
            </div>
          )}
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Requested Ship Date</span>
            <span className={styles.infoValue}>
              {isDraft ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {!order.requested_ship_date ? (
                    <button
                      type="button"
                      onClick={() => {
                        handleSageFieldChange('requested_ship_date', todayIsoDate());
                      }}
                      style={{
                        background: 'none',
                        border: '1px dashed #d1d5db',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        color: '#6b7280',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      + Add ship date
                    </button>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <input
                          type="date"
                          min={shipDateBounds().min}
                          max={shipDateBounds().max}
                          defaultValue={order.requested_ship_date || ''}
                          onBlur={(e) => {
                            const val = e.target.value;
                            if (val !== (order.requested_ship_date || '')) {
                              handleSageFieldChange('requested_ship_date', val);
                              if (!val) {
                                handleSageFieldChange('requested_ship_date_confirmed', false);
                              }
                            }
                          }}
                          className={styles.inlineEditInput}
                          style={{ width: '160px' }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            handleSageFieldChange('requested_ship_date', '');
                            handleSageFieldChange('requested_ship_date_confirmed', false);
                          }}
                          style={{
                            background: 'none',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            padding: '2px 8px',
                            fontSize: '12px',
                            color: '#6b7280',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                          title="Clear ship date"
                        >
                          ✕ Clear
                        </button>
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: order.requested_ship_date_confirmed ? '#059669' : '#d97706' }}>
                        <input
                          type="checkbox"
                          checked={order.requested_ship_date_confirmed}
                          onChange={(e) => handleSageFieldChange('requested_ship_date_confirmed', e.target.checked)}
                          className={styles.inlineCheckbox}
                        />
                        {order.requested_ship_date_confirmed ? 'Customer confirmed' : 'Unconfirmed — please confirm or clear this date'}
                      </label>
                      {order.llm_extraction_data?.udf_required_by_evidence && order.requested_ship_date === order.llm_extraction_data?.udf_required_by && (
                        <span data-testid="ship-date-evidence" style={{ fontSize: '11px', color: '#6b7280' }} title="Where the extractor read this date on the PO">
                          From PO: “{order.llm_extraction_data.udf_required_by_evidence}”
                        </span>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <span>
                  {order.requested_ship_date ? formatDate(order.requested_ship_date) : '-'}
                  {order.requested_ship_date && !order.requested_ship_date_confirmed && (
                    <span style={{ color: '#d97706', fontSize: '12px', marginLeft: '6px' }} title="Customer did not explicitly request this date">⚠ Unconfirmed</span>
                  )}
                  {order.requested_ship_date && order.requested_ship_date_confirmed && (
                    <span style={{ color: '#059669', fontSize: '12px', marginLeft: '6px' }} title="Customer explicitly requested this date">✓ Confirmed</span>
                  )}
                  {order.llm_extraction_data?.udf_required_by_evidence && order.requested_ship_date === order.llm_extraction_data?.udf_required_by && (
                    <span data-testid="ship-date-evidence" style={{ fontSize: '11px', color: '#6b7280', marginLeft: '6px' }}>From PO: “{order.llm_extraction_data.udf_required_by_evidence}”</span>
                  )}
                </span>
              )}
            </span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Jobsite</span>
            <span className={styles.infoValue}>
              {isDraft ? (
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={order.udf_jobsite === 'Y'}
                    onChange={(e) => handleSageFieldChange('udf_jobsite', e.target.checked ? 'Y' : 'N')}
                    className={styles.inlineCheckbox}
                  />
                  {order.udf_jobsite === 'Y' ? 'Yes' : 'No'}
                </label>
              ) : (
                order.udf_jobsite === 'Y' ? 'Yes' : 'No'
              )}
            </span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Backorder</span>
            <span className={styles.infoValue}>
              {isDraft ? (
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={order.order_type === 'B'}
                    onChange={(e) => handleSageFieldChange('order_type', e.target.checked ? 'B' : 'S')}
                    className={styles.inlineCheckbox}
                    data-testid="order-detail-backorder"
                  />
                  {order.order_type === 'B' ? 'Yes' : 'No'}
                </label>
              ) : (
                order.order_type === 'B' ? 'Yes' : 'No'
              )}
              {order.backorder_of_order_id && (
                <button
                  type="button"
                  data-testid="backorder-view-original"
                  onClick={() => onViewDuplicateOrder(order.backorder_of_order_id!)}
                  style={{
                    background: 'none', border: 'none', color: '#2563eb',
                    cursor: 'pointer', fontSize: '12px', marginLeft: '8px',
                    textDecoration: 'underline', padding: 0,
                  }}
                >
                  view original order
                </button>
              )}
            </span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Warehouse</span>
            <span className={styles.infoValue}>
              {isDraft ? (
                <SearchableDropdown
                  value={order.warehouse_code || '000'}
                  onChange={(next) => {
                    const val = next || '000';
                    if (val !== (order.warehouse_code || '000')) {
                      handleSageFieldChange('warehouse_code', val);
                    }
                  }}
                  items={warehouseItems}
                  placeholder="000"
                  maxLength={3}
                  inputClassName={styles.inlineEditInput}
                  inputStyle={{ width: '140px' }}
                  testId="order-detail-warehouse"
                  record={{ action: 'order_warehouse_pick', valueKey: 'warehouse_code', targets: { 'order-id': order.id } }}
                />
              ) : (
                (() => {
                  const code = order.warehouse_code || '000';
                  const match = warehouseItems.find((w) => w.value === code);
                  return match ? `${code} — ${match.label}` : code;
                })()
              )}
              {(() => {
                // Ship-from provenance (kanban b5ba3619 / 29601f9c): Merlin's
                // PO evidence, the will-call-history fill or the item-history
                // fill, shown while the order still carries that code so the
                // CSR knows why it is not 000.
                const prov = warehouseProvenance(order.warehouse_code, order.llm_extraction_data);
                return prov ? (
                  <span
                    data-testid="warehouse-provenance"
                    data-source={prov.source}
                    style={{ fontSize: '11px', color: '#6b7280', marginLeft: '6px', whiteSpace: 'nowrap' }}
                    title={prov.title}
                  >
                    {prov.text}
                  </span>
                ) : null;
              })()}
            </span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>
              Ship Via
              {isDraft && (
                <span
                  style={{ color: '#9ca3af', marginLeft: 2 }}
                  title="Required before submitting to Sage"
                  aria-hidden="true"
                >
                  *
                </span>
              )}
            </span>
            <span className={styles.infoValue}>
              {order.cancel_reason_code === 'FRT' && (
                <span
                  style={{
                    display: 'inline-block',
                    marginRight: '8px',
                    background: '#fff7ed',
                    border: '1px solid #fb923c',
                    color: '#9a3412',
                    borderRadius: '4px',
                    padding: '2px 8px',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px',
                  }}
                  title='Order is on Sage hold "FRT — Customer frt chg sign off". Ops will attach a TL/PTL freight quote and clear the hold.'
                  data-testid="truckload-pending-badge"
                >
                  Truckload pending
                </span>
              )}
              {isDraft ? (
                <>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      ...(showFreightFieldError
                        ? {
                            border: '1px solid #dc2626',
                            borderRadius: '6px',
                            padding: '4px',
                            background: '#fef2f2',
                          }
                        : {}),
                    }}
                    data-testid="ship-via-field"
                  >
                    <SearchableDropdown
                      value={order.ship_via || ''}
                      onChange={(next) => {
                        if (next !== (order.ship_via || '')) {
                          handleSageFieldChange('ship_via', next);
                        }
                      }}
                      items={shipViaItems}
                      placeholder="Select carrier..."
                      maxLength={15}
                      inputClassName={styles.inlineEditInput}
                      inputStyle={{ width: '150px' }}
                      highlightedLabel="Carriers"
                      otherLabel="Other"
                      testId="order-detail-ship-via"
                      record={{ action: 'order_ship_via_pick', valueKey: 'ship_via', targets: { 'order-id': order.id } }}
                      invalid={showFreightFieldError}
                    />
                    {order.ship_via && (
                      <button
                        type="button"
                        onClick={() => handleSageFieldChange('ship_via', '')}
                        style={{
                          background: 'none',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          padding: '2px 8px',
                          fontSize: '12px',
                          color: '#6b7280',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                        title="Clear ship via"
                        data-testid="order-detail-ship-via-clear"
                      >
                        ✕ Clear
                      </button>
                    )}
                    <button
                      onClick={() => setShowFreightModal(true)}
                      style={{
                        background: '#3b82f6',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '11px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                      title="Get live freight quotes"
                      data-action="freight_quotes_open"
                      data-outcome="open"
                      data-target-order-id={order.id}
                      data-record-value="mode"
                      value="edit"
                      data-testid="freight-quotes-btn"
                    >
                      Get Quotes
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>{order.ship_via || '—'}</span>
                  {/* Quote fetch needs orders:manage (backend gate on the
                      freight-quotes endpoint) — hide for read-only viewers. */}
                  {canEdit && (
                  <button
                    onClick={() => setShowFreightModal(true)}
                    style={{
                      background: 'none',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      padding: '2px 8px',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#374151',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                    title="View live freight quote estimates (view only — the carrier is already set in Sage)"
                    data-action="freight_quotes_open"
                    data-outcome="open"
                    data-target-order-id={order.id}
                    data-record-value="mode"
                    value="view"
                    data-testid="freight-quotes-btn"
                  >
                    View Quotes
                  </button>
                  )}
                </div>
              )}
            </span>
          </div>
          {!isDraft && orderDetail.freight_needs_input && (
            <div
              data-testid="freight-needs-input-banner"
              role="alert"
              style={{
                margin: '6px 0 10px',
                padding: '10px 12px',
                borderRadius: 6,
                background: '#fffbeb',
                border: '1px solid #fcd34d',
                color: '#92400e',
                fontSize: '13px',
                lineHeight: 1.45,
              }}
            >
              <strong>Freight needed to finish this shipment.</strong>{' '}
              {orderDetail.freight_needs_input.sage_order_no}:{' '}
              {orderDetail.freight_needs_input.why}. Enter it on the Freight
              row below and save, or key the freight on the Sage sales order;
              the scan re-runs on its own within about ten minutes.
            </div>
          )}
          {!isDraft && orderDetail.bol_scan_desk_ask && (
            <BolScanDeskAskBanner
              orderId={order.id}
              ask={orderDetail.bol_scan_desk_ask}
              canEdit={canEdit}
            />
          )}
          {!isDraft && orderDetail.freight_error && (
            <div
              data-testid="freight-error-banner"
              role="alert"
              style={{
                margin: '6px 0 10px',
                padding: '10px 12px',
                borderRadius: 6,
                background: '#fef2f2',
                border: '1px solid #fca5a5',
                color: '#991b1b',
                fontSize: '13px',
                lineHeight: 1.45,
              }}
            >
              <strong>Freight not added at BOL scan.</strong> The{' '}
              {orderDetail.freight_error.ship_via ?? 'carrier'} rate lookup failed
              when this order was scanned, so the Sage invoice was built with $0
              freight.{' '}
              {orderDetail.truckload_freight ? (
                <span data-testid="freight-error-truckload-remedy">
                  A freight amount on file already shows{' '}
                  <strong>{formatPrice(orderDetail.truckload_freight.total)}</strong>
                  {Number(orderDetail.truckload_freight.fuel) > 0 && (
                    <>
                      {' '}({formatPrice(orderDetail.truckload_freight.freight)} freight +{' '}
                      {formatPrice(orderDetail.truckload_freight.fuel)} fuel)
                    </>
                  )}{' '}
                  — key that amount on the Sage invoice before the batch posts.
                </span>
              ) : (
                <>
                  Pull the freight from{' '}
                  {orderDetail.freight_error.ship_via ?? 'the carrier'}&apos;s site
                  {orderDetail.freight_error.tracking_number
                    ? ` (PRO ${orderDetail.freight_error.tracking_number})`
                    : ''}
                  , enter it on the Freight row below, and key it on the Sage
                  invoice before the batch posts.
                </>
              )}{' '}
              This notice clears itself once Sage shows freight for the order.
            </div>
          )}
          {(!isDraft || orderDetail.truckload_freight_eligible || freightRecommendation != null) && (
            <div className={styles.infoRow} data-testid="sage-freight-row">
              <span
                className={styles.infoLabel}
                // The truckload control stacks several lines; keep the label
                // on the first one instead of floating mid-row.
                style={orderDetail.truckload_freight_eligible ? { alignSelf: 'flex-start', paddingTop: 2 } : undefined}
              >
                Freight
              </span>
              <span
                className={styles.infoValue}
                style={orderDetail.truckload_freight_eligible ? { paddingLeft: 16, textAlign: 'left' } : undefined}
              >
                {/* Any carrier, drafts included (owner ruling 2026-10-07): on a
                    truckload carrier the desk keys the broker's freight + fuel;
                    on a live-rated carrier the amount overrides the quote. */}
                {orderDetail.truckload_freight_eligible && (
                  <TruckloadFreightControl
                    orderId={order.id}
                    canEdit={canEdit}
                    locked={orderDetail.invoice != null}
                    truckload={orderDetail.truckload_carrier ?? true}
                    summary={orderDetail.truckload_freight ?? null}
                    needsInput={orderDetail.freight_needs_input ?? null}
                    freightError={orderDetail.freight_error ?? null}
                    formatPrice={formatPrice}
                    onChanged={(summary) => onTruckloadFreightChanged?.(summary)}
                  />
                )}
                {!isDraft && (() => {
                  // Prefer the invoiced amount once the order ships; before
                  // that, the open-SO mirror carries Sage's computed FreightAmt.
                  const invoiceAmt = orderDetail.invoice?.freight_amt;
                  const sageAmt = orderDetail.sage_freight?.freight_amt;
                  const amt = invoiceAmt ?? sageAmt;
                  // The open-SO mirror carries an explicit 0.00 (not null) after
                  // a failed scan-time quote, so treat $0 the same as unset here.
                  const presetTotal =
                    orderDetail.truckload_freight != null
                      ? Number(orderDetail.truckload_freight.total)
                      : null;
                  const wrap = (node: React.ReactNode) =>
                    orderDetail.truckload_freight_eligible ? (
                      <div data-testid="sage-freight-status" style={{ marginTop: 4, fontSize: '12px' }}>
                        Sage: {node}
                      </div>
                    ) : (
                      node
                    );
                  if (orderDetail.freight_error && (amt == null || Number(amt) === 0)) {
                    if (presetTotal != null) {
                      return wrap(
                        <span style={{ color: '#b91c1c', fontWeight: 600 }} data-testid="truckload-freight-on-file">
                          $0 in Sage — key the {formatPrice(presetTotal)} freight on file
                        </span>,
                      );
                    }
                    return wrap(
                      <span style={{ color: '#b91c1c', fontWeight: 600 }}>
                        $0 — carrier quote failed at scan, key manually
                      </span>,
                    );
                  }
                  if (amt == null) {
                    if (presetTotal != null) {
                      // Amount keyed on the order — Sage doesn't carry it until
                      // BOL scan / the sales-site push lands it on the invoice.
                      return wrap(
                        <span style={{ color: '#9ca3af' }} data-testid="truckload-freight-on-file">
                          not set yet — {formatPrice(presetTotal)} on file for BOL scan
                        </span>,
                      );
                    }
                    return wrap(
                      <span
                        style={{ color: '#9ca3af' }}
                        title="Sage hasn't computed a freight amount for this order yet — use View Quotes for live estimates."
                      >
                        Not set in Sage yet
                      </span>,
                    );
                  }
                  const source = invoiceAmt != null ? 'invoiced' : 'from Sage';
                  return wrap(
                    <span>
                      {formatPrice(amt)}
                      <span style={{ color: '#6b7280', marginLeft: 6, fontSize: '12px' }}>
                        ({source})
                      </span>
                    </span>,
                  );
                })()}
                {/* Merlin's evidence-backed freight concession (kanban 8d83c042):
                    ONE quiet inline chip after the amount, only when a written
                    source names a concession for THIS order. Accept writes a
                    real /FREIGHTDIS charge line; nothing applies on its own. */}
                {freightRecommendation != null && (
                  <MerlinRecommendationChip
                    recommendation={freightRecommendation}
                    orderId={order.id}
                    canEdit={canEdit}
                    formatPrice={formatPrice}
                    onDecide={async (action, appliedValue) => {
                      await portalOrderService.decideRecommendation(order.id, freightRecommendation.id, {
                        action,
                        applied_value: appliedValue ?? null,
                      });
                      onRecommendationDecided?.();
                    }}
                  />
                )}
              </span>
            </div>
          )}
          <FreightQuoteModal
            isOpen={showFreightModal}
            onClose={() => setShowFreightModal(false)}
            readOnly={!isDraft}
            onSelect={(carrier) => {
              // Selecting an LTL carrier clears the FRT freight-pending
              // hold (if set) so the two states stay mutually exclusive.
              // Guarded: read-only rows can't fire this, but never write
              // ship_via on a non-draft order regardless.
              if (!isDraft) return;
              if (order.cancel_reason_code === 'FRT') {
                handleSageFieldChange('cancel_reason_code', null);
              }
              handleSageFieldChange('ship_via', carrier);
              setShowFreightModal(false);
            }}
            orderId={order.id}
            currentShipVia={order.ship_via}
            isTruckloadPending={order.cancel_reason_code === 'FRT'}
            onMarkTruckload={() => {
              // The "truckload-pending" intent is recorded as Sage's FRT
              // hold code — that way the order shows on hold in Sage with
              // description "Customer frt chg sign off" and ops sees the
              // same state we do. Clear any previously-selected LTL ship_via
              // since they're mutually exclusive.
              if (order.ship_via) {
                handleSageFieldChange('ship_via', null);
              }
              handleSageFieldChange('cancel_reason_code', 'FRT');
            }}
          />
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Bill of Lading</span>
            <span className={styles.infoValue}>
              {isDraft ? (
                <select
                  value={order.udf_bl || 'ppd.'}
                  onChange={(e) => handleSageFieldChange('udf_bl', e.target.value)}
                  className={styles.inlineEditInput}
                  style={{ width: '120px' }}
                >
                  {BILL_OF_LADING_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              ) : (
                <span title="Set in Sage; change it from Edit in Sage.">{billOfLadingLabel(order.udf_bl)}</span>
              )}
            </span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Charge Freight</span>
            <span className={styles.infoValue}>
              {order.udf_bl === 'col.' || order.udf_bl === '3rd party' ? (
                <span title="Collect / 3rd-party shipments bill the carrier to the customer or a third party directly — ChemMasters never bills freight on them.">
                  N/A — customer pays carrier
                </span>
              ) : canEdit && (isDraft || orderDetail.editable_in_sage) && orderDetail.invoice == null ? (
                <label
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: chargeFreightBusy ? 'wait' : 'pointer' }}
                  title="Uncheck to waive freight: a DO NOT BILL FREIGHT comment is kept on the Sage order for the invoicing desk."
                >
                  <input
                    type="checkbox"
                    checked={order.charge_freight !== false}
                    disabled={chargeFreightBusy}
                    onChange={(e) => handleChargeFreightToggle(e.target.checked)}
                    data-testid="order-detail-charge-freight"
                  />
                  {order.charge_freight !== false ? 'Bill freight when invoiced' : 'Waived — do not bill freight'}
                  <span
                    data-testid="order-detail-charge-freight-waive-hint"
                    style={{ display: 'block', fontSize: 11, color: '#6b7280', fontWeight: 400 }}
                  >
                    {order.charge_freight !== false
                      ? 'Uncheck to waive freight (Sage gets a DO NOT BILL FREIGHT note).'
                      : 'Check to bill freight again.'}
                  </span>
                </label>
              ) : (
                <span data-testid="order-detail-charge-freight-readonly">
                  {order.charge_freight !== false
                    ? 'Bill freight when invoiced'
                    : 'Waived — do not bill freight'}
                  {orderDetail.invoice != null ? ' (invoiced — locked)' : ''}
                </span>
              )}
            </span>
          </div>
          {chargeFreightMessage && (
            <div
              data-testid="order-detail-charge-freight-message"
              style={{
                fontSize: '12px',
                padding: '4px 0 2px',
                color: chargeFreightMessage.kind === 'ok' ? 'var(--color-success, #1a7f37)' : 'var(--color-danger, #b42318)',
              }}
            >
              {chargeFreightMessage.text}
            </div>
          )}
          {order.charge_freight === false
            && order.udf_bl !== 'col.' && order.udf_bl !== '3rd party' && (
            <div
              data-testid="order-detail-charge-freight-hint"
              style={{ fontSize: '12px', padding: '2px 0 4px', color: 'var(--color-text-muted, #6b7280)' }}
            >
              The order still ships via the selected carrier — the freight cost
              just isn't billed to the customer.
            </div>
          )}
          {order.charge_freight === false
            && order.udf_bl !== 'col.' && order.udf_bl !== '3rd party'
            && orderDetail.invoice == null && (
            <div
              data-testid="order-detail-charge-freight-waiver-push"
              data-status={waiverPush?.status ?? 'none'}
              style={{
                fontSize: '12px',
                padding: '0 0 4px',
                color: waiverPush && (waiverPush.status === 'applied' || waiverPush.status === 'applied_test')
                  ? 'var(--color-success, #1a7f37)'
                  : 'var(--color-text-muted, #6b7280)',
              }}
            >
              {waiverPush && waiverPush.waived
                ? `Sales site: ${waiverPush.message}`
                : 'Sales site: the waiver has not been sent as data yet — '
                  + 'the desk is alerted at BOL scan if the invoice carries freight.'}
            </div>
          )}
          {/* Hold Reason: drafts store the code locally (rides the submission);
              submitted orders still open in Sage set/change/release the hold in
              live Sage and show what Sage currently has underneath. */}
          <HoldReasonField
            order={order}
            canEdit={canEdit}
            isDraft={isDraft}
            editableInSage={!!orderDetail.editable_in_sage && orderDetail.invoice == null}
            cancelReasonCodes={cancelReasonCodes}
            onDraftChange={(code) => handleSageFieldChange('cancel_reason_code', code)}
            onOrderUpdated={onOrderUpdated}
          />
          {order.sage_order_no && (
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Sage Order #</span>
              <span className={styles.infoValue}>{order.sage_order_no}</span>
            </div>
          )}
          {order.confidence_bucket && (
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>AI Confidence</span>
              <ConfidenceBadge bucket={order.confidence_bucket} />
            </div>
          )}
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Created</span>
            <span className={styles.infoValue}>{formatDate(order.created_at)}</span>
          </div>
        </div>
      </div>

      {/* Buyer notifications — what was emailed to the confirmation contacts */}
      <div className={styles.section} data-testid="order-notifications-section">
        <div className={styles.sectionTitle}>Buyer notifications</div>
        <OrderNotificationsPanel
          orderId={order.id}
          canManage={canEdit}
          refreshKey={(order.notification_contacts ?? []).map((c) => c.email).join(',')}
        />
      </div>

      {/* Invoice — most-recent Sage invoice for this order, when present */}
      {orderDetail.invoice && (
        <OrderInvoiceSection invoice={orderDetail.invoice} />
      )}

      {/* Backorder prompts — inline offers after a quantity reduction */}
      {backorderPrompts.length > 0 && (
        <div data-testid="backorder-prompts">
          {backorderPrompts.map((bp) => {
            const createdId = acceptedBackorderIds.get(bp.id) ?? bp.created_backorder_order_id;
            const accepted = bp.status === 'accepted' && createdId;
            return (
              <div
                key={bp.id}
                data-testid={`backorder-prompt-${bp.id}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                  background: accepted ? '#ecfdf5' : '#fffbeb',
                  border: `1px solid ${accepted ? '#6ee7b7' : '#fcd34d'}`,
                  borderRadius: '8px', padding: '10px 14px', margin: '0 0 10px',
                  fontSize: '13px',
                }}
              >
                {accepted ? (
                  // Deliberately NO navigation affordance here — pulling the
                  // CSR into the new draft mid-task risks the original order
                  // being left unfinished (2026-06-11 review feedback). The
                  // backorder waits in the Orders list with a BACKORDER badge.
                  <span data-testid={`backorder-created-${bp.id}`} style={{ color: '#065f46' }}>
                    ✓ Backorder draft created for <b>{bp.item_code}</b> (qty {bp.accepted_qty ?? bp.remaining_qty}).
                    {' '}Finish this order first — the new draft is waiting in your Orders list
                    {' '}(marked <b>BACKORDER</b>) to review afterwards.
                  </span>
                ) : (
                  <>
                    <span style={{ color: '#92400e' }}>
                      Quantity for <b>{bp.item_code}</b> reduced {bp.qty_before} → {bp.qty_after}.
                      Create a backorder for the remaining <b>{bp.remaining_qty}</b>?
                    </span>
                    {canEdit && (() => {
                      const draftQty = backorderQtyDrafts.get(bp.id) ?? String(parseFloat(bp.remaining_qty));
                      const parsed = parseFloat(draftQty);
                      const qtyValid = Number.isFinite(parsed) && parsed > 0;
                      return (
                        <>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12.5px', color: '#92400e' }}>
                            Qty
                            <input
                              type="number"
                              min="0"
                              step="any"
                              value={draftQty}
                              data-testid={`backorder-qty-${bp.id}`}
                              onChange={(e) => {
                                const v = e.target.value;
                                setBackorderQtyDrafts((prev) => {
                                  const next = new Map(prev);
                                  next.set(bp.id, v);
                                  return next;
                                });
                              }}
                              style={{
                                width: '70px', padding: '3px 6px', fontSize: '13px',
                                border: `1px solid ${qtyValid ? '#d1d5db' : '#dc2626'}`,
                                borderRadius: '6px',
                              }}
                            />
                          </label>
                          <button
                            type="button"
                            data-testid={`backorder-accept-${bp.id}`}
                            disabled={backorderPromptBusy === bp.id || !qtyValid}
                            onClick={() => onAcceptBackorderPrompt?.(order.id, bp.id, draftQty)}
                            style={{
                              background: qtyValid ? '#2563eb' : '#93c5fd', color: '#fff', border: 'none',
                              borderRadius: '6px', padding: '4px 12px',
                              cursor: qtyValid ? 'pointer' : 'not-allowed', fontSize: '13px',
                            }}
                          >
                            Create backorder
                          </button>
                        </>
                      );
                    })()}
                    {canEdit && <button
                      type="button"
                      data-testid={`backorder-decline-${bp.id}`}
                      disabled={backorderPromptBusy === bp.id}
                      onClick={() => onDeclineBackorderPrompt?.(order.id, bp.id)}
                      style={{
                        background: 'none', color: '#6b7280', border: '1px solid #d1d5db',
                        borderRadius: '6px', padding: '4px 12px', cursor: 'pointer', fontSize: '13px',
                      }}
                    >
                      No backorder needed
                    </button>}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Review submit-gate banner — the answer cards live INLINE under
          their own line rows below (amber "N issues" chip on each flagged
          line, minimized at first; reviewer feedback 2026-07: the row
          already shows what we extracted). This banner only makes the
          blocked state unmissable. Draft orders only. */}
      {order.status === 'draft' && (() => {
        const summary = attentionSummary(orderDetail.line_items);
        if (summary.toAnswer === 0) return null;
        return (
          <div
            className={styles.section}
            data-testid="review-answer-banner"
            style={{
              borderLeft: '4px solid #f59e0b',
              background: '#fffbeb',
              borderRadius: '6px',
              padding: '8px 14px',
            }}
          >
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#92400e' }}>
              {summary.toAnswer} line{summary.toAnswer === 1 ? '' : 's'} in the
              table below {summary.toAnswer === 1 ? 'needs' : 'need'} your
              answer before this order can be submitted.
            </div>
            <div style={{ fontSize: '12px', color: '#a16207' }}>
              Everything else matched and was auto-checked — click the amber
              issues chip on a flagged line to answer it.
            </div>
          </div>
        );
      })()}

      {/* Expired-quote salesperson alerts (2026-08-26 flow): the CSR sees
          who was notified and when, decides whether to wait, and sees the
          outcome here (plus a bell notification) once the salesperson
          replies. Refresh re-pulls the pricing context. */}
      {pricingContext?.expired_quote_alerts?.map((alert) => {
        const minutesAgo = (iso: string | null): string => {
          if (!iso) return '';
          const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
          if (mins < 60) return `${mins}m ago`;
          const hours = Math.floor(mins / 60);
          return hours < 48 ? `${hours}h ${mins % 60}m ago` : `${Math.floor(hours / 24)}d ago`;
        };
        const respondedIn = (secs: number | null): string => {
          if (secs == null) return '';
          const mins = Math.round(secs / 60);
          return mins < 60 ? `responded in ${mins}m` : `responded in ${Math.floor(mins / 60)}h ${mins % 60}m`;
        };
        const who = alert.salesperson_name || alert.salesperson_no || 'Salesperson';
        const palette: Record<string, { bg: string; border: string; color: string }> = {
          approved: { bg: 'rgba(34,197,94,0.08)', border: '#22c55e', color: '#15803d' },
          denied: { bg: '#fdedec', border: '#c0392b', color: '#c0392b' },
          send_failed: { bg: '#fdedec', border: '#c0392b', color: '#c0392b' },
        };
        // After a denial the Sage price is re-checked; a confirmed change
        // turns the red "adjust it" line into a green "done" one.
        const denialFixed = alert.status === 'denied' && alert.denial_followthrough === 'resolved';
        const tone = denialFixed
          ? palette.approved
          : palette[alert.status] ?? { bg: 'rgba(251,191,36,0.10)', border: '#d97706', color: '#92400e' };
        let text: string;
        const standing = alert.approval_source === 'standing_rule' || alert.approval_source === 'standing_prior_approval';
        // The conversation is live: how many Merlin↔salesperson messages have
        // crossed, and how recently — so the CSR's wait/don't-wait call sees
        // that a back-and-forth is under way, not just a one-shot alert.
        const replies = alert.salesperson_replies ?? 0;
        const merlinMsgs = alert.merlin_messages ?? 0;
        const conversation = replies > 0
          ? ` ${who} has replied ${replies === 1 ? 'once' : `${replies} times`} and Merlin has answered on the thread (${merlinMsgs} message${merlinMsgs === 1 ? '' : 's'}${alert.last_message_at ? `, last ${minutesAgo(alert.last_message_at)}` : ''}).`
          : '';
        if (alert.status === 'approved' && alert.approval_source === 'quote_renewed') {
          // The salesperson renewed the quote on the sales site instead of
          // replying: the order's price is a live quote price again.
          const onDate = (iso: string | null | undefined): string =>
            iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
          const renewedOn = onDate(alert.renewed_on);
          const validThrough = onDate(alert.renewed_valid_through);
          text = `Expired quote #${alert.quote_source_id}: ${who} renewed the quote on the sales site${renewedOn ? ` on ${renewedOn}` : ''}${validThrough ? ` (good through ${validThrough})` : ''}, so the price is a live quote price again. Settled; no price change needed.`;
        } else if (alert.status === 'approved') {
          text = standing
            ? `Expired quote #${alert.quote_source_id} price auto-approved by Merlin — a standing rule ${who} set covers this quote for this customer.`
            : `Expired quote #${alert.quote_source_id} price APPROVED by ${who}${alert.response_seconds != null ? ` (${respondedIn(alert.response_seconds)})` : ''}. No price change needed.`;
        } else if (alert.status === 'denied') {
          const head = standing
            ? `Expired quote #${alert.quote_source_id} price auto-DENIED by Merlin (a standing rule ${who} set says not to honor it)`
            : `Expired quote #${alert.quote_source_id} price DENIED by ${who}${alert.response_seconds != null ? ` (${respondedIn(alert.response_seconds)})` : ''}`;
          const usd = (v: number | string) => `$${Number(v).toFixed(2)}`;
          const lines = alert.denial_lines ?? [];
          const stillDenied = lines.filter((l) => l.unchanged)
            .map((l) => `${l.item_code} ${usd(l.denied_price)}`).join(', ');
          const nowPriced = lines.filter((l) => !l.unchanged)
            .map((l) => (l.sage_prices.length ? `${l.item_code} now ${l.sage_prices.map(usd).join(' / ')}` : `${l.item_code} no longer on the order`))
            .join(', ');
          switch (alert.denial_followthrough) {
            case 'resolved':
              text = `${head}. Price changed in Sage${nowPriced ? ` (${nowPriced})` : ''}. Nothing more to do.`;
              break;
            case 're_alerted':
              text = `${head}. Sage still has the denied price${stillDenied ? ` (${stillDenied})` : ''}. Adjust it BEFORE invoicing.`;
              break;
            case 'invoiced_unchanged':
              text = `${head}, but invoice ${alert.denial_invoice_no ?? ''} billed the denied price${stillDenied ? ` (${stillDenied})` : ''}. Decide with ${who.split(' ')[0]} whether to credit and rebill.`;
              break;
            case 'order_gone':
              text = `${head}. The order is no longer in Sage, so there is nothing to adjust.`;
              break;
            case 'unverified':
              text = `${head}. The price change could not be confirmed in Sage. Check the order.`;
              break;
            default:
              text = standing
                ? `Expired quote #${alert.quote_source_id} price auto-DENIED by Merlin — a standing rule ${who} set says not to honor it. Adjust the price in Sage BEFORE invoicing.`
                : `${head} — adjust the price in Sage BEFORE invoicing.`;
          }
        } else if (alert.status === 'disputed') {
          // The salesperson told Merlin the ALERT is wrong. For the order that
          // is the answer: the price stays, nothing is asked of the CSR. That
          // the alert system gets reviewed is our business, not the CSR's, and
          // is deliberately not mentioned (owner feedback, review a6er0e:
          // "that's just white noise").
          text = `Expired quote #${alert.quote_source_id}: ${who} says this alert is wrong${alert.dispute_reason ? ` ("${alert.dispute_reason}")` : ''}. The price stays as submitted and there is nothing for you to do.`;
        } else if (alert.status === 'cancelled') {
          text = `Expired quote #${alert.quote_source_id}: the alert to ${who} was withdrawn. No decision is pending.`;
        } else if (alert.status === 'send_failed') {
          text = `Expired quote #${alert.quote_source_id}: the alert to ${who} could not be delivered — contact them directly before invoicing.`;
        } else if (alert.status === 'awaiting_response') {
          // What the last nudge did, so two CSRs on one order do not both ask.
          const asker = alert.last_nudge_by || 'The order desk';
          let nudged = '';
          if (alert.nudge_pending) {
            nudged = ` ${asker} asked Merlin to nudge them; it goes out within a minute or two.`;
          } else if (alert.last_nudge_failed) {
            nudged = ' The last nudge could not be delivered.';
          } else if (alert.last_nudge_sent_at) {
            nudged = ` Nudged ${minutesAgo(alert.last_nudge_sent_at)} at ${asker}'s request.`;
          }
          if (alert.nudge_blocked === 'limit_reached') {
            nudged += ' No more nudges on this alert; contact them directly.';
          }
          text = `Expired quote #${alert.quote_source_id}: ${who} notified ${minutesAgo(alert.first_sent_at)}${alert.send_count > 1 ? ` (${alert.send_count} sends)` : ''} — awaiting their call.${conversation}${nudged} The order is not held; you can wait for the decision or proceed.`;
        } else {
          text = `Expired quote #${alert.quote_source_id}: alerting ${who} — Merlin is composing the notification.`;
        }
        return (
          <div
            key={alert.alert_id}
            data-testid={`expired-quote-alert-banner-${alert.alert_id}`}
            style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              margin: '10px 0', padding: '8px 12px', borderRadius: '6px',
              background: tone.bg, borderLeft: `3px solid ${tone.border}`,
              color: tone.color, fontSize: '12.5px', fontWeight: 500,
            }}
          >
            <span style={{ flex: 1 }}>
              {text}
              {nudgeNotes.get(alert.alert_id) && (
                <span
                  data-testid={`expired-quote-alert-nudge-note-${alert.alert_id}`}
                  style={{ display: 'block', marginTop: '2px', fontWeight: 600 }}
                >
                  {nudgeNotes.get(alert.alert_id)}
                </span>
              )}
            </span>
            {canEdit && alert.status === 'awaiting_response' && alert.can_nudge && (
              <button
                type="button"
                data-testid={`expired-quote-alert-nudge-${alert.alert_id}`}
                disabled={nudgeBusy === alert.alert_id}
                onClick={() => nudgeSalesperson(alert.alert_id)}
                title={`Merlin sends ${who} a short follow-up saying you asked`}
                style={{
                  background: tone.border, border: `1px solid ${tone.border}`,
                  color: '#fff', borderRadius: '4px', padding: '2px 10px',
                  fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap',
                  cursor: nudgeBusy === alert.alert_id ? 'wait' : 'pointer',
                  opacity: nudgeBusy === alert.alert_id ? 0.7 : 1,
                }}
              >
                {nudgeBusy === alert.alert_id ? 'Nudging…' : `Nudge ${who.split(' ')[0]}`}
              </button>
            )}
            {alert.conversation_id && (
              <button
                type="button"
                data-testid={`expired-quote-alert-conversation-${alert.alert_id}`}
                onClick={() => navigate(`/agent-console?conversation=${alert.conversation_id}`)}
                title="Open the full Merlin ↔ salesperson conversation in the Agent Console"
                style={{
                  background: 'none', border: `1px solid ${tone.border}`,
                  color: tone.color, borderRadius: '4px', padding: '2px 8px',
                  fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                View Merlin&apos;s conversation
              </button>
            )}
            {(alert.status === 'awaiting_response' || alert.status === 'pending_send') && (
              <button
                type="button"
                data-testid={`expired-quote-alert-refresh-${alert.alert_id}`}
                onClick={() => onRefreshPricingContext?.()}
                style={{
                  background: 'none', border: `1px solid ${tone.border}`,
                  color: tone.color, borderRadius: '4px', padding: '2px 8px',
                  fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                Check for reply
              </button>
            )}
          </div>
        );
      })}

      {/* Line Items with Pricing */}
      <div className={styles.section} data-section="order_lines" data-target-order-id={order.id}>
        <div className={styles.sectionTitle}>
          Line Items ({orderDetail.line_items.length})
        </div>
        {orderDetail.line_items.length > 0 && (() => {
          // Prefer the freight API's authoritative total (includes skid
          // weight). Fall back to the local qty × unit_weight sum when
          // the API call hasn't completed, returned an error, or the
          // order has no ship-to address yet.
          let localTotal = 0;
          let anyKnown = false;
          let anyMissing = false;
          for (const li of orderDetail.line_items) {
            const pItem = pricingMap.get(li.item_code);
            const unitWeight = resolveShipWeight(pItem);
            const lineWeight = calculateLineWeight(li.quantity, unitWeight);
            if (lineWeight != null) {
              localTotal += lineWeight;
              anyKnown = true;
            } else if (parseWeight(unitWeight) == null || parseWeight(li.quantity) == null) {
              anyMissing = true;
            }
          }

          const apiSucceeded =
            weightSummary?.success === true &&
            weightSummary.total_weight != null;
          const fallbackMessage =
            weightSummary?.success === false
              ? FALLBACK_REASON_MESSAGES[
                  weightSummary.fallback_reason ?? 'freight_api_error'
                ]
              : null;

          return (
            <div
              className={styles.totalWeightSummary}
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'baseline',
                gap: '8px',
                fontSize: '12px',
                color: '#4b5563',
                marginBottom: '6px',
              }}
              title={
                apiSucceeded
                  ? `Authoritative total from Sage's freight engine — includes ${
                      weightSummary?.skid_weight ?? 0
                    } lb of skid weight across ${
                      weightSummary?.total_skids ?? 0
                    } skids`
                  : 'Local estimate: sum of (quantity × unit weight) across all line items. ' +
                    'Skid weight is not included; the freight quote modal will show a higher number.'
              }
            >
              <span style={{ fontWeight: 500 }}>Total Order Weight:</span>
              <span
                style={{
                  fontWeight: 600,
                  color: apiSucceeded || anyKnown ? '#111827' : '#9ca3af',
                }}
              >
                {apiSucceeded
                  ? formatWeight(weightSummary?.total_weight ?? null)
                  : anyKnown
                    ? formatWeight(localTotal)
                    : '—'}
              </span>
              {apiSucceeded &&
                weightSummary?.skid_weight != null &&
                weightSummary?.product_weight != null && (
                  <span style={{ color: '#6b7280' }}>
                    ({formatWeight(weightSummary.product_weight)} product
                    {' + '}
                    {formatWeight(weightSummary.skid_weight)} skids
                    {weightSummary.total_skids != null
                      ? ` · ${weightSummary.total_skids} skid${weightSummary.total_skids === 1 ? '' : 's'}`
                      : ''}
                    )
                  </span>
                )}
              {!apiSucceeded && anyKnown && anyMissing && (
                <span
                  style={{ color: '#d97706' }}
                  title="Some items are missing weight or quantity data"
                >
                  (partial)
                </span>
              )}
              {!apiSucceeded && fallbackMessage && (
                <span
                  style={{ color: '#9ca3af', fontStyle: 'italic' }}
                  title={fallbackMessage}
                >
                  (estimate)
                </span>
              )}
            </div>
          );
        })()}
        {orderDetail.line_items.length > 0 ? (
          <div className={styles.lineItemsTableWrap}>
          <table className={styles.lineItemsTable} data-testid="order-line-items-table">
            <thead>
              <tr>
                <th>Item</th>
                <th className={styles.rightAlign}>Qty</th>
                <th className={styles.rightAlign} title="Unit weight × quantity (lbs)">Weight</th>
                <th className={styles.rightAlign}>Order Price</th>
                <th className={styles.rightAlign}>Recommended</th>
                <th style={{ textAlign: 'center' }}>Source</th>
                <th style={{ textAlign: 'center' }}>Status</th>
                <th style={{ textAlign: 'center' }}>Applied</th>
                {isDraft && <th style={{ textAlign: 'center', width: '40px' }}></th>}
              </tr>
            </thead>
            <tbody>
              {(() => {
                // At most one flagged line's inline answer card is open;
                // default collapsed ("minimized at first").
                const unansweredLines = orderDetail.line_items.filter(lineNeedsAnswer);
                const effectiveExpandedId =
                  expandedAnswerId != null &&
                  unansweredLines.some((l) => l.id === expandedAnswerId)
                    ? expandedAnswerId
                    : null;
                return orderDetail.line_items.map((li) => {
                const pItem = pricingMap.get(li.item_code);
                const recommended = pItem ? getRecommendedPrice(pItem) : null;
                const pricingStatus = getPricingStatus(li, pItem);
                const isEditingQty = editingCell?.itemId === li.id && editingCell?.field === 'quantity';
                const isEditingPrice = editingCell?.itemId === li.id && editingCell?.field === 'unit_price';
                const isDeleting = deletingItemId === li.id;

                const showAnswerCard =
                  isDraft && lineNeedsAnswer(li) && li.id === effectiveExpandedId;
                return (
                  <Fragment key={li.id}>
                  <tr style={isDeleting ? { opacity: 0.4 } : undefined}>
                    <td style={{ position: 'relative' }}>
                      {switchingItemId === li.id ? (
                        <div className={styles.itemCodeSwitchContainer}>
                          <input
                            ref={switchInputRef}
                            className={styles.itemCodeSwitchInput}
                            type="text"
                            placeholder="Search product..."
                            value={switchQuery}
                            onChange={(e) => handleSwitchSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') {
                                // Prevent the modal's window-level Escape handler from
                                // closing the entire OrderDetailModal. We only want to
                                // cancel the inline item code switch here.
                                e.stopPropagation();
                                e.nativeEvent.stopImmediatePropagation();
                                cancelSwitch();
                              }
                            }}
                            disabled={switchSaving}
                          />
                          {switchSaving && <div className={styles.switchSaving}>Updating...</div>}
                          {!switchSaving && switchQuery.length >= 2 && (
                            <div className={styles.itemCodeSwitchDropdown}>
                              {switchLoading ? (
                                <div className={styles.switchDropdownMsg}>Searching...</div>
                              ) : switchResults.length === 0 ? (
                                <div className={styles.switchDropdownMsg}>No products found</div>
                              ) : (
                                switchResults.map((p) => (
                                  <div
                                    key={p.id}
                                    className={styles.switchDropdownItem}
                                    onClick={() => handleSwitchSelect(p)}
                                  >
                                    <span className={styles.switchItemCode}>{p.sage_item_code}</span>
                                    <span className={styles.switchItemDesc}>
                                      {p.udf_fulldesc || p.item_code_desc || ''}
                                    </span>
                                    <KitComponentBadge product={p} />
                                    <BrandAliasBadge product={p} />
                                    {p.sheet_status === 'inactive' && (
                                      <span
                                        title="Marked INACTIVE on the active-make list — confirm this is correct before using this item."
                                        style={{
                                          fontSize: '10px',
                                          fontWeight: 700,
                                          color: '#d97706',
                                          background: 'rgba(251, 191, 36, 0.15)',
                                          borderRadius: '3px',
                                          padding: '1px 4px',
                                          marginLeft: '6px',
                                        }}
                                      >
                                        ⚠ inactive
                                      </span>
                                    )}
                                    {(() => {
                                      const uw = resolveShipWeight(p);
                                      return parseWeight(uw) != null ? (
                                        <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '6px' }}>
                                          {formatWeight(uw)}
                                        </span>
                                      ) : null;
                                    })()}
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                          <button
                            className={styles.switchCancelBtn}
                            onClick={cancelSwitch}
                            type="button"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <>
                          {isDraft ? (
                            <div
                              className={`${styles.lineItemCode} ${li.item_code ? styles.editableCell : styles.editableCellEmpty}`}
                              onClick={() =>
                                li.item_code
                                  ? startSwitch(li.id)
                                  : startSetItemCode(li.id)
                              }
                              title={li.item_code ? 'Click to switch product' : 'Click to search and assign a product'}
                            >
                              {li.item_code || <em style={{ color: '#9ca3af' }}>Set item code</em>}
                            </div>
                          ) : (
                            <div className={styles.lineItemCode}>{li.item_code}</div>
                          )}
                          <div className={styles.lineItemDesc}>{li.item_description}</div>
                          {/* One ask per line: while the row has an unanswered
                              answer card, the card carries the explanation —
                              repeating the amber ℹ note here just duplicates
                              the "!" and eats space (reviewer feedback,
                              2026-07). It returns once the card resolves. */}
                          {li.notes && !lineNeedsAnswer(li) && (
                            <div className={styles.lineItemNotes}>
                              <span className={styles.lineItemNotesIcon}>&#9432;</span>
                              {renderInlineBold(li.notes)}
                            </div>
                          )}
                          {/* Line-edit advisory: the never-bought-SKU /
                              variant-sibling guard, surfaced the moment the
                              line is saved so the CSR sees it while still on
                              the line, not first at Submit (the submit gate
                              repeats it). Dismiss only hides it locally. */}
                          {(lineAdvisories?.get(li.id) ?? []).map((adv, i) => (
                            <div
                              key={`${adv.field}-${i}`}
                              className={styles.lineAdvisory}
                              role="status"
                              data-testid={`line-advisory-${li.id}`}
                            >
                              <span className={styles.lineAdvisoryIcon} aria-hidden="true">&#9888;</span>
                              <span className={styles.lineAdvisoryText}>{renderInlineBold(adv.message)}</span>
                              {onDismissLineAdvisory && (
                                <button
                                  type="button"
                                  className={styles.lineAdvisoryDismiss}
                                  aria-label="Dismiss advisory"
                                  title="Dismiss (the submit step will ask again)"
                                  data-testid={`line-advisory-dismiss-${li.id}`}
                                  onClick={() => onDismissLineAdvisory(li.id)}
                                >
                                  &times;
                                </button>
                              )}
                            </div>
                          ))}
                          {/* Review-tier chip. Flagged lines get the "N
                              issues" expander that toggles the inline answer
                              card under this row; INFO-tier reasons stay off
                              the row (tooltip only) — corroborated-safe noise
                              must not read like a warning. */}
                          {(() => {
                            const evidencePhrase = describeEvidence(li.review_evidence);
                            const reasonTitle =
                              [li.needs_review_reason, evidencePhrase]
                                .filter(Boolean)
                                .join(' — ') || undefined;
                            const chipBase: React.CSSProperties = {
                              marginTop: '2px',
                              display: 'inline-block',
                              padding: '2px 6px',
                              borderRadius: '3px',
                              fontSize: '10px',
                              fontWeight: 700,
                              cursor: reasonTitle ? 'help' : undefined,
                            };
                            if (lineNeedsAnswer(li)) {
                              const blocked = li.review_tier === 'BLOCKER';
                              const open = li.id === effectiveExpandedId;
                              return (
                                <button
                                  type="button"
                                  data-testid={`review-chip-${li.id}`}
                                  title={reasonTitle}
                                  // Recorder: the title is Merlin's reason
                                  // ("PO $51.00 vs hist $38.80 …"), so name
                                  // the row by family and carry the ids;
                                  // aria-expanded gives open / close.
                                  data-action="order_line_review_toggle"
                                  data-target-order-id={order.id}
                                  data-target-item-id={li.id}
                                  aria-expanded={open}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedAnswerId(open ? null : li.id);
                                  }}
                                  style={{
                                    ...chipBase,
                                    cursor: 'pointer',
                                    border: 'none',
                                    background: blocked
                                      ? 'rgba(248,113,113,0.15)'
                                      : 'rgba(251, 191, 36, 0.15)',
                                    color: blocked ? '#dc2626' : '#d97706',
                                  }}
                                >
                                  {issueChipLabel(questionPartsFor(li), blocked)} {open ? '▴' : '▾'}
                                </button>
                              );
                            }
                            if (isInfoTier(li)) {
                              return (
                                <div
                                  data-testid={`review-chip-${li.id}`}
                                  title={reasonTitle}
                                  style={{
                                    ...chipBase,
                                    background: 'rgba(156,163,175,0.15)',
                                    color: '#6b7280',
                                  }}
                                >
                                  auto-checked ✓
                                </div>
                              );
                            }
                            if (isResolvedReviewLine(li)) {
                              return (
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                  <div
                                    data-testid={`review-chip-${li.id}`}
                                    title={reasonTitle}
                                    style={{
                                      ...chipBase,
                                      background: 'rgba(16,185,129,0.15)',
                                      color: '#059669',
                                    }}
                                  >
                                    {li.review_resolution === 'accepted'
                                      ? '✓ good to go'
                                      : li.review_resolution === 'fixed'
                                        ? '✓ fixed'
                                        : '✓ sent as-is'}
                                  </div>
                                  {isDraft && (
                                    <button
                                      type="button"
                                      data-testid={`review-undo-${li.id}`}
                                      disabled={reviewBusyId === li.id}
                                      onClick={() =>
                                        patchReviewLine(li.id, { needs_review: true, review_resolution: null })
                                      }
                                      style={{
                                        background: 'none',
                                        color: '#6b7280',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        padding: '0 6px',
                                        cursor: 'pointer',
                                        fontSize: '10px',
                                        marginTop: '2px',
                                      }}
                                    >
                                      Undo
                                    </button>
                                  )}
                                </div>
                              );
                            }
                            return null;
                          })()}
                          <LineItemSageComments
                            bundle={lineCommentBundleByLineId.get(li.id) ?? null}
                          />
                          {/* Outbound Sage comment: what WE will write to this
                              line's CommentText in Sage on submission. Teal to
                              distinguish from the indigo/violet read-side chips
                              above (those show what is already in Sage). */}
                          {commentEditId === li.id ? (
                            <div
                              style={{ marginTop: '4px', display: 'flex', gap: '4px', alignItems: 'flex-start' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <textarea
                                data-testid={`sage-comment-input-${li.id}`}
                                data-inline-edit-cell="true"
                                value={commentDraft}
                                onChange={(e) => setCommentDraft(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Escape') {
                                    // Cancel only the comment editor — the modal's
                                    // window-level Escape handler also skips this
                                    // element (data-inline-edit-cell) so the whole
                                    // modal doesn't close mid-typing.
                                    e.stopPropagation();
                                    e.nativeEvent.stopImmediatePropagation();
                                    setCommentEditId(null);
                                  } else if (e.key === 'Tab') {
                                    // Tab commits the field (Sage line-entry muscle
                                    // memory, same as the qty/price cells). Default
                                    // Tab silently focuses Save and typing goes
                                    // nowhere.
                                    e.preventDefault();
                                    void saveSageComment(li.id);
                                  }
                                }}
                                onFocus={(e) => {
                                  const len = e.currentTarget.value.length;
                                  e.currentTarget.setSelectionRange(len, len);
                                }}
                                maxLength={2048}
                                rows={2}
                                placeholder="Comment to print on this Sage line…"
                                autoFocus
                                style={{
                                  flex: 1,
                                  fontSize: '11px',
                                  padding: '4px 6px',
                                  border: '1px solid #5eead4',
                                  borderRadius: '4px',
                                  resize: 'vertical',
                                  minWidth: '180px',
                                }}
                              />
                              <button
                                type="button"
                                data-testid={`sage-comment-save-${li.id}`}
                                data-action="order_line_comment_save"
                                data-target-line-id={li.id}
                                data-target-order-id={order.id}
                                data-outcome="request"
                                disabled={commentSavingId === li.id}
                                onClick={() => saveSageComment(li.id)}
                                style={{
                                  background: '#0d9488',
                                  color: '#fff',
                                  border: '1px solid #0d9488',
                                  borderRadius: '4px',
                                  padding: '3px 8px',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                }}
                              >
                                {commentSavingId === li.id ? 'Saving…' : 'Save'}
                              </button>
                              <button
                                type="button"
                                data-testid={`sage-comment-cancel-${li.id}`}
                                onClick={() => setCommentEditId(null)}
                                style={{
                                  background: 'none',
                                  color: '#6b7280',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '4px',
                                  padding: '3px 6px',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          ) : li.sage_comment_text ? (
                            <div
                              data-testid={`sage-comment-chip-${li.id}`}
                              style={{
                                marginTop: '3px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px',
                                padding: '2px 6px',
                                borderRadius: '3px',
                                fontSize: '11px',
                                background: 'rgba(13,148,136,0.10)',
                                color: '#0f766e',
                              }}
                            >
                              <span style={{ fontWeight: 700 }}>→ Sage comment</span>
                              <span>{li.sage_comment_text}</span>
                              {isDraft && (
                                <button
                                  type="button"
                                  data-testid={`sage-comment-edit-${li.id}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCommentDraft(li.sage_comment_text ?? '');
                                    setCommentEditId(li.id);
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#0d9488',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                    padding: 0,
                                    textDecoration: 'underline',
                                  }}
                                >
                                  edit
                                </button>
                              )}
                            </div>
                          ) : isDraft ? (
                            <button
                              type="button"
                              data-testid={`sage-comment-add-${li.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCommentDraft('');
                                setCommentEditId(li.id);
                              }}
                              onKeyDown={(e) => {
                                // Keyboard users who Tab onto the affordance and
                                // start typing expect a text field — open the
                                // editor seeded with the keystroke.
                                if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                                  e.preventDefault();
                                  setCommentDraft(e.key === ' ' ? '' : e.key);
                                  setCommentEditId(li.id);
                                }
                              }}
                              style={{
                                marginTop: '3px',
                                display: 'inline-block',
                                background: 'none',
                                border: 'none',
                                color: '#9ca3af',
                                cursor: 'pointer',
                                fontSize: '10px',
                                padding: 0,
                              }}
                              title="Add a comment that prints on this line in Sage"
                            >
                              + Sage comment
                            </button>
                          ) : null}
                        </>
                      )}
                      {pItem?.pricing_type && (pItem.tier_price_1 || pItem.tier_price_2 || pItem.tier_price_3) && (
                        <div className={styles.tierHint}>
                          Tiered:
                          {pItem.tier_price_1 ? ` ${formatPrice(pItem.tier_price_1)}` : ''}
                          {pItem.tier_price_2 ? `, ${formatPrice(pItem.tier_price_2)} @ ${pItem.tier_price_2_min}+` : ''}
                          {pItem.tier_price_3 ? `, ${formatPrice(pItem.tier_price_3)} @ ${pItem.tier_price_3_min}+` : ''}
                        </div>
                      )}
                      {/*
                        Active-make list warning (PJ's orderability sheet).
                        Backend sets active_list_warning when the item is
                        sheet-inactive or missing from a populated sheet —
                        the Emily .03-vs-.04 case where an exploding-kit
                        component reached Sage. Amber, not blocking: PJ may
                        simply not have added the item yet.
                      */}
                      {pItem?.active_list_warning && (
                        <div
                          data-testid={`active-list-warning-${li.id}`}
                          title={
                            pItem.sheet_status === 'inactive'
                              ? 'This item is marked INACTIVE on the active-make list. Confirm this is correct before submitting.'
                              : 'This item is not on the active-make list — confirm this is correct before submitting.'
                          }
                          style={{
                            marginTop: '2px',
                            display: 'inline-block',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontSize: '10px',
                            fontWeight: 700,
                            background: 'rgba(251, 191, 36, 0.15)',
                            color: '#d97706',
                            cursor: 'help',
                          }}
                        >
                          ⚠ {pItem.sheet_status === 'inactive'
                            ? 'Inactive on active-make list'
                            : 'Not on active-make list'} — confirm this is correct
                        </div>
                      )}
                    </td>
                    <td className={styles.rightAlign}>
                      {isDraft && !isEditingQty ? (
                        <span
                          className={li.quantity == null ? styles.editableCellEmpty : styles.editableCell}
                          onClick={() => startEdit(li.id, 'quantity', li.quantity ?? '')}
                          title="Click to edit quantity"
                        >
                          {li.quantity != null ? <>{li.quantity}{li.packaging ? ` ${li.packaging}` : ''}</> : <em style={{ color: '#9ca3af' }}>Set qty</em>}
                        </span>
                      ) : isEditingQty ? (
                        <input
                          ref={editInputRef}
                          className={styles.inlineEditInput}
                          type="text"
                          inputMode="decimal"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={handleEditBlur}
                          onKeyDown={handleEditKeyDown}
                          disabled={editSaving}
                          data-inline-edit-cell="true"
                        />
                      ) : (
                        <>{li.quantity != null ? <>{li.quantity}{li.packaging ? ` ${li.packaging}` : ''}</> : <em style={{ color: '#9ca3af' }}>—</em>}</>
                      )}
                    </td>
                    <td
                      className={styles.rightAlign}
                      title={(() => {
                        const uw = resolveShipWeight(pItem);
                        return parseWeight(uw) != null
                          ? `Unit weight: ${formatWeight(uw)}`
                          : 'Weight not available';
                      })()}
                    >
                      {(() => {
                        const unitWeight = resolveShipWeight(pItem);
                        const lineWeight = calculateLineWeight(li.quantity, unitWeight);
                        if (lineWeight != null) {
                          return <span>{formatWeight(lineWeight)}</span>;
                        }
                        if (parseWeight(unitWeight) != null) {
                          // Unit weight known but quantity missing: show unit weight only.
                          return <span style={{ color: '#9ca3af' }}>{formatWeight(unitWeight)}/ea</span>;
                        }
                        return <em style={{ color: '#9ca3af' }}>—</em>;
                      })()}
                    </td>
                    <td className={styles.rightAlign}>
                      {isDraft && !isEditingPrice ? (
                        <span
                          className={li.unit_price == null ? styles.editableCellEmpty : styles.editableCell}
                          onClick={() => startEdit(li.id, 'unit_price', li.unit_price ?? '')}
                          title="Click to edit price"
                        >
                          {li.unit_price != null ? formatPrice(li.unit_price) : <em style={{ color: '#9ca3af' }}>Set price</em>}
                        </span>
                      ) : isEditingPrice ? (
                        <input
                          ref={editInputRef}
                          className={styles.inlineEditInput}
                          type="text"
                          inputMode="decimal"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={handleEditBlur}
                          onKeyDown={handleEditKeyDown}
                          disabled={editSaving}
                          data-inline-edit-cell="true"
                        />
                      ) : (
                        <>{li.unit_price != null ? formatPrice(li.unit_price) : <em style={{ color: '#9ca3af' }}>—</em>}</>
                      )}
                      {/* The CSR's own note on why this line is priced away
                          from the PO (owner ask 2026-10-29): read back from
                          the audit trail so it survives a reload. Hidden
                          while the prompt for a newer save is open. */}
                      {li.price_change_reason && priceReasonPrompt?.itemId !== li.id && (
                        <span
                          className={priceReasonStyles.savedNote}
                          data-testid={`price-reason-note-${li.id}`}
                          title="Why this line is priced differently from the PO"
                        >
                          Note: {li.price_change_reason}
                        </span>
                      )}
                      {/*
                        PO-vs-shipped mismatch warning. The backend sets
                        has_po_price_mismatch when extracted_price (LLM
                        reading from the PO) ≠ unit_price (what will
                        ship, waterfall-seeded). The AU00249 bug was
                        shipping PO-extracted prices silently; this
                        badge is the counter-measure.
                      */}
                      {/* One ask per line: hidden while this line has an
                          unanswered answer-panel card (the card shows the PO
                          price); returns if the card resolves and the
                          mismatch still holds. */}
                      {!lineNeedsAnswer(li) && pItem?.has_po_price_mismatch && pItem.extracted_price != null && (() => {
                        const ourLabel = sourceLabel(pItem.current_source);
                        const detailSuffix = pItem.price_source_detail
                          ? ` (${pItem.price_source_detail})`
                          : '';
                        return (
                        <div
                          data-testid={`po-mismatch-${li.id}`}
                          title={
                            `PO discovered ${formatPrice(pItem.extracted_price)}, ` +
                            `but ${ourLabel}${detailSuffix} has us at ` +
                            `${formatPrice(li.unit_price ?? '0')}. Review before submitting — ` +
                            `click the ship price to edit if the PO is correct.`
                          }
                          className={styles.poMismatchBadge}
                        >
                          ⚠ PO discovered {formatPrice(pItem.extracted_price)}
                        </div>
                        );
                      })()}
                    </td>
                    <td className={styles.rightAlign}>
                      {recommended?.price ? (
                        <span style={{ fontWeight: 600 }}>
                          {recommended.price}
                        </span>
                      ) : (
                        <span className={styles.priceNoSpecial}>—</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {(() => {
                        // One ask per line: while this line has an unanswered
                        // answer-panel card, that card is the single ask —
                        // suppress the price-source REVIEW pill here. It
                        // reappears if the card is undone/resolved and the
                        // condition still holds.
                        if (lineNeedsAnswer(li)) {
                          return (
                            <span style={{
                              display: 'inline-block', padding: '2px 6px', borderRadius: '3px',
                              fontSize: '10px', fontWeight: 700,
                              background: 'rgba(156,163,175,0.15)', color: '#9ca3af',
                            }}>
                              —
                            </span>
                          );
                        }
                        const src = pItem?.price_source;
                        if (!src || src === 'investigation' || li.unit_price == null) {
                          // REVIEW (amber — red is reserved for blockers) only
                          // when the price is actually missing, or the price is
                          // investigation-classified AND the user never typed
                          // it themselves this session. A hand-typed price
                          // renders as the muted Custom badge instead.
                          const showReview =
                            li.unit_price == null ||
                            (src === 'investigation' && !userPricedIds.has(li.id));
                          if (showReview) {
                            return (
                              <span style={{
                                display: 'inline-block', padding: '2px 6px', borderRadius: '3px',
                                fontSize: '10px', fontWeight: 700,
                                background: 'rgba(251, 191, 36, 0.15)', color: '#d97706',
                              }}>
                                REVIEW
                              </span>
                            );
                          }
                          if (src === 'investigation') {
                            return (
                              <span
                                title="Price entered by you this session — not from any pricing source"
                                style={{
                                  display: 'inline-block', padding: '2px 6px', borderRadius: '3px',
                                  fontSize: '10px', fontWeight: 700,
                                  background: 'rgba(156,163,175,0.15)', color: '#6b7280',
                                  border: '1px solid #d1d5db',
                                }}
                              >
                                Custom
                              </span>
                            );
                          }
                          return (
                            <span style={{
                              display: 'inline-block', padding: '2px 6px', borderRadius: '3px',
                              fontSize: '10px', fontWeight: 700,
                              background: 'rgba(156,163,175,0.15)', color: '#9ca3af',
                            }}>
                              —
                            </span>
                          );
                        }
                        const badges: Record<string, { label: string; bg: string; color: string }> = {
                          product_hybrid: { label: 'CATALOG', bg: 'rgba(108,140,255,0.15)', color: '#6c8cff' },
                          special_pricing: { label: 'SPECIAL', bg: 'rgba(167,139,250,0.15)', color: '#a78bfa' },
                          special_pricing_base_discount: { label: 'SP DISC', bg: 'rgba(167,139,250,0.15)', color: '#a78bfa' },
                          customer_quote: { label: 'QUOTE', bg: 'rgba(34,211,238,0.15)', color: '#22d3ee' },
                        };
                        const badge = badges[src] ?? { label: src, bg: 'rgba(156,163,175,0.15)', color: '#9ca3af' };
                        return (
                          <span>
                            <span style={{
                              display: 'inline-block', padding: '2px 6px', borderRadius: '3px',
                              fontSize: '10px', fontWeight: 700,
                              background: badge.bg, color: badge.color,
                            }}>
                              {badge.label}
                            </span>
                            {src === 'special_pricing' && pItem?.price_source_detail === 'tiered' && pItem && (() => {
                              const eff = getEffectiveSpecialPrice(pItem);
                              return eff.tier !== null ? renderTierBadge(pItem, eff.tier) : null;
                            })()}
                            {src === 'special_pricing_base_discount' && pItem?.applied_base_discount_pct != null && (
                              <span style={{ fontSize: '10px', color: '#9ca3af', marginLeft: '4px' }}>
                                −{pItem.applied_base_discount_pct}%
                                {pItem.list_price_before_discount && (
                                  <> off ${pItem.list_price_before_discount}</>
                                )}
                              </span>
                            )}
                            {src === 'customer_quote' && pItem?.price_source_detail && (
                              <span style={{ fontSize: '10px', color: '#9ca3af', marginLeft: '4px' }}>
                                #{pItem.price_source_detail}
                              </span>
                            )}
                          </span>
                        );
                      })()}
                      {/* Expired-quote flow (2026-08-26): this line's price
                          matches an expired project quote live pricing can't
                          explain. Order is NOT held; the salesperson gets the
                          margin alert at submission. */}
                      {pItem?.expired_quote_match && (
                        <span
                          data-testid={`expired-quote-pill-${li.id}`}
                          title={
                            `Price matches EXPIRED quote #${pItem.expired_quote_match.quote_source_id}` +
                            ` (quoted ${pItem.expired_quote_match.quote_created_date ?? '?'}, expired ` +
                            `${pItem.expired_quote_match.quote_expiration_date ?? '?'}). The order is not held — ` +
                            'the salesperson is alerted at submission to approve or adjust before invoicing.'
                          }
                          style={{
                            display: 'inline-block', padding: '2px 6px', borderRadius: '3px',
                            fontSize: '10px', fontWeight: 700, marginLeft: '4px',
                            background: '#fdedec', color: '#c0392b',
                          }}
                        >
                          EXPIRED QUOTE #{pItem.expired_quote_match.quote_source_id}
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {pricingStatus === 'match' ? (
                        <span className={`${styles.pricingStatusBadge} ${styles.pricingStatusMatch}`}>
                          Match
                        </span>
                      ) : pricingStatus === 'mismatch' ? (
                        <span className={`${styles.pricingStatusBadge} ${styles.pricingStatusMismatch}`}>
                          Mismatch
                        </span>
                      ) : pricingContext && !pricingContext.has_any_special_pricing ? (
                        // Customer has no special pricing at all — the
                        // customer-level note above covers it; a per-line
                        // "No SP" chip on every row is pure noise.
                        <span style={{ color: '#9ca3af', fontSize: '11px' }}>—</span>
                      ) : (
                        <span className={`${styles.pricingStatusBadge} ${styles.pricingStatusNone}`}>
                          No SP
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <PricingSourcePicker
                        itemCode={li.item_code}
                        pricingItem={pItem}
                        allowPick={isDraft}
                        onApplyPrice={async (price: number) => {
                          if (!orderDetail) return;
                          const quotePrice = String(price);
                          if (li.unit_price === quotePrice) return;
                          try {
                            const resp = await portalOrderService.updateLineItem(
                              orderDetail.order.id,
                              li.id,
                              { unit_price: quotePrice },
                            );
                            onLineItemUpdated?.(resp);
                          } catch {
                            // Best-effort
                          }
                        }}
                        formatPrice={formatPrice}
                      />
                    </td>
                    {isDraft && (
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className={styles.deleteLineItemBtn}
                          onClick={() => handleDeleteLineItem(li.id)}
                          disabled={isDeleting}
                          type="button"
                          data-action="order_line_remove"
                          data-target-order-id={order.id}
                          data-target-item-id={li.id}
                          data-outcome="request"
                          title="Remove line item"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      </td>
                    )}
                  </tr>
                  {/* Optional "why?" for a price just saved away from the PO
                      (owner ask 2026-10-29). Not a gate: the price is saved. */}
                  {isDraft && priceReasonPrompt?.itemId === li.id && (
                    <tr data-testid={`price-reason-row-${li.id}`}>
                      <td colSpan={9} style={{ padding: 0 }}>
                        <LinePriceReasonPrompt
                          lineItemId={li.id}
                          itemCode={li.item_code}
                          poPrice={priceReasonPrompt.poPrice}
                          newPrice={priceReasonPrompt.newPrice}
                          formatPrice={formatPrice}
                          saving={priceReasonSaving}
                          onSave={savePriceReason}
                          onSkip={skipPriceReason}
                        />
                      </td>
                    </tr>
                  )}
                  {/* Inline answer card — the moved review card, minus the
                      item/qty/price header the row above already shows. One
                      question at a time; only the final answer PATCHes. */}
                  {showAnswerCard && (() => {
                    const busy = reviewBusyId === li.id;
                    const evidencePhrase = describeEvidence(li.review_evidence);
                    const questionParts = questionPartsFor(li);
                    const subStep = Math.min(
                      cardSubStep[li.id] ?? 0,
                      Math.max(questionParts.length - 1, 0),
                    );
                    const isFinalStep = subStep >= questionParts.length - 1;
                    const currentPart = questionParts[subStep];
                    const isBlocker = li.review_tier === 'BLOCKER';
                    return (
                      <tr data-testid={`review-inline-row-${li.id}`}>
                        <td
                          colSpan={9}
                          style={{
                            background: '#fffbeb',
                            borderLeft: '4px solid #f59e0b',
                            padding: '8px 14px 10px',
                          }}
                        >
                          {currentPart && (
                            <div key={`${li.id}-q${subStep}`}>
                              {questionParts.length > 1 && (
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: '11px',
                                    color: '#a16207',
                                    fontWeight: 600,
                                  }}
                                >
                                  {subStep > 0 && (
                                    <button
                                      type="button"
                                      data-testid={`review-substep-back-${li.id}`}
                                      onClick={() => setSubStep(li.id, subStep - 1)}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: '#a16207',
                                        padding: 0,
                                        fontSize: '12px',
                                      }}
                                    >
                                      ◂
                                    </button>
                                  )}
                                  <span>
                                    Question {subStep + 1} of {questionParts.length}
                                  </span>
                                  <span style={{ letterSpacing: '2px' }}>
                                    {questionParts.map((_, di) => (di === subStep ? '●' : '○')).join('')}
                                  </span>
                                </div>
                              )}
                              {subStep === 0 && evidencePhrase && (
                                <div style={{ fontSize: '12px', color: '#92400e' }}>
                                  <strong>{evidencePhrase}.</strong>
                                </div>
                              )}
                              <div
                                style={
                                  currentPart.tier === 'INFO'
                                    ? { fontSize: '12px', color: '#78716c', fontStyle: 'italic' }
                                    : { fontSize: '12px', color: '#92400e' }
                                }
                              >
                                {renderInlineBold(currentPart.text)}
                              </div>
                            </div>
                          )}
                          {/* The line's ℹ note is suppressed on the row while
                              unanswered (one ask per line) — carry it here
                              unless the reason already says the same thing. */}
                          {li.notes && !(li.needs_review_reason || '').includes(li.notes) && (
                            <div style={{ fontSize: '12px', color: '#a16207', marginTop: '2px', fontStyle: 'italic' }}>
                              {renderInlineBold(li.notes)}
                            </div>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                            {!isFinalStep ? (
                              // Non-final question: acknowledging slides to the
                              // next one (local only — no PATCH until the last).
                              <button
                                type="button"
                                data-testid={`review-substep-next-${li.id}`}
                                disabled={busy}
                                onClick={() => setSubStep(li.id, subStep + 1)}
                                style={{
                                  background: '#059669',
                                  color: '#fff',
                                  border: '1px solid #059669',
                                  borderRadius: '4px',
                                  padding: '3px 10px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                }}
                              >
                                ✓ Looks good — next ▸
                              </button>
                            ) : (
                              // The CSR is always in control — BLOCKER lines
                              // are click-through-able too (user directive,
                              // 2026-07: no flag may ever lock them out of
                              // submitting; required-field gaps still surface
                              // as fixable errors at submit time).
                              <button
                                type="button"
                                data-testid={`review-accept-${li.id}`}
                                disabled={busy}
                                title={
                                  isBlocker
                                    ? 'Clears this flag. Anything Sage requires (item code, quantity, price) still has to be filled in before submit.'
                                    : undefined
                                }
                                onClick={() =>
                                  patchReviewLine(li.id, { needs_review: false, review_resolution: 'accepted' })
                                }
                                style={{
                                  background: '#059669',
                                  color: '#fff',
                                  border: '1px solid #059669',
                                  borderRadius: '4px',
                                  padding: '3px 10px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                }}
                              >
                                {isBlocker ? '✓ Send as-is — I\u2019ll handle it' : '✓ Looks right — good to go'}
                              </button>
                            )}
                            {reviewFixQtyId === li.id ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                <input
                                  type="number"
                                  min="0"
                                  step="any"
                                  autoFocus
                                  data-testid={`review-fix-qty-input-${li.id}`}
                                  value={reviewFixQtyValue}
                                  disabled={busy}
                                  onChange={(e) => setReviewFixQtyValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      void saveReviewFixQty(li.id);
                                    } else if (e.key === 'Escape') {
                                      // Keep the modal's window-level Escape
                                      // handler from closing the whole modal.
                                      e.stopPropagation();
                                      e.nativeEvent.stopImmediatePropagation();
                                      cancelReviewFixQty();
                                    }
                                  }}
                                  style={{
                                    width: '80px',
                                    fontSize: '12px',
                                    padding: '2px 6px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '4px',
                                  }}
                                />
                                <button
                                  type="button"
                                  data-testid={`review-fix-qty-save-${li.id}`}
                                  disabled={busy || !(parseFloat(reviewFixQtyValue) > 0)}
                                  onClick={() => void saveReviewFixQty(li.id)}
                                  style={{
                                    background: '#2563eb',
                                    color: '#fff',
                                    border: '1px solid #2563eb',
                                    borderRadius: '4px',
                                    padding: '3px 10px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                  }}
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={cancelReviewFixQty}
                                  style={{
                                    background: 'none',
                                    color: '#6b7280',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '4px',
                                    padding: '3px 10px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                  }}
                                >
                                  Cancel
                                </button>
                              </span>
                            ) : (
                              <button
                                type="button"
                                data-testid={`review-fix-qty-${li.id}`}
                                disabled={busy}
                                onClick={() => startReviewFixQty(li.id, li.quantity)}
                                style={{
                                  background: 'none',
                                  color: '#374151',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '4px',
                                  padding: '3px 10px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                }}
                              >
                                Fix quantity…
                              </button>
                            )}
                            <button
                              type="button"
                              data-testid={`review-switch-product-${li.id}`}
                              disabled={busy}
                              onClick={() =>
                                li.item_code ? startSwitch(li.id) : startSetItemCode(li.id)
                              }
                              title="Opens the product search on this line's row above"
                              style={{
                                background: 'none',
                                color: '#374151',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                padding: '3px 10px',
                                cursor: 'pointer',
                                fontSize: '12px',
                              }}
                            >
                              {li.item_code ? 'Switch product…' : 'Set product…'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })()}
                  </Fragment>
                );
                });
              })()}
            </tbody>
          </table>
          </div>
        ) : (
          <div style={{ fontSize: '13px', color: '#9ca3af', padding: '8px 0' }}>
            No line items yet
          </div>
        )}

        {/* INFO-tier drawer — corroborated-safe review notes kept for audit.
            Collapsed and muted by design: these never demand attention. */}
        {(() => {
          const infoLines = orderDetail.line_items.filter(isInfoTier);
          if (infoLines.length === 0) return null;
          return (
            <details
              data-testid="info-review-drawer"
              style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}
            >
              <summary style={{ cursor: 'pointer' }}>
                Auto-checked details ({infoLines.length})
              </summary>
              <div style={{ padding: '6px 0 2px 14px' }}>
                {infoLines.map((li) => (
                  <div key={li.id} style={{ padding: '2px 0' }}>
                    <span style={{ fontWeight: 600 }}>{li.item_code || '(no code)'}</span>
                    {' — '}
                    {renderInlineBold(li.needs_review_reason || 'No reason recorded')}
                    {describeEvidence(li.review_evidence) && (
                      <span style={{ color: '#9ca3af' }}>
                        {' '}({describeEvidence(li.review_evidence)})
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </details>
          );
        })()}

        {/* Inline Add Line Item (draft orders only) */}
        {isDraft && !showAddSearch && (
          <button
            className={styles.addLineItemBtn}
            onClick={() => setShowAddSearch(true)}
            type="button"
            data-action="order_line_add_open"
            data-target-order-id={order.id}
            data-outcome="open"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Line Item
          </button>
        )}

        {isDraft && showAddSearch && (
          <div className={styles.inlineSearchSection}>
            <div className={styles.inlineSearchHeader}>
              <span className={styles.inlineSearchLabel}>Search Product Catalog</span>
              <button className={styles.inlineSearchClose} onClick={closeSearch} type="button">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <input
              ref={searchInputRef}
              className={styles.inlineSearchInput}
              type="text"
              placeholder="Search by item code or description..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
            {addingItem && (
              <div className={styles.inlineSearchAdding}>Adding item...</div>
            )}
            {fillWithProduct && !addingItem && (() => {
              const resolvedUnitWeight = resolveShipWeight(fillWithProduct);
              const unitWeight = parseWeight(resolvedUnitWeight);
              const target = parseFloat(fillTargetInput.trim());
              const qty = quantityForTargetWeight(target, unitWeight);
              const computedWeight =
                qty != null && unitWeight != null ? qty * unitWeight : null;
              const canSubmit = qty != null;
              return (
                <div className={styles.fillWithForm}>
                  <div className={styles.fillWithHeader}>
                    Fill with <strong>{fillWithProduct.sage_item_code}</strong>
                    <span className={styles.fillWithUnit}>
                      (unit weight: {formatWeight(resolvedUnitWeight)})
                    </span>
                  </div>
                  <label className={styles.fillWithLabel} htmlFor="fill-target-weight">
                    Target weight:
                  </label>
                  <div className={styles.fillWithInputRow}>
                    <input
                      id="fill-target-weight"
                      ref={fillInputRef}
                      className={styles.fillWithInput}
                      type="text"
                      inputMode="decimal"
                      placeholder="e.g. 1000"
                      value={fillTargetInput}
                      onChange={(e) => {
                        setFillTargetInput(e.target.value);
                        setFillError(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && canSubmit) {
                          e.preventDefault();
                          void submitFillWith();
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          e.stopPropagation();
                          cancelFillWith();
                        }
                      }}
                    />
                    <span className={styles.fillWithUnitSuffix}>lbs</span>
                  </div>
                  {qty != null && computedWeight != null ? (
                    <div className={styles.fillWithPreview}>
                      → Will add <strong>{qty} × {fillWithProduct.sage_item_code}</strong>{' '}
                      = {formatWeight(computedWeight)}
                      {computedWeight !== target && (
                        <span className={styles.fillWithRoundNote}>
                          {' '}(rounded from target {formatWeight(target)})
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className={styles.fillWithHint}>
                      Enter a positive target weight in pounds.
                    </div>
                  )}
                  {fillError && (
                    <div className={styles.fillWithError}>{fillError}</div>
                  )}
                  <div className={styles.fillWithActions}>
                    <button
                      type="button"
                      className={styles.fillWithCancel}
                      onClick={cancelFillWith}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={styles.fillWithSubmit}
                      disabled={!canSubmit}
                      onClick={() => void submitFillWith()}
                    >
                      {canSubmit ? `Add ${qty} unit${qty === 1 ? '' : 's'}` : 'Add'}
                    </button>
                  </div>
                </div>
              );
            })()}
            {manualPriceProduct && !addingItem && (
              <div className={styles.manualPriceForm}>
                <div className={styles.manualPriceHeader}>
                  Manual price for{' '}
                  <strong>{manualPriceProduct.sage_item_code}</strong>
                  <span className={styles.fillWithUnit}>
                    ({manualPriceProduct.udf_fulldesc ||
                      manualPriceProduct.item_code_desc ||
                      manualPriceProduct.sage_item_code}
                    )
                  </span>
                </div>
                <div className={styles.manualPriceReason}>
                  ⚠ No authoritative price on file for this customer.
                  {manualPriceReason ? ` (${manualPriceReason})` : null} Enter a
                  unit price to add this line.
                </div>
                <div className={styles.manualPriceFieldRow}>
                  <label
                    className={styles.fillWithLabel}
                    htmlFor="manual-price-qty"
                  >
                    Qty
                  </label>
                  <input
                    id="manual-price-qty"
                    className={styles.manualPriceQtyInput}
                    type="text"
                    inputMode="decimal"
                    value={manualPriceQty}
                    onChange={(e) => {
                      setManualPriceQty(e.target.value);
                      setManualPriceError(null);
                    }}
                  />
                  <label
                    className={styles.fillWithLabel}
                    htmlFor="manual-price-input"
                  >
                    Unit price
                  </label>
                  <span className={styles.manualPricePrefix}>$</span>
                  <input
                    id="manual-price-input"
                    ref={manualPriceInputRef}
                    className={styles.manualPriceInput}
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={manualPriceInput}
                    onChange={(e) => {
                      setManualPriceInput(e.target.value);
                      setManualPriceError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void submitManualPrice();
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        e.stopPropagation();
                        cancelManualPrice();
                      }
                    }}
                  />
                </div>
                {manualPriceError && (
                  <div className={styles.fillWithError}>
                    {manualPriceError}
                  </div>
                )}
                <div className={styles.fillWithActions}>
                  <button
                    type="button"
                    className={styles.fillWithCancel}
                    onClick={cancelManualPrice}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={styles.fillWithSubmit}
                    onClick={() => void submitManualPrice()}
                  >
                    Add line at typed price
                  </button>
                </div>
              </div>
            )}
            {priceConfirm && !addingItem && (
              <div
                className={styles.priceConfirmForm}
                ref={priceConfirmPanelRef}
                tabIndex={-1}
                onKeyDown={handlePriceConfirmKeyDown}
                data-testid="price-confirm-panel"
              >
                <div className={styles.manualPriceHeader}>
                  {priceConfirm.targetLineId ? 'Set ' : 'Add '}
                  <strong>{priceConfirm.product.sage_item_code}</strong>
                  <span className={styles.fillWithUnit}>
                    ({priceConfirm.product.udf_fulldesc ||
                      priceConfirm.product.item_code_desc ||
                      priceConfirm.product.sage_item_code}
                    )
                  </span>
                </div>
                <div className={styles.manualPriceFieldRow}>
                  <label className={styles.fillWithLabel} htmlFor="price-confirm-qty">
                    Qty
                  </label>
                  <input
                    id="price-confirm-qty"
                    ref={priceConfirmQtyRef}
                    className={styles.manualPriceQtyInput}
                    type="text"
                    inputMode="decimal"
                    value={priceConfirm.quantity}
                    disabled={priceConfirm.quantityLocked}
                    title={
                      priceConfirm.quantityLocked
                        ? "The line keeps its own quantity"
                        : 'Quantity — special pricing tiers depend on it'
                    }
                    onChange={(e) => handlePriceConfirmQtyChange(e.target.value)}
                  />
                  {priceConfirm.product.sales_unit_of_measure && (
                    <span className={styles.fillWithUnitSuffix}>
                      {priceConfirm.product.sales_unit_of_measure}
                    </span>
                  )}
                  {priceConfirmLoading && (
                    <span className={styles.priceConfirmRepricing}>Re-pricing…</span>
                  )}
                </div>
                <div className={styles.priceConfirmLabel}>
                  Prices on file for {order.customer_no}
                  {priceConfirm.choices.length === 0 && ' — none'}
                </div>
                <div
                  className={styles.priceConfirmOptions}
                  role="radiogroup"
                  aria-label="Pricing options"
                >
                  {priceConfirm.choices.map((c, i) => (
                    <button
                      key={c.key}
                      type="button"
                      role="radio"
                      aria-checked={priceConfirmPick === i}
                      data-action="pricing_option_pick"
                      data-record-value="price_source"
                      value={c.type}
                      data-target-product-id={priceConfirm.product.id}
                      data-target-order-id={order.id}
                      className={`${styles.priceConfirmOption} ${
                        priceConfirmPick === i ? styles.priceConfirmOptionSelected : ''
                      }`}
                      onClick={() => setPriceConfirmPick(i)}
                      onDoubleClick={() => {
                        setPriceConfirmPick(i);
                        void submitPriceConfirm();
                      }}
                    >
                      <span
                        className={`${styles.priceConfirmBadge} ${priceChoiceBadgeClass(c.type)}`}
                      >
                        {priceChoiceBadgeText(c.type)}
                      </span>
                      <span className={styles.priceConfirmOptionLabels}>
                        <span className={styles.priceConfirmOptionLabel}>{c.label}</span>
                        {c.sublabel && (
                          <span className={styles.priceConfirmOptionSublabel}>{c.sublabel}</span>
                        )}
                      </span>
                      <span className={styles.priceConfirmOptionPrice}>
                        {formatPrice(c.price)}
                      </span>
                      {c.recommended && (
                        <span className={styles.priceConfirmRecommended}>recommended</span>
                      )}
                    </button>
                  ))}
                </div>
                <div className={styles.fillWithActions}>
                  <button
                    type="button"
                    className={styles.fillWithCancel}
                    data-action="pricing_option_cancel"
                    onClick={cancelPriceConfirm}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={styles.fillWithSubmit}
                    disabled={priceConfirmPick == null || priceConfirmLoading}
                    data-action="pricing_option_confirm"
                    data-outcome="request"
                    data-target-product-id={priceConfirm.product.id}
                    data-target-order-id={order.id}
                    onClick={() => void submitPriceConfirm()}
                  >
                    {(() => {
                      const c =
                        priceConfirmPick != null
                          ? priceConfirm.choices[priceConfirmPick]
                          : undefined;
                      const verb = priceConfirm.targetLineId ? 'Set' : 'Add';
                      return c ? `${verb} at ${formatPrice(c.price)}` : verb;
                    })()}
                  </button>
                </div>
              </div>
            )}
            {!addingItem && !fillWithProduct && !manualPriceProduct && !priceConfirm && searchQuery.length >= 2 && (
              <div className={styles.inlineSearchResults}>
                {searchLoading ? (
                  <div className={styles.inlineSearchLoading}>Searching...</div>
                ) : searchError ? (
                  <div
                    className={styles.inlineSearchError}
                    role="alert"
                    data-testid="product-search-error"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                    </svg>
                    {searchError}
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className={styles.inlineSearchNoResults}>No products found</div>
                ) : (
                  searchResults.map((p) => {
                    const resolvedUnitWeight = resolveShipWeight(p);
                    const hasWeight = parseWeight(resolvedUnitWeight) != null;
                    return (
                      <div
                        key={p.id}
                        className={styles.inlineSearchItem}
                        onClick={() => handleAddProduct(p)}
                      >
                        <span className={styles.inlineSearchItemCode}>{p.sage_item_code}</span>
                        <span className={styles.inlineSearchItemDesc}>
                          {p.udf_fulldesc || p.item_code_desc}
                        </span>
                        <KitComponentBadge product={p} />
                        <BrandAliasBadge product={p} />
                        <span className={styles.inlineSearchItemPrice}>
                          {p.standard_unit_price
                            ? `$${parseFloat(p.standard_unit_price).toFixed(2)}`
                            : '--'}
                          {p.sales_unit_of_measure ? ` / ${p.sales_unit_of_measure}` : ''}
                          {hasWeight && (
                            <span style={{ marginLeft: '8px', color: '#6b7280' }}>
                              · {formatWeight(resolvedUnitWeight)}
                            </span>
                          )}
                        </span>
                        <button
                          type="button"
                          className={styles.inlineSearchAddBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleAddProduct(p);
                          }}
                          title={`Add ${p.sage_item_code} at qty 1`}
                        >
                          Add
                        </button>
                        {hasWeight && (
                          <button
                            type="button"
                            className={styles.fillWithBtn}
                            onClick={(e) => {
                              // Don't bubble to the row's onClick (which adds qty=1)
                              e.stopPropagation();
                              handleFillWithProduct(p);
                            }}
                            title={`Fill with ${p.sage_item_code} — enter a target total weight and we'll compute the quantity`}
                          >
                            Fill with…
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        )}

        {addSuccess && (
          <div className={styles.addLineItemSuccess}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
            {addSuccess}
          </div>
        )}

        {addError && (
          <div
            className={styles.addLineItemSuccess}
            role="alert"
            style={{ background: '#fef2f2', color: '#991b1b' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            {addError}
          </div>
        )}
      </div>

      {/* Add Special Pricing CTA */}
      <div className={styles.tooltipWrap}>
        <button className={styles.addPricingBtn} type="button" disabled>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add Special Pricing
        </button>
        <span className={styles.tooltipText}>Not yet implemented</span>
      </div>

      {/* Order-level instructions — each becomes a standalone /C comment
          row on the Sage order (the desk's own convention). Merlin extracts
          them from the source PO/email as SUGGESTIONS that a CSR must add
          before they ride any payload (/C text prints on the customer
          invoice and the carrier BOL); CSRs can also type one-offs. On a
          draft, accepted lines ride the submission payload; on a submitted
          order an add/accept triggers the narrow comment-only Sage write
          immediately. */}
      <div className={styles.section} data-testid="order-instructions-section">
        <div className={styles.sectionTitle}>
          Order Instructions{' '}
          <span style={{ fontWeight: 400, textTransform: 'none' }}>
            — Sage /C comment lines
          </span>
        </div>
        {orderInstructions.length === 0 && !instructionAddOpen && (
          <div style={{ fontSize: '12px', color: '#9ca3af', padding: '2px 0 6px' }}>
            No order-level instructions. Customer directives like “WILL CALL”
            or “call ahead before delivery” land in Sage from here.
          </div>
        )}
        {suggestedInstructionCount > 0 && (
          <div
            data-testid="order-instruction-suggested-hint"
            style={{ fontSize: '12px', color: '#6d28d9', padding: '2px 0 6px' }}
          >
            Merlin found {suggestedInstructionCount} suggested instruction
            {suggestedInstructionCount === 1 ? '' : 's'} on the customer&apos;s
            PO. Suggestions are NOT sent to Sage unless you add them — /C
            lines print on the customer invoice and the carrier BOL.
          </div>
        )}
        {orderInstructions.map((ins) => (
          <div
            key={ins.id}
            data-testid={`order-instruction-${ins.id}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '4px 0',
              fontSize: '13px',
              borderBottom: '1px solid #f3f4f6',
            }}
          >
            <span
              title={ins.source === 'merlin' ? 'Extracted by Merlin from the source PO/email' : 'Added by a CSR'}
              style={{
                fontSize: '10px',
                fontWeight: 600,
                padding: '1px 6px',
                borderRadius: '9999px',
                background: ins.source === 'merlin' ? '#ede9fe' : '#f3f4f6',
                color: ins.source === 'merlin' ? '#6d28d9' : '#4b5563',
                flexShrink: 0,
              }}
            >
              {ins.source === 'merlin' ? 'Merlin' : 'CSR'}
            </span>
            {instructionEditId === ins.id ? (
              <>
                <input
                  data-testid={`order-instruction-edit-${ins.id}`}
                  value={instructionEditDraft}
                  onChange={(e) => setInstructionEditDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void submitInstructionEdit(ins.id);
                    if (e.key === 'Escape') setInstructionEditId(null);
                  }}
                  maxLength={500}
                  autoFocus
                  data-inline-edit-cell="true"
                  style={{
                    flex: 1,
                    fontSize: '13px',
                    padding: '3px 6px',
                    border: '1px solid #93c5fd',
                    borderRadius: '4px',
                  }}
                />
                <button
                  type="button"
                  onClick={() => void submitInstructionEdit(ins.id)}
                  disabled={instructionBusy || !instructionEditDraft.trim()}
                  style={{ fontSize: '12px', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setInstructionEditId(null)}
                  style={{ fontSize: '12px', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <span
                  style={{
                    flex: 1,
                    color: ins.sage_state === 'desk_removed' ? '#9ca3af' : '#111827',
                    textDecoration:
                      ins.sage_state === 'desk_removed' ? 'line-through' : 'none',
                  }}
                >
                  {ins.text}
                </span>
                <span
                  data-testid={`order-instruction-state-${ins.id}`}
                  style={{
                    fontSize: '10px',
                    fontWeight: 600,
                    padding: '1px 6px',
                    borderRadius: '9999px',
                    flexShrink: 0,
                    background:
                      ins.sage_state === 'confirmed'
                        ? '#dcfce7'
                        : ins.sage_state === 'submitted'
                          ? '#dbeafe'
                          : ins.sage_state === 'failed'
                            ? '#fee2e2'
                            : ins.sage_state === 'suggested'
                              ? '#ede9fe'
                              : ins.sage_state === 'desk_removed'
                                ? '#e5e7eb'
                                : '#fef3c7',
                    color:
                      ins.sage_state === 'confirmed'
                        ? '#166534'
                        : ins.sage_state === 'submitted'
                          ? '#1d4ed8'
                          : ins.sage_state === 'failed'
                            ? '#991b1b'
                            : ins.sage_state === 'suggested'
                              ? '#6d28d9'
                              : ins.sage_state === 'desk_removed'
                                ? '#374151'
                                : '#92400e',
                  }}
                  title={
                    ins.sage_state === 'confirmed'
                      ? 'Verified on the Sage order'
                      : ins.sage_state === 'submitted'
                        ? 'Sent to Sage — lands on the next import drain'
                        : ins.sage_state === 'failed'
                          ? 'The Sage write failed — retry below'
                          : ins.sage_state === 'suggested'
                            ? 'Merlin suggestion — NOT sent to Sage unless you add it'
                            : ins.sage_state === 'desk_removed'
                              ? 'Verified on the Sage order earlier, now gone — the Sage desk deleted it. It will not be re-sent.'
                              : order.sage_order_no
                                ? 'Not in Sage yet — use “Send to Sage now”'
                                : 'Will ride the Sage submission as a /C line'
                  }
                >
                  {ins.sage_state === 'confirmed'
                    ? 'In Sage'
                    : ins.sage_state === 'submitted'
                      ? 'Sent to Sage'
                      : ins.sage_state === 'failed'
                        ? 'Failed'
                        : ins.sage_state === 'suggested'
                          ? 'Suggested'
                          : ins.sage_state === 'desk_removed'
                            ? 'Removed in Sage'
                            : 'Pending'}
                </span>
                {canEdit && ins.sage_state === 'suggested' && (
                  <button
                    type="button"
                    data-testid={`order-instruction-accept-${ins.id}`}
                    onClick={() => void acceptInstruction(ins.id)}
                    disabled={instructionBusy}
                    style={{ fontSize: '12px', color: '#059669', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                  >
                    Add
                  </button>
                )}
                {canEdit &&
                  (ins.sage_state === 'pending' || ins.sage_state === 'suggested') && (
                  <>
                    <button
                      type="button"
                      data-testid={`order-instruction-edit-btn-${ins.id}`}
                      onClick={() => {
                        setInstructionEditId(ins.id);
                        setInstructionEditDraft(ins.text);
                      }}
                      style={{ fontSize: '12px', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      edit
                    </button>
                    <button
                      type="button"
                      data-testid={`order-instruction-remove-${ins.id}`}
                      onClick={() => void removeInstruction(ins.id)}
                      disabled={instructionBusy}
                      style={{ fontSize: '12px', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      {ins.sage_state === 'suggested' ? 'dismiss' : 'remove'}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        ))}
        {canEdit && instructionAddOpen && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '6px 0' }}>
            <input
              data-testid="order-instruction-input"
              value={instructionDraft}
              onChange={(e) => setInstructionDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submitNewInstruction();
                if (e.key === 'Escape') {
                  setInstructionAddOpen(false);
                  setInstructionDraft('');
                }
              }}
              placeholder="e.g. WILL CALL — customer pickup"
              maxLength={500}
              autoFocus
              data-inline-edit-cell="true"
              style={{
                flex: 1,
                fontSize: '13px',
                padding: '4px 8px',
                border: '1px solid #93c5fd',
                borderRadius: '4px',
              }}
            />
            <button
              type="button"
              data-testid="order-instruction-save"
              data-action="order_instruction_save"
              data-target-order-id={order.id}
              data-outcome="request"
              onClick={() => void submitNewInstruction()}
              disabled={instructionBusy || !instructionDraft.trim()}
              style={{ fontSize: '12px', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
            >
              {instructionBusy ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => {
                setInstructionAddOpen(false);
                setInstructionDraft('');
              }}
              style={{ fontSize: '12px', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        )}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', paddingTop: '4px' }}>
          {canEdit && suggestedInstructionCount > 1 && (
            <button
              type="button"
              data-testid="order-instruction-accept-all"
              onClick={() => void acceptAllInstructions()}
              disabled={instructionBusy}
              style={{
                fontSize: '12px',
                color: '#059669',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                fontWeight: 600,
              }}
            >
              Add all {suggestedInstructionCount} suggested
            </button>
          )}
          {canEdit && !instructionAddOpen && (
            <button
              type="button"
              data-testid="order-instruction-add"
              onClick={() => setInstructionAddOpen(true)}
              style={{
                fontSize: '12px',
                color: '#2563eb',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              + Add instruction
            </button>
          )}
          {canEdit &&
            Boolean(order.sage_order_no) &&
            orderInstructions.some(
              (i) => i.sage_state === 'pending' || i.sage_state === 'failed',
            ) && (
              <button
                type="button"
                data-testid="order-instruction-push"
                onClick={() => void retryInstructionPush()}
                disabled={instructionBusy}
                style={{
                  fontSize: '12px',
                  color: '#059669',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  fontWeight: 600,
                }}
              >
                {instructionBusy ? 'Sending…' : 'Send to Sage now'}
              </button>
            )}
        </div>
        {instructionMessage && (
          <div
            data-testid="order-instruction-message"
            role="alert"
            style={{
              marginTop: '6px',
              fontSize: '12px',
              padding: '5px 8px',
              borderRadius: '4px',
              background: instructionMessage.kind === 'ok' ? '#ecfdf5' : '#fef2f2',
              color: instructionMessage.kind === 'ok' ? '#065f46' : '#991b1b',
            }}
          >
            {instructionMessage.text}
          </div>
        )}
      </div>

      {/* Sage order notes — production (read-only) + CS (editable).
          Collapsed by default (reviewer feedback, 2026-07: the panel is big
          and busy and rarely needed during draft review); one click opens
          the full existing panel unchanged.
          BUT: once the order has a Sage order number, CS notes are exactly
          what this panel exists for (there's nothing to sync before then —
          queue_order_note requires sage_order_no), so it must default open
          post-submission. Leaving it collapsed unconditionally hid the "Queue
          CS Note" composer behind an easy-to-miss disclosure on every order,
          including confirmed ones, which is very likely why CS notes were
          typed but never actually queued (kanban c05fd737). */}
      <details
        className={styles.section}
        data-testid="order-notes-collapse"
        open={Boolean(order.sage_order_no)}
      >
        <summary
          style={{
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 600,
            color: '#6b7280',
            padding: '4px 0',
          }}
        >
          Order Notes <span style={{ fontWeight: 400 }}>— Sage production &amp; CS notes</span>
        </summary>
        <OrderNotesPanel orderId={order.id} readOnly={!canEdit} />
      </details>
    </>
  );
};

// ─── Special Pricing Tab (sub-component) ─────────────────────────────────

/**
 * What an agreement line actually charges: the tier ladder for a tiered row,
 * else the flat special price.
 *
 * Tiered is checked FIRST to match the pipeline (price_resolution.py resolves
 * tiers before the flat column). A tiered row also carries a `special_price`
 * that is never billed — e.g. HATCH01 / F1435.05 stores 122.798 alongside real
 * rungs of 134.95 / 124.95 (36+) / 116.95 (72+) — so showing the flat value
 * here misstated the deal and disagreed with the sales site.
 */
function formatAgreementPrice(
  p: AgreementProduct,
  formatPrice: (val: string | number | null | undefined) => string,
): string {
  if ((p.pricing_type || '').toLowerCase() === 'tiered') {
    const ladder = formatTierLadder(p, (v) => formatPrice(v));
    if (ladder) return `Tiered: ${ladder}`;
  }
  return formatPrice(p.special_price);
}

interface PricingTabProps {
  pricingContext: OrderPricingContextResponse | null;
  filteredAgreements: CustomerPricingAgreement[];
  showActiveOnly: boolean;
  setShowActiveOnly: (v: boolean) => void;
  expandedAgreements: Set<string>;
  toggleAgreement: (id: string) => void;
  orderItemCodes: Set<string>;
  formatPrice: (val: string | number | null | undefined) => string;
  formatDate: (dateStr: string | null | undefined) => string;
}

const PricingTab: React.FC<PricingTabProps> = ({
  pricingContext,
  filteredAgreements,
  showActiveOnly,
  setShowActiveOnly,
  expandedAgreements,
  toggleAgreement,
  orderItemCodes,
  formatPrice,
  formatDate,
}) => {
  if (!pricingContext) {
    return <div className={styles.loadingState}>No pricing data available</div>;
  }

  return (
    <>
      {/* Per-ship-to pricing override banner (e.g. White Cap Lansing → ERSCO PL) */}
      {pricingContext.pricing_customer_no && (
        <div className={styles.pricingOverrideBanner} data-testid="pricing-override-banner">
          <span className={styles.pricingOverrideIcon}>&#8505;</span>
          <div>
            <div className={styles.pricingOverrideTitle}>
              Private-Label Pricing Override — {pricingContext.pricing_customer_no}
              {pricingContext.pricing_customer_name
                ? ` (${pricingContext.pricing_customer_name})`
                : ''}
            </div>
            <div className={styles.pricingOverrideText}>
              This order&apos;s ship-to location prices from{' '}
              {pricingContext.pricing_customer_no}&apos;s price list instead of{' '}
              {pricingContext.customer_no}&apos;s.
              {pricingContext.pricing_override_reason
                ? ` ${pricingContext.pricing_override_reason}`
                : ''}{' '}
              <a
                className={styles.pricingOverrideLink}
                href="/portal/pricing-exemptions"
                target="_blank"
                rel="noopener noreferrer"
              >
                View all standing exemptions
              </a>
            </div>
          </div>
        </div>
      )}

      {/* No special pricing alert */}
      {!pricingContext.has_any_special_pricing && (
        <div className={styles.noSpecialAlert}>
          <span className={styles.alertIcon}>&#9888;</span>
          <div>
            <div className={styles.alertTitle}>No Special Pricing on File</div>
            <div className={styles.alertText}>
              Customer {pricingContext.customer_no} ({pricingContext.customer_name}) has no active
              special pricing agreement.
            </div>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className={styles.filterBar}>
        <span className={styles.filterLabel}>Show:</span>
        <button
          className={`${styles.filterToggle} ${showActiveOnly ? styles.filterToggleActive : ''}`}
          onClick={() => setShowActiveOnly(true)}
          type="button"
        >
          Active Only
        </button>
        <button
          className={`${styles.filterToggle} ${!showActiveOnly ? styles.filterToggleActive : ''}`}
          onClick={() => setShowActiveOnly(false)}
          type="button"
        >
          All Agreements
        </button>
        <span style={{ fontSize: '12px', color: '#9ca3af', marginLeft: 'auto' }}>
          {filteredAgreements.length} agreement{filteredAgreements.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Agreement list */}
      {filteredAgreements.length === 0 ? (
        <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
          No {showActiveOnly ? 'active ' : ''}agreements found
        </div>
      ) : (
        filteredAgreements.map((a) => {
          const isExpanded = expandedAgreements.has(a.id);
          return (
            <div key={a.id} className={styles.agreementCard}>
              <div className={styles.agreementHeader} onClick={() => toggleAgreement(a.id)}>
                <div className={styles.agreementHeaderLeft}>
                  <span className={`${styles.agreementExpandIcon} ${isExpanded ? styles.agreementExpandIconOpen : ''}`}>
                    &#9654;
                  </span>
                  <span
                    className={`${styles.agreementStatus} ${
                      !a.is_active
                        ? styles.agreementInactive
                        : a.in_force === false
                          ? styles.agreementExpired
                          : styles.agreementActive
                    }`}
                    data-testid="agreement-status"
                    title={
                      a.is_active && a.in_force === false
                        ? 'Still flagged active in the Sales App, but the agreement end date has passed. Its prices no longer apply to orders.'
                        : undefined
                    }
                  >
                    {!a.is_active ? 'Inactive' : a.in_force === false ? 'Expired' : 'Active'}
                  </span>
                  <span className={styles.agreementMeta}>
                    {a.product_count} product{a.product_count !== 1 ? 's' : ''}
                    {a.effective_date && ` | From ${formatDate(a.effective_date)}`}
                    {a.expiration_date && ` | Expires ${formatDate(a.expiration_date)}`}
                  </span>
                  {a.via_linked_customer && a.customer_no && (
                    <span
                      className={styles.dealBadge}
                      data-testid="agreement-via-linked"
                      title={`Filed in the Sales App under linked account ${a.customer_no}; shared with ${pricingContext.customer_no} through its linked-customer label.`}
                    >
                      via {a.customer_no}
                    </span>
                  )}
                </div>
                {(a.has_freight_deal || a.has_grout_deal) && (
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {a.has_freight_deal && <span className={styles.dealBadge}>Freight Deal</span>}
                    {a.has_grout_deal && <span className={styles.dealBadge}>Grout Deal</span>}
                  </div>
                )}
              </div>

              {isExpanded && (
                <div className={styles.agreementBody}>
                  {/* Agreement details */}
                  <div className={styles.agreementDetail}>
                    {a.base_discount !== null && a.base_discount !== undefined && (
                      <span>Base Discount: {a.base_discount}%</span>
                    )}
                    {a.payment_terms && <span>Terms: {a.payment_terms}</span>}
                  </div>
                  {a.note && <div className={styles.agreementNote}>{a.note}</div>}

                  {/* Products within agreement */}
                  <div className={styles.agreementProducts}>
                    <div className={styles.agreementProductsTitle}>
                      Products ({a.products.length})
                    </div>
                    {a.products.length > 0 ? (
                      <div className={styles.productList}>
                        {a.products.map((p: AgreementProduct) => (
                          <div
                            key={p.item_code}
                            className={`${styles.productItem} ${orderItemCodes.has(p.item_code) ? styles.productItemInOrder : ''}`}
                          >
                            <span className={styles.productItemCode}>{p.item_code}</span>
                            {p.item_description && (
                              <span className={styles.productItemName}>
                                {p.item_description}
                              </span>
                            )}
                            {orderItemCodes.has(p.item_code) && (
                              <span className={styles.inOrderBadge}>In Order</span>
                            )}
                            <span className={styles.productItemPrice}>
                              {formatAgreementPrice(p, formatPrice)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: '12px', color: '#9ca3af', padding: '4px 0' }}>
                        No products in this agreement
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Add Special Pricing CTA */}
      <div style={{ marginTop: '16px' }}>
        <div className={styles.tooltipWrap}>
          <button className={styles.addPricingBtn} type="button" disabled>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Special Pricing
          </button>
          <span className={styles.tooltipText}>Not yet implemented</span>
        </div>
      </div>
    </>
  );
};

// ─── Open Quotes Tab (sub-component) ──────────────────────────────────────

interface QuotesTabProps {
  pricingContext: OrderPricingContextResponse | null;
  formatPrice: (val: string | number | null | undefined) => string;
  formatDate: (dateStr: string | null | undefined) => string;
  isDraft: boolean;
  onAttachQuote: (quoteSourceId: string) => void;
  /** Normalized refs of quotes already on the order's UDF_QUOTE slots. */
  attachedQuoteRefs: Set<string>;
  /** All five UDF_QUOTE slots are used — nothing more can be attached. */
  quoteSlotsFull: boolean;
  /** Padded source id of the quote whose attach is in flight, if any. */
  attachingQuoteId: string | null;
  /** Clear the slot holding `quoteRef` (or the given slot). */
  onDetachQuote: (quoteRef: string, slot?: QuoteSlot) => void;
  /** Ref of the quote whose removal is in flight, if any. */
  detachingQuoteRef: string | null;
  attachNotice: QuoteAttachNotice | null;
  onDismissAttachNotice: () => void;
  orderLineItems: OrderLineItem[];
  onApplyQuotePrice: (itemCode: string, price: number) => void;
}

const ATTACH_NOTICE_PALETTE: Record<QuoteAttachNotice['kind'], { border: string; bg: string; color: string }> = {
  success: { border: '#a7f3d0', bg: '#ecfdf5', color: '#065f46' },
  info: { border: '#bfdbfe', bg: '#eff6ff', color: '#1e40af' },
  error: { border: '#fecaca', bg: '#fef2f2', color: '#991b1b' },
};

/** Single expandable quote card with drill-down product list. */
const QuoteCard: React.FC<{
  quote: CustomerOpenQuote;
  formatPrice: (val: string | number | null | undefined) => string;
  formatDate: (dateStr: string | null | undefined) => string;
  isDraft: boolean;
  onAttachQuote: (quoteSourceId: string) => void;
  isAttached: boolean;
  isAttaching: boolean;
  slotsFull: boolean;
  onDetachQuote: (quoteRef: string) => void;
  isDetaching: boolean;
  orderItemCodes: Set<string>;
  onApplyQuotePrice: (itemCode: string, price: number) => void;
}> = ({
  quote,
  formatPrice,
  formatDate,
  isDraft,
  onAttachQuote,
  isAttached,
  isAttaching,
  slotsFull,
  onDetachQuote,
  isDetaching,
  orderItemCodes,
  onApplyQuotePrice,
}) => {
  const paddedId = String(quote.source_id).padStart(5, '0');
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState<QuoteDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const handleToggle = async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    if (!detail) {
      setLoading(true);
      try {
        const data = await portalOrderService.getQuoteDetail(quote.source_id);
        setDetail(data);
      } catch {
        // Keep expanded but show error state
      } finally {
        setLoading(false);
      }
    }
  };

  const products = detail?.products || [];
  const overlappingCodes = new Set(quote.overlapping_items.map((i) => i.item_code));

  return (
    <div key={quote.id} className={styles.agreementCard}>
      <div
        className={styles.agreementHeader}
        style={{ cursor: 'pointer' }}
        onClick={handleToggle}
      >
        <div className={styles.agreementHeaderLeft}>
          <span style={{ fontSize: '13px', color: '#6b7280', marginRight: '4px' }}>
            {expanded ? '\u25BC' : '\u25B6'}
          </span>
          <span style={{ fontWeight: 600, fontSize: '13px', color: '#1e40af' }}>
            #{String(quote.source_id).padStart(5, '0')}
          </span>
          <span className={`${styles.agreementStatus} ${styles.agreementActive}`}>
            {quote.status || 'Open'}
          </span>
          <span className={styles.agreementMeta}>
            {quote.project_name && `${quote.project_name} | `}
            {quote.product_count} product{quote.product_count !== 1 ? 's' : ''}
            {quote.estimated_total != null && ` | ~${formatPrice(quote.estimated_total)}`}
            {quote.expiration_date && ` | Expires ${formatDate(quote.expiration_date)}`}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {quote.overlapping_items.length > 0 && (
            <span
              className={styles.dealBadge}
              /* Name the overlapping products on hover — the count alone
                 doesn't tell the CSR whether attaching this quote is right. */
              title={quote.overlapping_items
                .map((i) =>
                  i.item_description
                    ? `${i.item_code} — ${i.item_description}`
                    : i.item_code,
                )
                .join('\n')}
            >
              {quote.overlapping_items.length} item{quote.overlapping_items.length !== 1 ? 's' : ''} overlap
            </span>
          )}
          {/* Attached state is derived from the order's UDF_QUOTE slots, so
              it survives re-renders and shows on non-draft orders too — a
              second press used to be a silent no-op (kanban 43b3f27a). */}
          {isAttached ? (
            <>
            <span
              data-testid={`quote-attached-badge-${paddedId}`}
              style={{
                padding: '3px 8px',
                fontSize: '11px',
                fontWeight: 600,
                border: '1px solid #6ee7b7',
                borderRadius: '4px',
                background: '#ecfdf5',
                color: '#047857',
              }}
              title="This quote ID is on the order's UDF_QUOTE slots. Overlapping prices were applied when it was attached."
            >
              {'\u2713'} Attached
            </span>
            {isDraft && (
              <DetachQuoteButton
                testId={`detach-quote-btn-${paddedId}`}
                busy={isDetaching}
                onClick={() => onDetachQuote(paddedId)}
              />
            )}
            </>
          ) : isDraft ? (
            <button
              type="button"
              data-testid={`attach-quote-btn-${paddedId}`}
              disabled={isAttaching || slotsFull}
              onClick={(e) => {
                e.stopPropagation();
                if (isAttaching || slotsFull) return;
                onAttachQuote(paddedId);
              }}
              style={{
                padding: '3px 8px',
                fontSize: '11px',
                fontWeight: 600,
                border: '1px solid #2563eb',
                borderRadius: '4px',
                background: '#eff6ff',
                color: '#2563eb',
                cursor: isAttaching || slotsFull ? 'not-allowed' : 'pointer',
                opacity: isAttaching || slotsFull ? 0.6 : 1,
              }}
              title={
                slotsFull
                  ? 'All 5 quote slots on this order are already used — nothing more can be attached.'
                  : isAttaching
                    ? 'Attaching this quote…'
                    : 'Attach this quote ID and apply all overlapping prices'
              }
            >
              {isAttaching ? 'Attaching…' : 'Attach'}
            </button>
          ) : null}
        </div>
      </div>

      {/* Expanded detail view */}
      {expanded && (
        <div className={styles.agreementBody}>
          {loading && (
            <div style={{ padding: '12px', color: '#9ca3af', fontSize: '12px' }}>
              Loading quote products...
            </div>
          )}

          {!loading && detail && (
            <>
              {/* Quote metadata */}
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '12px', color: '#6b7280', marginBottom: '10px', padding: '0 4px' }}>
                {detail.payment_terms && <span>Terms: {detail.payment_terms}</span>}
                {detail.freight_type && <span>Freight: {detail.freight_type}</span>}
                {detail.freight_estimate != null && <span>Freight Est: {formatPrice(detail.freight_estimate)}</span>}
                {detail.has_freight_deal && <span className={styles.dealBadge}>Freight Deal</span>}
                {detail.has_grout_deal && <span className={styles.dealBadge}>Grout Deal</span>}
              </div>

              {/* All products table */}
              <div className={styles.agreementProductsTitle}>
                All Products ({products.length})
                {isDraft && quote.overlapping_items.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      for (const item of quote.overlapping_items) {
                        onApplyQuotePrice(item.item_code, item.price);
                      }
                    }}
                    style={{
                      marginLeft: '8px',
                      padding: '2px 6px',
                      fontSize: '10px',
                      fontWeight: 600,
                      border: '1px solid #059669',
                      borderRadius: '3px',
                      background: '#ecfdf5',
                      color: '#059669',
                      cursor: 'pointer',
                    }}
                    title="Apply quote prices to all overlapping line items"
                  >
                    Apply All Overlapping Prices
                  </button>
                )}
              </div>

              <div className={styles.productList}>
                {/* Header row */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto auto auto auto',
                    gap: '8px',
                    padding: '4px 8px',
                    fontSize: '10px',
                    fontWeight: 600,
                    color: '#9ca3af',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb',
                  }}
                >
                  <span>Item</span>
                  <span>Packaging</span>
                  <span style={{ textAlign: 'right' }}>Qty</span>
                  <span style={{ textAlign: 'right' }}>Price</span>
                  <span></span>
                </div>

                {products.map((p: QuoteProductDetail, idx: number) => {
                  const isOverlapping = p.item_code ? overlappingCodes.has(p.item_code) : false;
                  const isInOrder = p.item_code ? orderItemCodes.has(p.item_code) : false;
                  return (
                    <div
                      key={`${p.item_code}-${idx}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto auto auto auto',
                        gap: '8px',
                        padding: '5px 8px',
                        fontSize: '12px',
                        alignItems: 'center',
                        background: isOverlapping ? '#eff6ff' : 'transparent',
                        borderBottom: '1px solid #f3f4f6',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                        <span className={styles.productItemCode}>{p.item_code || '—'}</span>
                        {p.item_description && (
                          <span className={styles.productItemName}>{p.item_description}</span>
                        )}
                        {isInOrder && <span className={styles.inOrderBadge}>In Order</span>}
                      </span>
                      <span style={{ color: '#6b7280', fontSize: '11px' }}>{p.packaging || '—'}</span>
                      <span style={{ textAlign: 'right', color: '#374151' }}>{p.quantity}</span>
                      <span style={{ textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>
                        {formatPrice(p.price)}
                      </span>
                      <span>
                        {isDraft && isInOrder && (
                          <button
                            type="button"
                            onClick={() => {
                              if (p.item_code) onApplyQuotePrice(p.item_code, p.price);
                            }}
                            style={{
                              padding: '1px 5px',
                              fontSize: '10px',
                              fontWeight: 600,
                              border: '1px solid #059669',
                              borderRadius: '3px',
                              background: '#ecfdf5',
                              color: '#059669',
                              cursor: 'pointer',
                            }}
                            title={`Apply ${formatPrice(p.price)} to this line item`}
                          >
                            Apply
                          </button>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Notes for products that have them */}
              {products.some((p) => p.note) && (
                <div style={{ marginTop: '8px', fontSize: '11px', color: '#6b7280' }}>
                  <div style={{ fontWeight: 600, marginBottom: '4px' }}>Notes:</div>
                  {products
                    .filter((p) => p.note)
                    .map((p, idx) => (
                      <div key={idx} style={{ marginBottom: '2px' }}>
                        <span style={{ fontWeight: 500 }}>{p.item_code}:</span> {p.note}
                      </div>
                    ))}
                </div>
              )}
            </>
          )}

          {!loading && !detail && (
            <div style={{ padding: '12px', color: '#ef4444', fontSize: '12px' }}>
              Failed to load quote details.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** "Unattach" affordance next to an attached / referenced quote. Clears the
 *  UDF_QUOTE slot only; prices stay (see handleDetachQuote). */
const DetachQuoteButton: React.FC<{ testId: string; busy: boolean; onClick: () => void }> = ({
  testId,
  busy,
  onClick,
}) => (
  <button
    type="button"
    data-testid={testId}
    disabled={busy}
    onClick={(e) => {
      e.stopPropagation();
      if (!busy) onClick();
    }}
    style={{
      padding: '3px 8px',
      fontSize: '11px',
      fontWeight: 600,
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      background: '#fff',
      color: '#4b5563',
      cursor: busy ? 'not-allowed' : 'pointer',
      opacity: busy ? 0.6 : 1,
    }}
    title="Remove this quote reference from the order. Line prices are left as they are."
  >
    {busy ? 'Removing…' : 'Unattach'}
  </button>
);

/** Card for a quote referenced on the order (udf_quote slot) — rendered
 *  regardless of quote ownership, so orphan quotes for new customers
 *  (AU01339 / quote #10972) are finally visible to the CSR. */
const ReferencedQuoteCard: React.FC<{
  quote: ReferencedOrderQuote;
  formatPrice: (val: string | number | null | undefined) => string;
  formatDate: (dateStr: string | null | undefined) => string;
  isDraft: boolean;
  onDetach: (rawReference: string, slot: QuoteSlot) => void;
  isDetaching: boolean;
}> = ({ quote, formatPrice, formatDate, isDraft, onDetach, isDetaching }) => {
  const detach = isDraft ? (
    <DetachQuoteButton
      testId={`detach-referenced-btn-${quote.slot}`}
      busy={isDetaching}
      onClick={() => onDetach(quote.raw_reference, quote.slot as QuoteSlot)}
    />
  ) : null;
  if (!quote.found) {
    return (
      <div className={styles.agreementCard} data-testid="referenced-quote-card-notfound">
        <div className={styles.agreementHeader}>
          <span style={{ fontWeight: 600, fontSize: '13px', color: '#b45309' }}>
            Quote reference &ldquo;{quote.raw_reference}&rdquo; not found
          </span>
          <span style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '8px', flex: 1 }}>
            No matching quote in the quote system — this reference will be dropped at Sage submission.
          </span>
          {detach}
        </div>
      </div>
    );
  }
  const orderedLines = quote.lines.filter((ln) => ln.on_order);
  return (
    <div
      className={styles.agreementCard}
      data-testid="referenced-quote-card"
      style={{ borderColor: '#1d4ed8', background: '#f5f8ff' }}
    >
      <div className={styles.agreementHeader}>
        <div className={styles.agreementHeaderLeft} style={{ flexWrap: 'wrap', gap: '6px' }}>
          <span style={{ fontWeight: 700, fontSize: '13px', color: '#1e40af' }}>
            Quote #{String(quote.source_id).padStart(5, '0')}
          </span>
          <span className={styles.agreementBadge}>
            {quote.status || 'Unknown'}
            {quote.expiration_date ? ` · expires ${formatDate(quote.expiration_date)}` : ''}
          </span>
          {quote.merlin_verified != null && (
            <span
              style={{ fontSize: '11px', borderRadius: '9px', padding: '2px 8px', fontWeight: 600, background: '#dbeafe', color: '#1d4ed8' }}
              title={quote.merlin_note || undefined}
            >
              {'✨'} {quote.merlin_verified ? 'Verified by Merlin' : 'Referenced by Merlin'}
            </span>
          )}
          {quote.is_expired && (
            <span style={{ fontSize: '11px', borderRadius: '9px', padding: '2px 8px', fontWeight: 600, background: '#fdedec', color: '#c0392b' }}>
              Expired
            </span>
          )}
          {!quote.customer_linked && (
            <span
              style={{ fontSize: '11px', borderRadius: '9px', padding: '2px 8px', fontWeight: 600, background: '#fef9e7', color: '#7d6608' }}
              title="The quote system has no customer selected on this quote (typical for new/prospect customers) — confirm it belongs to this buyer."
            >
              Not linked to a Sage customer — verify
            </span>
          )}
          {quote.customer_linked && !quote.belongs_to_order_customer && (
            <span
              style={{ fontSize: '11px', borderRadius: '9px', padding: '2px 8px', fontWeight: 600, background: '#fdedec', color: '#c0392b' }}
              title="This quote is linked to a different customer in the quote system."
            >
              Linked to a different customer — verify
            </span>
          )}
        </div>
        {/* Header is a flex row (space-between): the affordance sits at the right. */}
        {detach && <div style={{ marginLeft: '8px', flexShrink: 0 }}>{detach}</div>}
      </div>
      <div style={{ fontSize: '12.5px', color: '#6b7280', margin: '4px 0 6px' }}>
        Quoted to: <strong style={{ color: '#374151' }}>{quote.pc_name || '(no name on quote)'}</strong>
        {quote.pc_contact_name ? ` (${quote.pc_contact_name})` : ''}
        {' · '}{quote.lines.length} product{quote.lines.length !== 1 ? 's' : ''}
      </div>
      {orderedLines.length > 0 && (
        <table style={{ borderCollapse: 'collapse', fontSize: '12.5px', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', color: '#6b7280', padding: '3px 10px 3px 0', borderBottom: '1px solid #e5e7eb' }}>Item</th>
              <th style={{ textAlign: 'left', color: '#6b7280', padding: '3px 10px 3px 0', borderBottom: '1px solid #e5e7eb' }}>Quote price</th>
              <th style={{ textAlign: 'left', color: '#6b7280', padding: '3px 10px 3px 0', borderBottom: '1px solid #e5e7eb' }}>Order price</th>
              <th style={{ borderBottom: '1px solid #e5e7eb' }} />
            </tr>
          </thead>
          <tbody>
            {orderedLines.map((ln) => (
              <tr key={ln.item_code}>
                <td style={{ padding: '4px 10px 4px 0', borderBottom: '1px solid #f3f4f6' }}>
                  {ln.item_code}
                  {ln.item_description && (
                    <div style={{ color: '#6b7280', fontSize: '11px' }}>{ln.item_description}</div>
                  )}
                </td>
                <td style={{ padding: '4px 10px 4px 0', borderBottom: '1px solid #f3f4f6' }}>{formatPrice(ln.quote_price)}</td>
                <td style={{ padding: '4px 10px 4px 0', borderBottom: '1px solid #f3f4f6' }}>{formatPrice(ln.order_price)}</td>
                <td style={{ padding: '4px 0', borderBottom: '1px solid #f3f4f6', fontWeight: 600, color: ln.price_matches ? '#186a3b' : '#b45309' }}>
                  {ln.price_matches ? '✓ matches' : 'differs'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {quote.merlin_note && (
        <div style={{ fontSize: '12px', color: '#374151', background: '#fffbe6', border: '1px solid #f3e8b5', borderRadius: '8px', padding: '6px 10px', marginTop: '8px' }}>
          {'✨'} Merlin: {quote.merlin_note}
        </div>
      )}
    </div>
  );
};

const QuotesTab: React.FC<QuotesTabProps> = ({
  pricingContext,
  formatPrice,
  formatDate,
  isDraft,
  onAttachQuote,
  attachedQuoteRefs,
  quoteSlotsFull,
  attachingQuoteId,
  onDetachQuote,
  detachingQuoteRef,
  attachNotice,
  onDismissAttachNotice,
  orderLineItems,
  onApplyQuotePrice,
}) => {
  if (!pricingContext) {
    return <div className={styles.loadingState}>No pricing data available</div>;
  }

  const notice = attachNotice && (
    <div
      data-testid="quote-attach-notice"
      role={attachNotice.kind === 'error' ? 'alert' : 'status'}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        marginBottom: '12px',
        borderRadius: '6px',
        fontSize: '12.5px',
        fontWeight: 500,
        border: `1px solid ${ATTACH_NOTICE_PALETTE[attachNotice.kind].border}`,
        background: ATTACH_NOTICE_PALETTE[attachNotice.kind].bg,
        color: ATTACH_NOTICE_PALETTE[attachNotice.kind].color,
      }}
    >
      <span style={{ flex: 1 }}>{attachNotice.text}</span>
      <button
        type="button"
        onClick={onDismissAttachNotice}
        aria-label="Dismiss"
        style={{
          border: 'none',
          background: 'transparent',
          color: 'inherit',
          fontSize: '16px',
          lineHeight: 1,
          cursor: 'pointer',
          padding: '0 2px',
        }}
      >
        {'\u00d7'}
      </button>
    </div>
  );

  const quotes = pricingContext.open_quotes || [];
  const referenced = pricingContext.referenced_quotes || [];
  const orderItemCodes = new Set(orderLineItems.map((li) => li.item_code).filter(Boolean) as string[]);

  if (quotes.length === 0 && referenced.length === 0) {
    return (
      <div className={styles.loadingState} style={{ color: '#9ca3af' }}>
        No open quotes found for this customer.
      </div>
    );
  }

  return (
    <>
      {notice}
      {referenced.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '0 4px' }}>
            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>Referenced on this order</h4>
          </div>
          {referenced.map((q) => (
            <ReferencedQuoteCard
              key={`${q.slot}-${q.raw_reference}`}
              quote={q}
              formatPrice={formatPrice}
              formatDate={formatDate}
              isDraft={isDraft}
              onDetach={onDetachQuote}
              isDetaching={detachingQuoteRef === q.raw_reference}
            />
          ))}
        </>
      )}

      {quotes.length > 0 ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: `${referenced.length > 0 ? '16px' : '0'} 0 12px`, padding: '0 4px' }}>
            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>Open Quotes</h4>
            <span title="Quote IDs are not yet synced to Sage — must be set manually" style={{ color: '#d97706', cursor: 'help', fontSize: '14px' }}>&#9888;</span>
            <span style={{ fontSize: '12px', color: '#9ca3af', marginLeft: 'auto' }}>
              {quotes.length} quote{quotes.length !== 1 ? 's' : ''} — click to expand
            </span>
          </div>

          {quotes.map((q: CustomerOpenQuote) => (
            <QuoteCard
              key={q.id}
              quote={q}
              formatPrice={formatPrice}
              formatDate={formatDate}
              isDraft={isDraft}
              onAttachQuote={onAttachQuote}
              isAttached={attachedQuoteRefs.has(normalizeQuoteRef(q.source_id) ?? '')}
              isAttaching={attachingQuoteId === String(q.source_id).padStart(5, '0')}
              slotsFull={quoteSlotsFull}
              onDetachQuote={onDetachQuote}
              isDetaching={detachingQuoteRef === String(q.source_id).padStart(5, '0')}
              orderItemCodes={orderItemCodes}
              onApplyQuotePrice={onApplyQuotePrice}
            />
          ))}
        </>
      ) : (
        <div className={styles.loadingState} style={{ color: '#9ca3af', marginTop: '8px' }}>
          No customer-owned open quotes on file.
        </div>
      )}
    </>
  );
};

export default OrderDetailModal;
